import type { NoticeParams } from "../pages/engines/lockCalculator/model/noticeParams";
import { type UILanguageCode } from "../types";

const en = {
  languageCode: "en" as UILanguageCode,
  projects: {
    all: "All projects",
    new: "New project",
    singular: "Project",
    plural: "Projects",
    my: "My Projects",
    createNew: "Create new project",
    delete: "Delete project",
    search: "Search projects",
    shareWithUser: "Share project with user",
    editPermissions: "Edit permissions",
    editSharings: "Edit sharings",
    invitations: "Project invitations",

    invitationStatus: {
      pending: "Pending",
      accepted: "Accepted",
      rejected: "Rejected",
      cancelled: "Cancelled",
      statusChanges: {
        accept: "Invitation accepted successfully.",
        rejected: "Invitation rejected.",
      },
    },

    invitePage: {
      badge: "Project access invitation",
      fallbackAuthorName: "a team member",
      collaborationLead: "invited you to collaborate on this project and review its shared content.",
      invitedBy: "Invited by",
      invitationSentTo: "Invitation sent to",
      projectName: "Project name",
      description: "Description",
      inviteNoLongerAvailable:
        "This invitation is no longer available because it has already been accepted, rejected, or cancelled.",
      acceptSignedIn: "Accepting this invitation will open the project immediately.",
      acceptSignedOut: "Accepting this invitation will ask you to sign in before granting access to the project.",
      acceptAction: "Accept invitation",
      rejectAction: "Reject invitation",
    },

    questions: {
      delete: "Do you really want to delete this project?",
      unshare: "Do you really want to unshare the project for this user?",
    },

    errors: {
      deleteProjectWithConfigurationsNotPossible:
        "It is not possible to delete a project if configurations are present.",
      deleteProjectWithDocumentsNotPossible: "It is not possible to delete a project if documents are present.",
      notFound: "Project not found.",
      sharingNotFound: "Sharing not found.",
      sharingAlreadyExists: "The project is already shared with this user.",
      documentNotFound: "Document not found.",
      missingDocumentName: "Document name is missing.",
      documentAlreadyPresent: "Document already present.",
      missingProjectStatus: "Project status is missing.",
      inviteReadNotAllowed: "You are not allowed to view this invite.",
      inviteInsertNotAllowed: "You are not allowed to send an invite for this project.",
      inviteUpdateNotAllowed: "You are not allowed to accept or refuse this invite.",
      inviteDeleteNotAllowed: "You are not allowed to delete this invite.",
      inviteAlreadyExists: "An invite for this user already exists.",
      inviteNotFound: "Invite not found.",
      inviteAlreadyCancelled: "This invite has already been cancelled.",
      inviteAlreadyAccepted: "This invite has already been accepted.",
      inviteAlreadyRejected: "This invite has already been rejected.",
    },
  },

  calculations: {
    all: "All calculations",
    new: "New calculation",
    plural: "Calculations",
    create: "Create calculation",
    createNew: "Create new calculation",
    delete: "Delete calculation",
    duplicate: "Duplicate calculation",
    my: "My Calculations",
    search: "Search calculations",
    reset: "Reset calculation",
    newCalculationName: "New calculation name",
    shareWithUser: "Share calculations with user",
    editSharings: "Edit sharings",
    invitations: "Calculation invitations",
    invitePage: {
      badge: "Calculation access invitation",
      collaborationLead: "invited you to collaborate on this calculation and review its shared content.",
      calculationName: "Calculation name",
      inviteNoLongerAvailable:
        "This invitation is no longer available because it has already been accepted, rejected, or cancelled.",
      acceptSignedIn: "Accepting this invitation will open the calculation immediately.",
      acceptSignedOut: "Accepting this invitation will ask you to sign in before granting access to the calculation.",
    },
    questions: {
      delete: "Do you really want to delete this calculation?",
      unshare: "Do you really want to unshare the calculation for this user?",
      reset: "Do you really want to reset all the data of the current calculation?",
    },

    errors: {
      notFound: "Calculation not found.",
      sharingNotFound: "Sharing not found.",
      sharingAlreadyExists: "The calculation is already shared with this user.",
      moduleNotSupported: "Calculation module not supported.",
      moduleRestricted: "This module is not open for creating, editing or duplicating calculations.",
      inviteInsertNotAllowed: "You are not allowed to send an invite for this calculation.",
      inviteUpdateNotAllowed: "You are not allowed to accept or refuse this invite.",
      inviteDeleteNotAllowed: "You are not allowed to delete this invite.",
      inviteAlreadyExists: "An invite for this user already exists.",
      inviteNotFound: "Invite not found.",
      inviteAlreadyCancelled: "This invite has already been cancelled.",
      inviteAlreadyAccepted: "This invite has already been accepted.",
      inviteAlreadyRejected: "This invite has already been rejected.",
    },
  },

  sharingRequests: {
    title: "Access requests",
    requestedAt: "Requested at",
    message: "Message",
    accept: "Accept",
    reject: "Reject",
    acceptTitle: "Accept access request",
    rejectQuestion: "Do you really want to reject this access request?",
    accepted: "Access request accepted.",
    rejected: "Access request rejected.",
    notShared: {
      projectTitle: "Project not shared",
      calculationTitle: "Calculation not shared",
      projectText: "This project has not been shared with you, so you cannot access it.",
      calculationText: "This calculation has not been shared with you, so you cannot access it.",
      requestText: "You can ask its owner to share it with you: they will receive your request by email.",
      messageLabel: "Message to the owner",
      request: "Request access",
      sent: "Your access request has been sent.",
      pending: (date: string) =>
        `You requested access on ${date}: you will receive an email as soon as the owner answers.`,
    },
    managePage: {
      projectTitle: "Project access request",
      calculationTitle: "Calculation access request",
      requestedBy: "Requested by",
      project: "Project",
      calculation: "Calculation",
      projectLead: "asks you for access to this project.",
      calculationLead: "asks you for access to this calculation.",
      alreadyAccepted: "This request has already been accepted.",
      alreadyRejected: "This request has already been rejected.",
    },
    errors: {
      notFound: "Access request not found.",
      readNotAllowed: "You are not allowed to view this access request.",
      alreadyExists: "You have already requested access: the owner has not answered yet.",
      alreadyShared: "It has already been shared with you: reload the page.",
      alreadyAccepted: "This access request has already been accepted.",
      alreadyRejected: "This access request has already been rejected.",
    },
  },

  notifications: {
    title: "Notifications",
    projectSharingRequest: (user: string, project: string) =>
      `${user} asks for access to the project ${project}`,
    calculationSharingRequest: (user: string, calculation: string) =>
      `${user} asks for access to the calculation ${calculation}`,
    projectSharingInvite: (user: string, project: string) =>
      `${user} invited you to the project ${project}`,
    calculationSharingInvite: (user: string, calculation: string) =>
      `${user} invited you to the calculation ${calculation}`,
  },

  users: {
    add: "Add user",
    all: "All users",
    plural: "Users",
    remove: "Remove user",
    invite: "Invite user",
    search: "Search users",
    usersEmail: "Users email",
    usersName: "Users name",

    questions: {},

    pending: {
      plural: "Pending users",
      edit: "Edit pending user",
      delete: "Remove pending user",
      createdAt: "Added",
      info: "If this email has no account yet, the user stays pending until their first login, when they get these roles and permissions.",
    },

    errors: {
      notFound: "User not found.",
      alreadyExists: "A user with this email already exists.",
      pendingAlreadyExists: "A pending user with this email already exists.",
    },
  },

  items: {
    all: "All items",
    singular: "Item",
    plural: "Items",
    createNew: "Create new item",
    delete: "Delete item",
    search: "Search items",
    list: "Items list",

    questions: {
      delete: "Do you really want to delete this item?",
    },

    errors: {
      notFound: "Item not found.",
      doesNotExist: "Item with this code does not exits.",
      missingCode: "Item code is missing.",
    },
  },

  // patrolIntermediates: {
  //   singular: "Intermediate",
  //   plural: "Intermediates",
  //   createNew: "Create new intermediate",
  //   delete: "Delete intermediate",
  //   edit: "Edit intermediate",
  //   search: "Search intermediates",

  //   questions: {
  //     delete: "Do you really want to delete this intermediate?",
  //   },
  // },

  // patrolCurves: {
  //   singular: "Curve",
  //   plural: "Curves",
  //   createNew: "Create new curve",
  //   delete: "Delete curve",
  //   edit: "Edit curve",
  //   search: "Search curves",

  //   questions: {
  //     delete: "Do you really want to delete this curve?",
  //   },
  // },

  clients: {
    all: "All clients",
    plural: "Clients",
    createNew: "Create new client",
    delete: "Delete client",
    search: "Search clients",

    questions: {
      delete: "Do you really want to delete this client?",
    },

    errors: {
      notFound: "Client not found.",
      insertNotAllowed: "You are not allowed to insert a new client.",
      readNotAllowed: "You are not allowed to read this client.",
      updateNotAllowed: "You are not allowed to update this client.",
      deleteNotAllowed: "You are not allowed to delete this client.",
    },
  },

  accessControl: {
    title: "Access control",
    plural: "Access control",
    disclaimer:
      "Access control decides which users can use each application. It doesn't override geo-blocking: an allowed user is still refused in the countries where the application, or one of its modules, is geo-blocked. To let a user in from anywhere, give them the geo_blocking:bypass permission (e.g. with the geo-blocking-bypass role): it lifts every geo-blocking, on every application and module.",
    applications: "Applications",
    modules: "Modules",
    modulesDisclaimer:
      "A module's access control applies in the main application only: the module's own application, if any, follows its own tab. A user kept out of a module doesn't see it, nor its calculations, and can't open them.",

    settings: {
      lockSwitchLabel: "Restrict application to approved users",
      lockSwitchDescription:
        "When enabled, only users with an 'allowed' rule or administrator access can use the application.",
      confirmEnable: "Do you really want to restrict the application to approved users only?",
      confirmDisable: "Do you really want to allow all authenticated users back in?",
    },

    // the modules' tab: a module's lock switch, in the main application only
    moduleSettings: {
      lockSwitchLabel: "Restrict module to approved users",
      lockSwitchDescription:
        "When enabled, only users with an 'allowed' rule or administrator access can use the module in the main application.",
      confirmEnable: "Do you really want to restrict the module to approved users only?",
      confirmDisable: "Do you really want to allow all authenticated users back in?",
    },

    userAccess: {
      notAllowed: "Not allowed",
      causes: {
        rule: "Rule",
        default: "Default",
        admin: "Administrator",
        locked: "App restricted",
      },
    },

    rules: {
      plural: "Access rules",
      app: "Application",
      createNew: "Add rule",
      search: "Search by email or reason",
      delete: "Delete rule",
      email: "Email",
      user: "User",
      status: "Status",
      statusAllowed: "Allowed",
      statusBlocked: "Blocked",
      reason: "Reason",
      createdAt: "Created",
      import: "Import",
      importTitle: "Import rules",
      importFrom: "Import from",
      currentStatus: "Current status",
      selectAll: "Select all",
      importHint:
        "Rules already present with a different status are not selected: select them to overwrite the current status and reason.",

      questions: {
        delete: "Do you really want to delete this access rule?",
      },
    },

    forbiddenTitle: "Forbidden",
    forbiddenText: "You are not allowed to access this application.",
    signedInAs: "Signed in as",

    errors: {
      forbidden: "You are not allowed to access this application.",
      ruleNotFound: "Access rule not found.",
      ruleMissingEmail: "Email is required.",
      ruleInvalidStatus: "Status must be either allowed or blocked.",
      ruleCannotTargetSelf: "You cannot add an access rule for yourself.",
      settingsReadNotAllowed: "You are not allowed to read the access settings.",
      settingsUpdateNotAllowed: "You are not allowed to update the access settings.",
      ruleReadNotAllowed: "You are not allowed to read access rules.",
      ruleInsertNotAllowed: "You are not allowed to create an access rule.",
      ruleDeleteNotAllowed: "You are not allowed to delete an access rule.",
      lastAdmin: "You cannot remove the last user able to manage application access control.",
      invalidScope: "Unknown access scope.",
      invalidModule: "Unknown module.",
    },
  },

  geoBlocking: {
    plural: "Geo-blocking",
    disclaimer:
      "Geo-blocking decides from which countries each application, or a module, can be used. A module's rules apply wherever the module is used, in the main application and in its own; an application's rules only to that application. With no rules, every country is allowed; once a rule is 'Allowed', only the allowed countries are. Access control doesn't override it: to let a user in from anywhere, give them the geo_blocking:bypass permission (e.g. with the geo-blocking-bypass role).",
    settings: "Regions and countries",
    modules: "Modules",
    rules: {
      singular: "Rule",
      target: "Region or country",
      type: "Type",
      region: "Region",
      country: "Country",
      status: "Status",
      statusAllowed: "Allowed",
      statusBlocked: "Blocked",
      createdAt: "Created",
    },
    regions: {
      plural: "Regions",
      singular: "Region",
      countries: "Countries",
    },
    countries: {
      plural: "Countries",
      code: "Code",
    },
    errors: {
      moduleBlocked: "You are not allowed to access this module.",
      ruleAlreadyExists: "A rule for this region or country already exists.",
      regionNameAlreadyExists: "A region with this name already exists.",
      regionInUse: "The region is used by a geo-blocking rule.",
      blocksSelf: "This change would block your own access to the application.",
    },
  },

  usageStatistics: {
    plural: "Usage statistics",
    from: "From",
    to: "To",
    apps: "Applications",
    visits: "Visits",
    description: "Number of distinct users per day (UTC).",
    errors: {
      readNotAllowed: "You are not allowed to see the usage statistics.",
      invalidPeriod: "Choose a valid period, of one year at most.",
    },
  },

  helpRequests: {
    title: "Help",
    dialogTitle: "Request help",
    intro: "Describe your problem: our technical consulting team will reply to you by email.",
    type: "Type",
    subject: "Subject",
    message: "Message",
    screenshot: "Screenshot",
    attachScreenshot: "Attach the screenshot of the current page",
    screenshotUnavailable: "The screenshot of the page could not be taken.",
    attachments: "Attachments",
    attachmentsHint: (maxFiles: number, maxMegabytes: number) =>
      `Up to ${maxFiles} files, ${maxMegabytes} MB in total.`,
    removeAttachment: "Remove attachment",
    diagnosticsInfo:
      "Technical information about your browser and the latest errors of the page will be attached to help us analyze the problem.",
    send: "Send",
    success: "Your request has been sent. You will receive a confirmation by email.",

    errors: {
      invalidForm: "The request could not be read, or it exceeds the allowed size.",
      missingSubject: "Subject is required.",
      subjectTooLong: "The subject is too long.",
      missingMessage: "Message is required.",
      messageTooLong: "The message is too long.",
      tooManyAttachments: (maxFiles: number) => `You can attach up to ${maxFiles} files.`,
      attachmentsTooLarge: (maxMegabytes: number) => `The attachments exceed ${maxMegabytes} MB in total.`,
      invalidScreenshot: "The screenshot is not valid.",
      invalidDiagnostics: "The technical information is not valid.",
    },
  },

  feedbacks: {
    title: "Report to developers",
    dialogTitle: "Send feedback",
    intro: "Report a bug or share a suggestion: the development team will receive it.",
    types: {
      bug: "Bug",
      suggestion: "Suggestion",
      other: "Other",
    },
    success: "Thank you, your feedback has been sent.",

    errors: {
      sendNotAllowed: "You are not allowed to send feedback.",
      invalidType: "Choose a valid feedback type.",
    },
  },

  home: {
    greeting: (name: string) => `Hello, ${name}`,
    subtitle: "Choose a calculator to start a new calculation.",
    availableCalculators: "Available calculators",
  },

  modules: {
    all: "All modules",
    plural: "Modules",
    createNew: "Create new module",
    delete: "Delete module",
    search: "Search modules",
    singular: "Module",

    questions: {
      delete: "Do you really want to delete this module?",
    },

    errors: {
      notFound: "Module not found.",
      insertNotAllowed: "You are not allowed to insert a new module.",
      readNotAllowed: "You are not allowed to read this module.",
      updateNotAllowed: "You are not allowed to update this module.",
      deleteNotAllowed: "You are not allowed to delete this module.",
    },
  },

  categories: {
    all: "All categories",
    plural: "Categories",
    createNew: "Create new category",
    delete: "Delete category",
    search: "Search categories",
    view: "View Categories ",
    edit: "Edit category",

    questions: {
      delete: "Do you really want to delete this category?",
    },

    errors: {
      notFound: "Category not found.",
      insertNotAllowed: "You are not allowed to insert a new category.",
      readNotAllowed: "You are not allowed to read this category.",
      updateNotAllowed: "You are not allowed to update this category.",
      deleteNotAllowed: "You are not allowed to delete this category.",
    },
  },

  roles: {
    plural: "Roles",
  },

  permissions: {
    plural: "Permissions",
  },

  secrets: {
    plural: "Secrets",

    errors: {
      missingValue: "Secret value is missing.",
    },
  },

  adminDashboard: {
    projects: "Browse and manage the projects of all users",
    calculations: "Browse and manage the calculations of all users",
    modules: "Configure calculation modules and their settings",
    categories: "Manage the categories used to group modules",
    items: "Manage the product items catalog",
    clients: "Manage API clients, their secrets and permissions",
    users: "Manage users and their roles",
    accessControl: "Restrict applications and manage allowed or blocked users",
    usageStatistics: "See how many users access the applications and modules each day",
    geoBlocking: "Allow or block applications and modules by country",
  },

  common: {
    date: "Date",
    active: "Active",
    add: "Add",
    errorInvalidVisibility: "Invalid visibility: choose public, internal or hidden.",
    administration: "Administration",
    all: "All",
    alreadyPresent: "Already present",
    angle: "Angle",
    angles: "Angles",
    archived: "Archived",
    author: "Author",
    automaticSync: "Automatic sync",
    back: "Back",
    baseHeight: "Base height",
    cancel: "Cancel",
    categories: "Categories",
    category: "Category",
    clamped: "Clamped",
    thickness: "Thickness",
    close: "Close",
    code: "Code",
    clearPoints: "Clear points",
    undo: "Undo",
    components: "Components",
    confirmed: "Confirmed",
    coordinates: "Coordinates",
    copy: "Copy",
    country: "Country",
    create: "Create",
    created: "Created",
    createNew: "Create new",
    curve: "Curve",
    customer: "Customer",
    dataHasBeenSavedSuccessfully: "Data has been saved succesfully.",
    default: "Default",
    degrees: "Degrees",
    delete: "Delete",
    description: "Description",
    designer: "Designer",
    details: "Details",
    download: "Download",
    dragAndDropOrUploadAnImage: "Drag and Drop or click to upload an image",
    draw: "Draw",
    dropIt: "Drop it!",
    duplicate: "Duplicate",
    edit: "Edit",
    editor: "Editor",
    email: "E-mail",
    error: "Error",
    errorLoadingData: "Error loading data",
    extensions: "Extensions",
    factor: "Factor",
    gap: "Gap",
    failed: "Failed",
    fieldContainsInvalidValueInsertBiggerOne: "The field contains an invalid value, insert a bigger one.",
    fieldInvalidValue: "The field contains an invalid value.",
    colorHexInvalid: "Please enter a valid HEX color in #RRGGBB format.",
    finish: "Finish",
    fixed: "Fixed",
    forbidden: "Forbidden",
    from: "From",
    generalData: "General data",
    genericError: "An error has occurred, please try again.",
    groovingMinHeight: "Grooving min height",
    groovingMaxHeight: "Grooving max height",
    groovingThickness: "Grooving thickness",
    height: "Height",
    help: "Help",
    hidden: "Hidden",
    hide: "Hide",
    home: "Home",
    installed: "Installed",
    intermediate: "Intermediate",
    isVisible: "Is visible",
    languages: {
      arabic: "Arabic",
      chinese: "Chinese",
      croatian: "Croatian",
      czech: "Czech",
      danish: "Danish",
      dutch: "Dutch",
      english: "English",
      estonian: "Estonian",
      finnish: "Finnish",
      french: "French",
      german: "German",
      greek: "Greek",
      hungarian: "Hungarian",
      icelandic: "Icelandic",
      italian: "Italian",
      japanese: "Japanese",
      korean: "Korean",
      latvian: "Latvian",
      lithuanian: "Lithuanian",
      norwegian: "Norvegian",
      plural: "Languages",
      polish: "Polish",
      portugese: "Portugese",
      russian: "Russian",
      singular: "Language",
      slovak: "Slovak",
      slovenian: "Slovenian",
      spanish: "Spanish",
      swedish: "Swedish",
      turkish: "Turkish",
    },
    translations: "Translations",
    lastAccess: "Last access",
    lastModified: "Last modified",
    lastName: "Lastname",
    lastSync: "Last sync",
    length: "Length",
    screwDiameter: "Screw diameter",
    screwHeadHeight: "Screw head height",
    loading: "Loading...",
    location: "Location",
    login: "Login",
    logout: "Logout",
    loop: "Loop",
    mappings: "Mappings",
    max: "Max",
    maxHeight: "Max height",
    meters: "Meters",
    min: "Min",
    minHeight: "Min height",
    minimumOrderQuantity: "Minimum order quantity",
    minOrderQuantity: "Min order qty.",
    minValue: "Min value",
    minWidth: "Min width",
    myProfile: "My Profile",
    name: "Name",
    never: "never",
    next: "Next",
    no: "No",
    noItemsFound: "No items found.",
    notFound: "Not found",
    offer: "Offer",
    ok: "Ok",
    onlyImagesAreAllowed: "Only images are allowed.",
    open: "Open",
    optional: "optional",
    order: "Order",
    overwrite: "Overwrite",
    owner: "Owner",
    pitch: "Pitch",
    print: "Print",
    public: "Public",
    everyOne: "Everyone",
    visibility: "Visibility",
    unitSystem: "Unit system",
    unitSystemMetric: "Metric",
    unitSystemImperial: "Imperial",
    remove: "Remove",
    removeImage: "Remove image",
    reset: "Reset",
    resetToDefault: "Reset to default",
    resetView: "Reset view",
    resourceNotFound: "The resource you are looking for has not been found.",
    roof: "Roof",
    rothoblaas: "Rothoblaas",
    rothoblaasUsers: "Rothoblaas users",
    save: "Save",
    search: "Search",
    select: "Select",
    selectProjectInwhichCreateCopy: "Select the project in which to create the copy",
    sentAt: "Sent at",
    sessionExpired: "Session expired",
    settings: "Settings",
    shape: "Shape",
    shared: "Shared",
    sharedByMe: "Shared by me",
    sharedWithMe: "Shared with me",
    canEdit: "Can edit",
    viewOnly: "View only",
    sharedViaProject: "Shared via the project",
    show: "Show",
    span: "Span",
    spans: "Spans",
    status: "Status",
    stripeLength: "Stripe length",
    substructureHeight: "Substructure height",
    substructures: "Substructures",
    support: "Support",
    supportHeight: "Support height",
    supports: "Supports",
    sync: "Sync",
    testEnvironment: "Test environment",
    to: "To",
    toBeInstalled: "To be installed",
    toInspect: "To inspect",
    type: "Type",
    unlock: "Unlock",
    update: "Update",
    value: "Value",
    visible: "Visible",
    width: "Width",
    quantity: "Quantity",
    yes: "Yes",
    chooseSecondPointDifferentXY: "Choose a second point with a different x and y.",
    zoomIn: "Zoom in",
    zoomOut: "Zoom out",
    youAreNotAllowed: "You are not allowed to access this resource.",
    area: "Area",
    units: "Units",
    volume: "Volume",
    preview: "Preview",
    summary: "Summary",

    unitM: "m",
    unitCm: "cm",
    unitM2: "m²",
    unitM4: "m^4",
    unitKn: "kN",
    unitKnM2: "kN/m²",
    unitKnM: "kN/m",
    unitGPa: "GPa",
    unitMpa: "MPa",
    unitMm: "mm",
    unitInch: "in",
    unitN: "N",
    unitNMm: "N·mm",
    unitPcs: "pcs",
    unitDeg: "°",
    titlePreview: "Preview",
    wasteFactor: "Waste Factor",

    cookiePolicy: "Cookie Policy",
    privacyPolicy: "Privacy Policy",
    termsAndConditions: "Terms and Conditions",
    writePermissions: "Write permissions",
    invitationHasBeenSentSuccessfully: "Invitation has been sent successfully.",
    resendInvite: "Resend invite",
    cancelInviteQuestion: "Do you really want to cancel this invite?",
    deleteInviteQuestion: "Do you really want to delete this invite?",
    invite: "Invite",
    singleOptionAvailable: "Field disabled: only one selectable option is available for the chosen configuration.",
  },

  concreteResinModule: {
    addCalculation: "Save calculation",
    anchorMapping: "Anchor Mapping",
    anchorMappings: "Anchor Mappings",
    cartridges: "Cartridges",
    chemicalAnchor: "Chemical Anchor",
    chemicalAnchors: "Chemical Anchors",
    downloadPdf: "Download PDF",
    downloadPdfNoCalculationsSavedHint: "Save at least one calculation to download the PDF.",
    holeDepth: "Hole Depth ( h₁ )",
    holeDiameter: "Hole Diameter ( d₀ )",
    holeDiameterImperial: "US drill bit [in]",
    holeDiameterImperialInfo: 'Computed from the hole diameter: the smallest 1/64" bit not smaller than it.',
    holeNumbers: "Hole Numbers",
    holes: "holes",
    instruction2:
      "This calculation estimates resin quantity based on the selected overage factor. Overage is often dependent on the application and installer experience.",
    instructionsSummaryPrefix:
      "The resin quantity is estimated on the hole filling given in the product ETA: starting from the bottom of the cleaned hole, fill it with adhesive up to about two thirds, slowly withdrawing the static mixing nozzle as the hole fills to avoid air pockets; if the nozzle does not reach the bottom of the hole, use a suitable extension nozzle. Apply and inject the product according to Rothoblaas literature for the intended use. Rothoblaas reserves the right to change calculation methods, designs or models without notice or liability; the latest design resources are on",
    instructionsSummarySuffix: ".",
    liters: "Liters",
    mlQuantity: "ml Quantity",
    noCalculationsSaved: "No calculations saved",
    overageFactor: "Construction Waste Factor",
    rodDiameter: "Rod Diameter ( d )",
    titleCalculationsSaved: "Calculations saved",
    titleHoleGeometry: "Hole Geometry",
    titleInstructionsAndNotes: "Instructions and Notes",
    titleSummary: "Summary",
    totalVolumeRequired: "Total Volume Required",
    unitOverageFactor: "%",
    unitSystemImperial: "Imperial",
    unitSystemMetric: "Metric",
    waste: "waste",
    cartridgesShort: "cart.",
    unitLiters: "L",
    errors: {
      anchorInUseByMapping: "The anchor is used by a mapping: delete its mappings first",
      anchorInUseByEntry: "The anchor is used in saved calculations: set it to Hidden instead of deleting it",
      anchorNotFound: "The chemical anchor was not found.",
      anchorNotAvailable: "The selected chemical anchor is no longer available: choose another one.",
      anchorAlreadyPresent: "The item is already a chemical anchor.",
      mappingAlreadyPresent: "The chemical anchor already has a mapping with these diameters.",
      invalidRodDiameter: "The rod diameter is not available for the selected chemical anchor.",
      invalidHoleDiameter: "The hole diameter doesn't match the selected rod diameter.",
      mappingNotFound: "The anchor mapping was not found.",
      entryMissingChemicalAnchor: "Choose a chemical anchor before saving the calculation",
      entryIncompleteCalculation: "Fill in all the fields before saving the calculation",
    },
  },

  usLiftingCalculatorModule: {
    calculator: {
      aboveTarget: (target: number) => `The lift is within capacity but exceeds the ${target}% target.`,
      allSavedMembersCleared: "All saved members were cleared.",
      allowAnotherDevice: "Allow another device when the selected device has no verified configuration.",
      alternateDevice: "No verified configuration exists with the selected rigging device.",
      anchorPointsDetail: "Number of Anchor Points",
      angleDiagramAlt: "Lifting angle ranges of the selected device and lift type",
      angleDiagramTitle: "Lifting angle ranges",
      angleFromHorizontal: "Angle from Horizontal",
      angleLegendTitle: "Angle range legend",
      anglePhotoTitle: "Lifting angle",
      applicableLifts: "Applicable Lifts",
      automatic: "Automatic",
      backToModeSelection: "Back to mode selection",
      beamDepth: "Beam Depth",
      beamDepthDetail: "Beam Depth",
      beamOrientationAlt: (orientation: string) => `RAPTOR on the beam: ${orientation}`,
      beamSpecifications: (depth: string, width: string) => `${depth} depth × ${width} width`,
      beamWidth: "Beam Width",
      beamWidthDetail: "Beam Width",
      buildingPdf: "Building PDF…",
      capacityTablePreview: "Single Lifting Device Capacity Table Preview",
      capacityUtilization: "Capacity Utilization",
      clearSavedMembers: "Clear Saved Members",
      clearSavedMembersConfirm:
        "Are you sure you wish to clear all saved members? This removes saved lifts from every mode and cannot be undone.",
      clearSavedMembersTitle: "Clear Saved Members?",
      cltPanelDepthDetail: "CLT Panel Depth",
      cltThickness: "CLT Thickness",
      columnSpecifications: (width: string) => `${width} width`,
      columnWidth: "Column Width",
      columnWidthDetail: "Column Width",
      completedExcelTemplate: "Completed Excel Template",
      contactTechSupport: "Contact tech support",
      contactUs: "Contact us",
      copied: "Copied",
      copyFailed: "Copy Failed",
      copyTable: "Copy Table",
      customLiftDesign: "Custom Lift Design",
      datasheetNoticeText:
        "Consult the technical datasheet for each lifting device for conditions of use, installation instructions, fastener capacities, approved lifting orientations, and all applicable requirements and limitations.",
      datasheetNoticeTitle: "This tool is not a substitute for the applicable technical datasheets.",
      decreaseByEighth: "Decrease by 1/8 inch",
      deviceFamilyUnavailable: (name: string) => `${name} — Unavailable`,
      deviceImageAlt: "Selected lifting device",
      devices: "Lifting Devices",
      dimensionFormatError: "Enter the dimension in inches, e.g. 4 1/8.",
      downloadExcel: "Download Excel",
      downloadTemplate: "Download Template",
      downloaded: "Downloaded",
      draftOpened: "Draft Opened",
      eServiceTemplateRequired: "Approved eService template required",
      eServiceXlsx: "eService XLSX",
      editingMember: (name: string) => `Editing ${name}`,
      engineeringReviewRequired: "Engineering review required",
      enlargeImage: "Enlarge image",
      enterLiftName: "Enter a lift name.",
      enterNameOrSave: "Enter a member name or select Save Lift to name it in the pop-up.",
      enterWeightBeforeSaving: "Enter the lifted member weight before saving.",
      enterWeightForRecommendation: "Enter the lifted member weight to create a recommendation.",
      export: "Export",
      exportFailed: "Export Failed",
      exportFilledExcel: "Export Filled Excel",
      exportHelp: "Choose what to send to the PDF report.",
      exportMemberExcel: "Export Member Excel",
      exportReport: "Export report",
      fastenerTakeoff: "Fastener Material Takeoff",
      guidanceApplication: "Application",
      guidanceInstallation: (device: string) => `${device} installation`,
      guidanceLabel: "Installation guidance",
      guidanceLoadDirections: "Permitted load directions",
      guidanceMinimumDistances: "Minimum distances",
      guidanceMinimumUnavailable: "Enter a valid beam width to see the minimum-distance detail.",
      howRecommendationsSelected: "How recommendations are selected",
      increaseByEighth: "Increase by 1/8 inch",
      installation: "Installation",
      installationIllustrations: "Installation illustrations",
      installationIllustrationsTitle: "WASP / WASPL - Installation illustrations",
      installationLine: (installation: string) => `Installation: ${installation}`,
      installationType: "Installation Type",
      installationTypeColumn: "Installation Type",
      installationTypes: {
        inclined: "Inclined",
        perpendicular: "Perpendicular with Milling",
        standard: "Standard",
      },
      issues: {
        angleUnsupported: "Select a supported lifting angle. The entered angle was not replaced.",
        beamDepthMaximum: (maximum: string) => `Beam depth must not exceed ${maximum}.`,
        beamDepthMinimum: (minimum: string) =>
          `Beam depth must be at least ${minimum}. The supplied dimension was not changed.`,
        beamDepthPositive: "Beam depth must be a positive number. The supplied dimension was not changed.",
        beamOrientationRestricted: "The selected RAPTOR orientation is restricted by beam width and screw count.",
        beamWidthMaximum: (maximum: string) => `Beam width must not exceed ${maximum}.`,
        beamWidthMinimum: (minimum: string) =>
          `Beam width must be at least ${minimum}. The supplied width was not changed.`,
        beamWidthPositive: "Beam width must be a positive number. The supplied width was not changed.",
        cltEdgeGrainMinimum: (minimum: string) =>
          `CLT thickness must be at least ${minimum} for edge-grain and tilt-up lifts.`,
        cltFaceGrainMinimum: (minimum: string, device: string) =>
          `CLT thickness must be at least ${minimum} for ${device} face-grain lifts.`,
        cltSlabMaximum: (maximum: string) => `CLT slab thickness must not exceed ${maximum}.`,
        cltThicknessPositive: "CLT thickness must be a positive number. The supplied dimension was not changed.",
        columnWidthMinimum: (minimum: string) =>
          `Column width must be at least ${minimum}. The supplied dimension was not changed.`,
        columnWidthPositive: "Column width must be a positive number. The supplied dimension was not changed.",
        memberDepthMaximum: (maximum: string) => `The member dimension must not exceed ${maximum}.`,
      },
      liftName: "Lift Name",
      liftNamePlaceholder: "Enter a lift name",
      liftNotOverwritten: "The lift could not be overwritten.",
      liftNotSaved: "The lift could not be saved.",
      liftOverwritten: (name: string) => `${name} was overwritten with its current properties and recommendation.`,
      liftRecommendation: "Lift Recommendation",
      liftSaved: (name: string) => `${name} was saved with its current properties and recommendation.`,
      liftType: "Lift Type",
      liftTypeFallbackNote:
        "No verified tilt-up configuration is available. Vertical Face-Grain is shown as an alternative; this does not verify a tilt-up operation.",
      liftedMemberWeight: "Lifted Member Weight",
      liftingDevice: "Lifting Device",
      liftingPoints: "Lifting points",
      liftingSetup: "Lifting Setup",
      liftingSetupValue: (screws: number | string, devices: number | string) => `${screws} screws × ${devices} devices`,
      marginLine: (margin: string) => `Margin: ${margin}`,
      memberLoaded: (name: string) => `${name} was loaded.`,
      memberName: "Member Name",
      memberNamePlaceholder: "Enter a member name",
      memberSpecifications: "Member Specifications",
      memberType: "Member Type",
      memberTypes: {
        beam: "Beam",
        "clt-slab": "CLT Slab",
        "clt-wall": "CLT Wall",
        column: "Column",
      },
      memberWeight: "Member Weight",
      memberWorkspace: "Member Workspace",
      minimumDistancesCaption: (mounting: string, screwCount: number | string) =>
        `${mounting} · Screws per Device: ${screwCount}`,
      mode1Description: "Configure and verify one specific lift.",
      mode1Pill: "Mode 1",
      mode2Description: "Enter one member to find suitable lift options.",
      mode2Eyebrow: "Mode 2: Lift Recommendation",
      mode2Pill: "Mode 2",
      mode3Description: "Upload many members for batch lift recommendations.",
      mode3Pill: "Mode 3",
      modeSelection: "Mode selection",
      moduleTitle: "US Lifting Calculator",
      nameInUse: (name: string) => `A saved lift is already named “${name}”. Overwrite it, or enter another name.`,
      newMember: "New Member",
      noCapacitySubtitle: "The selected combination is not listed in the embedded summary tables.",
      noCapacityTable: "No capacity table is listed for this configuration.",
      noSavedLifts: "No saved lifts are available.",
      noSavedMembers: "No saved members",
      noSavedMembersToProcess: "Save a member in Mode 1 or Mode 2 first.",
      noSavedRecommendationLifts: "No saved Mode 2 lifts are available.",
      noScrews: (filters: string[]) => `No screws ${filters.join(" and ")}`,
      noScrewsListed: "No screws listed",
      notAvailableYet: "Available in a later version.",
      numberOfAnchorPoints: "Number of Anchor Points",
      numberOfLiftingDevices: "Number of Lifting Devices",
      numberOfRaptors: "Number of RAPTORs",
      openEmailEnquiry: "Open Email Enquiry",
      openInMode1: "Open in Mode 1",
      openInMode2: "Open in Mode 2",
      openOrCreateMember: "Open or create a member",
      openSavedSelect: "Open each saved lift, select a capacity-verified option for the takeoff, and save it again.",
      openSupportMenu: "Open the support menu",
      optionAlternativeTitle: (slot: number, label: string) => `Option ${slot} - ${label} Alternative`,
      optionTitle: (slot: number, label: string) => `Option ${slot} - ${label}`,
      orientation: "Orientation",
      orientationUnavailable: (orientation: string) => `${orientation} - Unavailable at this Beam Width`,
      overwriteExisting: "Overwrite Existing",
      overwritten: "Overwritten",
      parallel: "Parallel",
      parallelMounting: "Parallel Mounting",
      perpendicular: "Perpendicular",
      perpendicularMounting: "Perpendicular Mounting",
      perpendicularWithMillingShort: "Perp. w/ Milling",
      printCurrentScreen: "Print Current Screen",
      printCurrentScreenHelp: "The lift on screen, with its result and guidance images.",
      printPdf: "Print PDF",
      printSavedMembers: "Print Saved Members",
      printSavedMembersHelp:
        "Each saved lift prints on its own page, followed by a combined fastener takeoff when Mode 2 lifts are available.",
      processList: "Process List",
      processSavedMembers: "Process List of Saved Members",
      productCode: "Product Code",
      productPage: "Product page",
      properties: "Properties",
      quantityRequired: "Quantity Required",
      raptors: "RAPTORs",
      readyToSave: "The current recommendation is ready to save.",
      reasons: {
        noAdditionalVgspl: "No additional verified VGSPL screw count or length is available at these settings.",
        noVgsplAlternative: "No verified VGSPL alternative is available at these settings.",
        notEligible: (family: string) => `No eligible ${family} configuration is available at these settings.`,
        notWithinCapacity: (family: string) => `No ${family} configuration is within capacity at these settings.`,
        waspNoAlternative: (series: string) =>
          `No verified ${series} alternative is available with WASP at these settings.`,
        waspNotWithinCapacity: (series: string) => `No ${series} configuration is within capacity at these settings.`,
      },
      recommendedConfigurations: "Recommended Configurations",
      recommendedLiftConfiguration: "Recommended Lift Configuration",
      requiredFields: (fields: string) => `required: ${fields}`,
      reset: "Reset",
      result: "Result",
      review: {
        allSupportedDevices: "All supported devices",
        determinism:
          "This is a deterministic table search, not live AI. Selectors show distinct table angles; range diagrams remain reference illustrations. This update is not engineering approval.",
        deviceScope: "Device scope for this review",
        methodChecks:
          "Check dimensions, orientation, table angle, screw length and quantity before ranking. Missing required inputs cannot verify a lift.",
        methodRaptor:
          "RAPTOR beam and face-grain lifts compare VGSPL, a distinct VGSPL screw-count/length alternative, and HBSPL. RAPTOR MINI and RAPTOR edge-grain or tilt-up lifts compare VGSPL and HBSPL only.",
        methodWasp: "When allowed, device fallback is WASP → WASPL → RAPTOR MINI → RAPTOR.",
        raptorNote: "RAPTOR application and minimum-distance images are shown with the results.",
        raptorOnly: "RAPTOR only",
        scopeNote: "RAPTOR-only scope limits selection and searches; it does not establish validation.",
      },
      reviewSettings: "Recommendations and review settings",
      riggingBasis:
        "Total rigging capacity = anchor count x single-device capacity at the lower, most conservative bound of the selected angle range (or exactly 90 degrees). Equal load sharing is assumed, not confirmed by this tool.",
      riggingMemo:
        "This is not an approved orientation from the technical datasheet. Applications with a greater number of anchor points than published configurations are possible when designed by external qualified rigging engineer.",
      saveLift: "Save Lift",
      saveMember: "Save Member",
      saveMode1Lift: "Save Mode 1 Lift",
      saveMode2Lift: "Save Mode 2 Lift",
      saved: "Saved",
      savedLiftConfigurations: "Saved Lift Configurations",
      savedLiftCount: (count: number) => (count === 1 ? "1 saved lift" : `${count} saved lifts`),
      savedLiftLoaded: "Saved lift loaded. Change any property and select Save Lift to update it.",
      savedLifts: "Saved Lifts",
      savedLiftsEyebrow: "Saved lifts",
      savedLiftsFileSuffix: "saved-lifts",
      savedMembers: "Saved Members",
      savedMembersAvailable: (count: number) => `Saved members available: ${count}`,
      savedMembersDescription:
        "Use saved member properties to generate list recommendations without an Excel upload. Existing saved lifts are not changed.",
      savedOn: (mode: string, date: string) => `${mode} · Saved ${date}`,
      savedPill: "Saved",
      savedRecommendations: "Saved Recommendations",
      savedResult: "Saved Result",
      screw: "Screw",
      screwCountBeamWidth: (count: number) => `${count} - Unavailable at this Beam Width`,
      screwCountNotListed: (count: number) => `${count} — not listed`,
      screwLength: "Screw Length (in)",
      screwLengthOption: (length: string, code: string) => `${length} — ${code}`,
      screwLengthPhotoAlt: "Screw length compared with the element thickness",
      screwLengthReferenceText:
        "For lifting applications with the load component perpendicular to the timber grain, in order to reach the maximum capacity of the lifting system, it is recommended that the screw length embedded in timber, excluding the 10 mm RAPTOR plate, exceed 0.7·d (L - 10 mm > 0.7·d; d = thickness of the timber element) to avoid cracking. In the event that the ratio is not fulfilled, critical shear and deflection checks can be carried out according to AWC-NDS Chapters 3 & 5.",
      screwLengthReferenceTitle: "Influence of the ratio of screw length to the element thickness",
      screwLengthViewerTitle: "Screw length reference",
      screwType: "Screw Type",
      screwsAtLeast: (minimum: string) => `at least ${minimum}`,
      screwsColumn: "# Screws",
      screwsLongerThan: (minimum: string) => `longer than ${minimum}`,
      screwsPerDevice: "Screws per Lifting Device",
      screwsPerDeviceDetail: "Screws per Device",
      screwsPerRaptor: "Screws per RAPTOR",
      screwsPerRaptorFigure: (count: number) => `${count} screws per RAPTOR`,
      screwsPerRaptorFigureAlt: (count: number) => `RAPTOR installed with ${count} screws`,
      screwsRow: (count: number) => `${count} screws`,
      screwsShorterThan: (maximum: string) => `shorter than ${maximum}`,
      selectCompletedTemplate: "Select a completed template to begin.",
      selectListedScrew: "Select a listed screw configuration before saving.",
      selectNewMember: "Select New Member to create a saved lift, or open an existing saved member.",
      selectedConfiguration: "Selected Configuration",
      shortScrewEmbedment: "The current fastener does not penetrate 0.7*d",
      shortScrewRatio: (length: string) =>
        `The selected screw is shorter than the recommended 0.7d length (${length}). Review critical shear and deflection checks according to AWC-NDS Chapters 3 & 5. These checks are not performed by this tool.`,
      specificGravity: "Specific Gravity",
      statusAboveTarget: "Verified - Above Target Utilization",
      statusAlternativeLift: "Capacity verified - alternative lift",
      statusDimension: "Dimension out of range",
      statusEngineeringReview: "Verified — Engineering review required",
      statusNoCapacity: "No Capacity Listed",
      statusNotVerified: "Not Verified",
      statusOrientation: "Orientation unavailable",
      statusPending: "Pending",
      statusReview: "Review",
      statusUnavailable: "Unavailable",
      statusVerified: "Verified",
      submitFeedback: "Submit feedback",
      takeoffEmailBasis: "Calculation basis: USA - NDS",
      takeoffEmailIncluded: (count: number) => `Saved Mode 2 lifts included: ${count}`,
      takeoffEmailRequest: "Please review availability and pricing for the listed quantities.",
      takeoffEmailSubject: "US Lifting Calculator - fastener material takeoff",
      takeoffEmailTotal: (total: string) => `Total fasteners: ${total}`,
      takeoffEmailUnits: "Unit presentation: imperial (lb / in)",
      takeoffLength: "Length",
      takeoffTotal: "TOTAL",
      targetUtilization: "Set Target Utilization (%)",
      targetUtilizationShort: "Target Utilization",
      techSupportSubject: "US Lifting Calculator - technical support",
      template: "Template",
      templateDownloaded: (device: string) => `${device} template downloaded in imperial (lb / in) units.`,
      totalCapacity: "Total Capacity",
      totalFasteners: "Total Fasteners",
      totalRiggingCapacity: "Total Rigging Capacity",
      totalScrews: "Total Screws",
      unitIn: "in",
      unitLb: "lb",
      upload: {
        columns: {
          anchors: "# Anchors",
          angle: "Angle",
          liftingDevice: "Lifting Device",
          memberName: "Member Name",
          memberType: "Member Type",
          screw: "Screw",
          screwCount: "# Screws",
          status: "Status",
          totalCapacity: "Total Lifting Capacity",
          totalScrews: "Total Amount of Screws",
          utilization: "Utilization",
          weight: "Weight",
        },
        defaultAngle: "(default)",
        enteredTableReference: "Entered angle → table reference",
        externalRigging: "External rigging design required",
        faceGrainAlternative: "Vertical Face-Grain alternative; not a tilt-up verification.",
        fields: {
          angle: "Lifting Angle",
          columnNotWithWasp: "Column is not available with WASP / WASPL",
          columnRequiresRaptorMini: "Column requires RAPTOR MINI",
          device: "Lifting Device",
          installationType: "Installation Type",
          liftType: "Lift Type",
          memberType: "Member Type",
          thicknessDepth: "Thickness / Depth",
          weight: "Weight",
        },
        firstRows: (shown: number, total: number) =>
          `Showing the first ${shown} of ${total} members. The completed Excel includes all members.`,
        issues: {
          angleColumn: "Column lifting requires exactly 90 degrees from horizontal.",
          angleNotNumeric:
            "Lifting Angle must be a numeric angle from horizontal or a displayed angle range for the selected device.",
          angleOutOfRange: (minimum: number) =>
            `Lifting Angle must be from ${minimum} through 90 degrees from horizontal for the selected device. The entered angle was not changed.`,
          angleOutsideBand:
            "The entered sling angle is outside the selected table band. Re-enter and recalculate the actual angle; an alternative rigging angle is not verified.",
          angleRiggingMismatch:
            "The proposed angle does not match the entered rigging. Correct the input before using a result.",
          beamWidthRequired: "Beam Width is required for Beam rows.",
          materialConflict: (text: string) =>
            `Material name and Specific Gravity conflict in "${text}". Confirm the intended material; no value was substituted.`,
          materialUnrecognized: (text: string) =>
            `Unrecognized timber material "${text}". Use a listed material, DF, SYP, or a supported numeric Specific Gravity.`,
          materialUnsupported: (text: string, choices: string) =>
            `Unsupported Specific Gravity "${text}". Select ${choices}; the entered value was not replaced.`,
          orientationInvalid: "Orientation must be Parallel or Perpendicular.",
          orientationRestricted: "The requested RAPTOR orientation is restricted by beam width.",
          processingFailed: "This member could not be processed.",
        },
        missing: (fields: string) => `Missing or invalid: ${fields}.`,
        notices: {
          andMore: (names: string, more: number) => `${names} and ${more} more`,
          anglesMapped: (count: number) =>
            `${count} ${count === 1 ? "angle was" : "angles were"} mapped to table bands: the lower value of the band is the table reference. The entered sling angle is unchanged; it must remain within that band.`,
          appliesTo: (names: string) => `Applies to: ${names}.`,
          materialDefault: (value: string, names: string) =>
            `G = ${value} was used for blank material inputs: ${names}. Confirm the timber material.`,
          vgs13: "VGS13 screws and their corresponding working loads are applicable to WASPL only.",
        },
        processed: (members: number, verified: number, saved: number) =>
          `Processed ${members} member${members === 1 ? "" : "s"}. ${verified} produced verified recommendations. ${saved} saved as members.`,
        processedResults: "Processed Results",
        processedSaved: (members: number, verified: number) =>
          `Processed ${members} saved members. ${verified} produced verified recommendations. Existing saved lifts are unchanged.`,
        processing: "Processing workbook...",
        processingSaved: "Processing saved members...",
        requestedUsed: "Requested → Used",
        unverified: "Unverified",
        verified: "Verified",
        wasplOnlyShort: "WASPL only",
        workbookExported: "Filled Excel sheet exported.",
      },
      uploadFilledTemplate: "Upload Filled Template",
      uploadList: "Upload List for Recommendations",
      uploadListEyebrow: "Upload List",
      uploadListFileSuffix: "upload-list",
      uploadMemberList: "Upload Member List",
      useForTakeoff: "Use this option for the saved-lift takeoff",
      utilization: "Utilization",
      verifiedRecommendationRequired: "A capacity-verified recommendation is required before this lift can be saved.",
      viewSavedLifts: "View Saved Lifts",
      wallPanelLiftType: "Wall Panel Lift Type",
      waspOnly: "WASP only - this configuration is not permitted with WASPL.",
      wasplOnly: "WASPL only - this configuration is not permitted with WASP.",
      weight: "Weight",
      weightLabel: "Lifted Member Weight",
      weightPlaceholder: "Type weight here, e.g. 2500",
      woodSpecificGravity: "Wood Type / Specific Gravity",
      woodSpecificGravityShort: "Wood / Specific Gravity",
    },
    generalPrinciples: {
      title: (device: string) => `${device} — General principles`,
      introRaptor:
        "From the supplied RAPTOR technical datasheet. These principles are specific to RAPTOR; consult the applicable datasheet for other lifting devices.",
      introRaptorMini:
        "From the supplied RAPTOR MINI technical datasheet. These principles are specific to RAPTOR MINI.",
      introWasp: "From the supplied WASP / WASPL technical datasheet. These principles apply to both WASP and WASPL.",
      fastenerLengthPlate:
        "The choice of fastener length is to be based each time on the dimensions of the wooden element, on the fastener’s positioning, on the lift angle, on the weight of the load to be lifted and the arrangement of the lifting plate. In all cases, it is recommended that the connectors have greater length than the minimum required, but such that the tip does not protrude from the element to be lifted.",
      fastenerLength:
        "The choice of fastener length is to be based each time on the dimensions of the wooden element, on the fastener’s positioning, on the lift angle, and on the weight of the load to be lifted. In all cases, it is recommended that the connectors have greater length than the minimum required, but such that the tip does not protrude from the element to be lifted.",
      wllBasis:
        "The rigging capacity values (WLL) provided are based on calculations made according to ICC-ES Evaluation Report ESR-4645, the AWC-NDS 2018, AWC Technical Report 12, and the results of tests performed. The source documentation states a safety factor meeting or exceeding 5.0. The applicable requirements of ",
      oshaLink: "OSHA Section 1926.251",
      wllBasisAfterLink:
        " must be confirmed for the specific lifting operation. This calculator does not establish OSHA compliance or approval of the complete rigging arrangement.",
      specificGravityBasis: (maxCapacity: string) =>
        `A Specific Gravity of G = 0.42 was considered as bases for the calculation. For values with different timber species and Specific Gravity the following conversion table with the modification factors was used. The values never exceed the maximum capacity of the plate of ${maxCapacity}.`,
      specificGravityBasisWasp: (waspCapacity: string, wasplCapacity: string) =>
        `A Specific Gravity of G = 0.42 was considered as bases for the calculation. For values with different timber species and Specific Gravity the following conversion table with the modification factors was used. The values never exceed the maximum capacity of ${waspCapacity} for the WASP and ${wasplCapacity} for the WASPL.`,
      capacityFactors:
        "All values of screw capacity are adjusted by a dynamic acceleration factor of 1/1.1, a load duration factor CD = 1.25, temperature factor Ct = 1.0, and wet service factor CM = 1.0, and assume screws are installed on-site directly prior to lifting.",
      brittleFailureElement:
        "Checks for potential reductions in strength due to possible brittle failure related to the geometry of the lifted element must be conducted separately in accordance with AWC-NDS, particularly in scenarios where less than 70% of the lifted element depth is engaged by the fastener.",
      brittleFailureBeam:
        "Checks for potential reductions in strength due to possible brittle failure related to the geometry of the lifted element must be conducted separately in accordance with AWC-NDS, particularly in scenarios where less than 70% of the beam depth is engaged by the fastener.",
      loadToGrain:
        "All CLT calculations assume a load-to-grain angle of 90°, regardless of orientation, with no dowel length reductions considered for edge-grain lifting.",
      otherConfigurationsPlate:
        "For lifting plate rigging capacity calculation in installation configurations other than those indicated here, contact Rothoblaas Technical Department.",
      otherConfigurations:
        "For rigging capacity calculation in installation configurations other than those indicated here, contact Rothoblaas Technical Department.",
      fatigue: (device: string) =>
        `To conform with the fatigue limits of Service Class 0 of ASME BTH01-2023, no ${device} lifting device may be used for more than 20,000 load cycles.`,
      noPreDrilling: (specificGravity: string) =>
        `The rigging capacity values provided are calculated in the case of the plate fixed with screws inserted without pre-drilling. In accordance with ICC Evaluation Report ESR-4645. All minimum distance specifications must be increased for specific gravity values greater than ${specificGravity}.`,
      installation:
        "Fasteners must be installed in accordance with their relevant installation guide to avoid damage and maintain full performance in accordance to their design strength parameters.",
      noImpactWrenches:
        "The use of pulse screw guns/impact wrenches is not permitted. Respect the insertion angle with the help of a pilot hole and/or installation template. Avoid bending. Ensure correct tightening. We recommend the use of torque-controlled screwdrivers, e.g. with TORQUE LIMITER. Alternatively, tighten with a torque wrench.",
      hbsPlateValues:
        "The values indicated in the data sheet for the transport plate fixed with HBS PLATE screws have been calculated considering the geometry and mechanical parameters of the HBS PL version.",
      hbsPContact:
        "For the load-bearing capacities of the transport plate fixed with the HBS P screw, contact the Technical Office for further support.",
      singleUse: (screws: string) =>
        `${screws} may only be used once. Once tightened and loaded, the screws must not be loosened and used a second time to secure the transport plate. As soon as the timber element to be transported has been lifted to its final position and the transport plate is no longer needed for this purpose, the screws must be unscrewed and disposed of properly.`,
      screwsHbsPlateVgs: "HBS PLATE and VGS screws",
      screwsVgs: "VGS screws",
      vgsPlateReuse: (device: string) =>
        `The VGS PLATE is the only fastener eligible for reuse. Refer to the technical data sheets and instruction manuals for VGS PLATE and the corresponding JIG REUSE when using either with the ${device} device.`,
      qualifiedPersonnelPlate:
        "The lifting plate may only be used by qualified personnel. The user manual (supplied as a QR code on the product and available at www.rothoblaas.com) must be read and understood before use. The information and instructions contained therein must be followed. If in doubt, contact the Rothoblaas Technical Department before use.",
      qualifiedPersonnelDevice:
        "The lifting device may only be used by qualified personnel. The user manual (supplied as a QR code on the product and available at www.rothoblaas.com) must be read and understood before use. The information and instructions contained therein must be followed. If in doubt, contact the Rothoblaas Technical Department before use.",
      specificGravityColumn: "Specific Gravity [G]",
      modificationFactorColumn: "Modification Factor",
    },
    admin: {
      family: "Family",
      devicesAndScrews: "Devices & Screws",
      lifts: "Lifts",
      anchorCount: "Anchor count",
      anchorCounts: "Anchor counts",
      angle: "Angle",
      capacities: "Capacities",
      capacity: "Capacity",
      capacityFactor: "Capacity factor",
      capacityLb: "Capacity [lb]",
      device: "Device",
      deviceFamilies: "Device families",
      deviceFamily: "Device family",
      deviceFamilyLiftType: "Device family lift type",
      deviceFamilyLiftTypes: "Dimension limits",
      deviceScrewMappings: "Device screw mappings",
      deviceScrewMapping: "Device screw mapping",
      devices: "Devices",
      diameter: "Diameter",
      installationType: "Installation type",
      length: "Length",
      liftType: "Lift type",
      liftTypes: "Types",
      maxCapacity: "Max capacity",
      maxMemberDepth: "Max member depth",
      maxMemberWidth: "Max member width",
      memberType: "Member type",
      minMemberDepth: "Min member depth",
      minMemberWidth: "Min member width",
      plateThickness: "Plate thickness",
      productUrl: "Product URL",
      requiresRiggingReview: "Requires rigging review",
      screw: "Screw",
      screwCount: "Screws per device",
      screwFamilies: "Screw families",
      screwFamily: "Screw family",
      screws: "Screws",
      specificGravities: "Specific gravities",
      specificGravity: "Specific gravity",
      washer: "Washer",
    },
    errors: {
      calculationInvalidInput: "The calculation contains an invalid value.",
      calculationNotAvailable: "A selected value is no longer available. Review the selection.",
      capacityAlreadyExists:
        "A capacity with the same device, screw, lift type, installation type, screws per device and angle already exists.",
      capacityNotFound: "The capacity was not found.",
      deviceAlreadyExists: "The item is already a device.",
      deviceFamilyNameAlreadyExists: "A device family with this name already exists.",
      deviceFamilyLiftTypeAlreadyExists: "The device family already has this lift type.",
      deviceFamilyLiftTypeInvalidLimits:
        "The member limits must be greater than zero, and each maximum not lower than its minimum.",
      deviceFamilyLiftTypeNotFound: "The device family lift type was not found.",
      deviceScrewMappingAlreadyExists: "The device family already has this screw family.",
      deviceScrewMappingNotFound: "The device screw mapping was not found.",
      deviceInUseByLift: "The device is used in saved lifts: set it to Hidden instead of deleting it.",
      deviceNotFound: "The device was not found.",
      liftTypeAnchorCountAlreadyExists: "The lift type already has this anchor count.",
      liftTypeAnchorCountNotFound: "The anchor count was not found.",
      screwAlreadyExists: "The item is already a screw.",
      screwInUseByLift: "The screw is used in saved lifts: set it to Hidden instead of deleting it.",
      screwNotFound: "The screw was not found.",
      specificGravityAlreadyExists: "A specific gravity with this value already exists.",
      specificGravityInUseByLift:
        "The specific gravity is used in saved lifts: set it to Hidden instead of deleting it.",
      specificGravityNotFound: "The specific gravity was not found.",
      uploadFileTooLarge: "The file is larger than 10 MB.",
      uploadInvalidWorkbook: "The uploaded file is not a valid .xlsx workbook.",
      uploadMetricTemplate:
        "Use the USA - NDS template with weights in lb and dimensions in inches. Metric or European templates are not accepted.",
      uploadMissingColumns: "The template is missing required columns.",
      uploadNoHeader: "The workbook must include a header row with Member Name and Member Type.",
      uploadNoRows: "No member rows were found.",
      uploadNoWorksheet: "No worksheet was found in the uploaded workbook.",
    },
  },

  deckDesignerModule: {
    configurations: {
      singular: "Configuration",
      plural: "Configurations",
      errors: {
        connectorFamilyNotAvailable: "The selected connector family is no longer available: choose another one.",
        connectorNotAvailable: "The selected connector is no longer available: choose another one.",
        screwNotAvailable: "A selected screw is no longer available: choose another one.",
        supportFamilyNotAvailable: "The selected support family is no longer available: choose another one.",
        supportNotAvailable: "The selected support is no longer available: choose another one.",
        substructureNotAvailable: "The selected substructure is no longer available: choose another one.",
        invalidTotalHeight: "Maximum terrace extrados height (H) is not valid for the selected deck build-up.",
        noSupportForRequiredHeightRange: (min: number, max: number) =>
          `With the current configuration the support must cover a height (h) ${min === max ? `of ${min} mm` : `from ${min} to ${max} mm`}: no available support covers this range. Adjust H or the slope.`,
        noSupportForSpacing:
          "The spacing between supports along the joists is too small for the available supports (at least 1.5 times their width is needed). Reduce the joist spacing.",
        noSupportFamilyOptions: "No options available with the current configuration.",
        noSupportForSlopeDrop: {
          title: "No available support can cover the terrain level difference, whatever H is.",
          requiredRange: (min: number, max: number) =>
            `Required support height (h): from ${min} to ${max} mm (difference ${max - min} mm)`,
          widestSupport: (supportCode: string, rangeMm: number) =>
            `Largest difference covered by a support: ${rangeMm} mm (${supportCode})`,
          fix: (maxSlope: number | null) =>
            `${maxSlope != null ? `Reduce the slope to at most ${maxSlope}°` : "Reduce the slope"}, then pick H among the compatible values.`,
        },
        noCompatibleConnectorSubstructureScrew: "No compatible screw for the selected connector and substructure.",
        noCompatibleSupportScrew: "No compatible screw for the selected support.",
        noCompatibleConnectorBoardScrew: "No compatible screw for the selected board thickness.",
        noCompatibleConnectorFamilyMapping:
          "No connector family is compatible with the selected substructure, fastening type, grooving type and gap.",
        noCompatibleConnectorFamilyBoardThickness: "No compatible connector family fits the current board thickness.",
        noCompatibleConnectorFamilyBoardThicknessAndGroovingHeight:
          "No compatible connector family fits the current board thickness and grooving height.",
        noCompatibleConnector: "No connector is compatible with the current configuration.",
        noCompatibleSupportFamilyMapping:
          "No support family is compatible with the selected substructure material class.",
      },

      calculation: {
        shapeAndGeometricData: "Shape and geometric data",
        completeFieldsToContinue: "Fill in all the fields on this page to continue",
        showBillOfQuantities: "Show bill of quantities",
        boards: "Boards",
        billOfQuantities: "Bill of quantities",
        downloadPdf: "Download PDF",
        downloadPdfDescription:
          "Download the PDF with the full summary of the configuration: geometric data, boards, substructure, supports, deck views, support reactions and bill of products.",
        downloadPdfDisabledHint: "Complete the configuration to download the PDF.",
        downloadPdfDisabledBoardFitHint: "Resolve the board width and gap warning to download the PDF.",
        billOfQuantitiesEmpty: "Select the deck data to calculate the bill of quantities.",
        calculationDetailsEmpty: "Select the deck data to view the calculation details.",
        showCompatibleValues: "See compatible values",
        stratigraphySummaryTitle: "Current build-up",
        availableHeightForConnectorAndSupport: "Available height for connector and support",
        connectorThickness: "Connector thickness",
        remainingHeightForSupport: "Remaining height for the support",
        billOfQuantitiesProduct: "Product",
        billOfQuantitiesRequiredQuantity: "Required quantity",
        billOfQuantitiesQuantityToOrder: "Quantity to order",
        calculationDetails: "Calculation details",
        deckArea: "Deck area",
        totalBoards: "Boards",
        joists: "Joists",
        joistsLegend: "Substructure joists",
        layoutCalculation: "Layout calculation",
        longJoists: "Long joists",
        shortJoists: "Short joists",
        longJoist: "Long joist",
        shortJoist: "Short joist",
        joistsSpacing: "Joist spacing",
        supportCalculation: "Support calculation",
        maxSupportSpacing: "Max support spacing",
        supportsPerLongJoist: "Supports per long joist",
        supportsPerShortJoist: "Supports per short joist",
        supportSpacing: "Support spacing",
        supportsCount: "No. of supports",
        lShaped: "L-shaped",
        horizontal: "Horizontal",
        vertical: "Vertical",
        rectangular: "Rectangular",
        boardDirection: "Board direction",
        fasteningType: "Fastening type",
        hiddenFastening: "Hidden",
        hiddenFasteningDescription: "Boards are fastened without visible screws, using hidden connectors or clips.",
        visibleFastening: "Visible",
        visibleFasteningDescription: "Boards are fastened with screws visible on the deck surface.",
        groovingType: "Grooving type",
        smoothGrooving: "Smooth",
        groovedGrooving: "Grooved",
        maxBase: "Max width (L1)",
        maxHeight: "Max length (L2)",
        substructureSection: (unit: string) => `Element section bs x hs [${unit}]`,
        secondaryBase: "Secondary width (l1)",
        secondaryHeight: "Secondary length (l2)",
        totalHeight: "Maximum terrace extrados height (H)",
        slope: "Slope",
        slopeDirectionTitle: "Terrain slope",
        slopeDirectionHint:
          "Drag over the drawing to trace the direction in which the terrain descends. Without a direction the terrain stays flat.",
        slopeIncompleteWarning:
          "Both the slope and its direction are needed: the changes to the slope will not be saved. Close anyway?",
        totalHeightTooLittle: (height: string) =>
          `The selected deck build-up requires a total height of at least ${height}.`,
        secondaryItemDoesNotFitTotalHeight: (code: string) =>
          `Enabling ${code} would make the selected connector family incompatible with the total height (H).`,
        totalHeightIncompatible: (minHeight: string, maxHeight: string) =>
          `The total height for this deck build-up must be between ${minHeight} and ${maxHeight}.`,
        planeInteractionHint: "You can draw and edit the terrace directly from here.",
        planeInteractionStart: "Start drawing",
        // the button of the tutorial reopened from its icon
        planeInteractionContinue: "Continue drawing",
        showPlaneInteractionHint: "Show tutorial",
        planeInteractionFeatureDraw: "Click on the plane to trace the terrace's perimeter.",
        planeInteractionFeatureLength:
          "After selecting a point, type the length of the side (Backspace to correct it) and press Enter to confirm it.",
        planeInteractionFeatureDrag: "Drag the terrace's sides to change their dimensions.",
        planeInteractionFeatureEditLabel: "Click a label to enter the dimension you want.",
        planeInteractionFeatureDifferentBoards:
          "Drag the boards with a different width from one side to the other, if present.",
        planeInteractionFeatureClear: "Use the trash icon to clear the points and start the drawing over.",
        liveLoad: "Live load",
        deflection: "Deflection (EN1995-1-1 §7 / EN1999-1-1 §4)",
        gap: "Compatible gap width",
        gapIncompatible: (minimumGap: string, maximumGap: string) =>
          `The gap must be between ${minimumGap} and ${maximumGap}.`,
        joistsSpacingIncompatible: (minimumSpacing: number, maximumSpacing: number, unit: string) =>
          `Joist spacing must be between ${minimumSpacing} ${unit} and ${maximumSpacing} ${unit}.`,
        material: "Material",
        connectorMaterial: "Connector material",
        timberMaterial: "Timber",
        wpcMaterial: "WPC",
        boardWidth: "Board width",
        differentBoardWidthDragHint: "You can drag the boards from one side of the terrace to the other.",
        differentBoard: "Different-width board",
        differentBoardsColumn: "Boards with a different width",
        longDifferentBoardWidth: "Long board",
        shortDifferentBoardWidth: "Short board",
        boardThickness: "Board thickness",
        boardThicknessMustNotExceedBoardWidth: "Board thickness must not exceed board width.",
        boardThicknessExceedsConnectorFamilyMaxThickness: (maxValue: string) =>
          `The board thickness exceeds the maximum allowed by the selected connector family (${maxValue}).`,
        boardThicknessBelowAsymmetricalGroovingMinimum: (minValue: string) =>
          `The board thickness is too small for the selected asymmetrical grooving (minimum ${minValue}).`,
        boardThicknessBelowGroovingMinimum: (minValue: string) =>
          `The board thickness must be at least ${minValue} for the selected grooving type.`,
        boardThicknessOutOfSymmetricalGroovingRange: (minValue: string, maxValue: string) =>
          minValue === maxValue
            ? `The board thickness must be ${minValue} for the currently selected configuration.`
            : `The board thickness must be between ${minValue} and ${maxValue} for symmetrical grooving with the selected connector family.`,
        groovingHeight: "Grooving height",
        groovingHeightOutOfConnectorFamilyRange: (minValue: string, maxValue: string) =>
          `The grooving height must be between ${minValue} and ${maxValue} for the selected connector family.`,
        groovingHeightBelowConnectorMinimum: (minValue: number, unit: string) =>
          `The grooving height must be at least ${minValue} ${unit} for the selected connector.`,
        boardWidthMustNotExceedDeckDimension: (maxValue: string) =>
          `Board width must not exceed ${maxValue} for the selected board direction.`,
        sideNarrowerThanBoardWidth: "This side cannot be narrower than the selected board width.",
        boardFitWarning:
          "The selected board width and gap do not fit the deck dimensions exactly. To optimize, choose one of the following 3 options:",
        boardFitApplied: "Change applied. If you're not happy with it, press “Undo” to restore the previous values.",
        appliedFitParameter: "Parameter",
        appliedFitPrevious: "Previous",
        appliedFitCurrent: "Current",
        setBoardWidth: "Set board width",
        setBoardWidthTo: "Set board width to:",
        setDeckDimensions: "Set deck dimensions",
        setDeckDimensionsTo: "Set deck dimensions to:",
        setDifferentBoardWidth: "Set different board widths",
        setDifferentBoardWidthTo: "Set different board widths to:",
        topView: "Top view",
        sideView: "Side view",
        bottomView: "Bottom view",
        slopeView: "Slope",
        slopeViewSide: (from: number, to: number) => `Side ${from}–${to}`,
        slopeViewLegendDeck: "Terrace",
        slopeViewLegendTerrain: "Terrain",
        slopeViewExaggeration: "Slope exaggeration (view only)",
        slopeViewShowArrow: "Show slope arrow",
        unitSystemMetric: "Metric",
        unitSystemImperial: "Imperial",
        unitSystemChangeTitle: "Change unit of measure",
        unitSystemChangeWarning:
          "Changing the unit of measure keeps shape and dimensions; all other fields will be reset. Do you want to continue?",
        slopeViewExaggerationHelp:
          "Steepens the slope in the 3D view so it's easier to see. It's only a visual effect: the values and the configuration don't change.",
        slopeViewClickHint: "Click a label to see h, t and H at that point.",
        slopeViewNotToScale: "Schematic drawing, not to scale.",
        slopeViewUnreachable: "Not reachable",
        slopeViewLegendTotalHeight: "H — maximum terrace extrados height",
        slopeViewLegendSupportHeight: "h — height available for the support",
        slopeViewLegendTerrainHeight: "t — terrain height relative to the lowest vertex, where H is measured",
        supportCompatibleHRange: "Compatible H",
        supportMinHeight: "Min support height",
        supportMaxHeight: "Max support height",
        supportSlopeUnchanged: "The slope matches the one entered.",
        supportSlopeReduced: "The slope will be reduced compared to the one entered.",
        supportSlopeIncreased: "The slope will be increased compared to the one entered.",
        supportTotalHeightUnchanged: "H matches the one entered.",
        supportTotalHeightReduced: "H will be reduced compared to the one entered.",
        supportTotalHeightIncreased: "H will be increased compared to the one entered.",
        supportFlatTerrainOnly: "Only with both slopes at 0°",
        supportSuggestedSlope: "Slope",
        supportWidth: "Support width",
        suggestedValueUnchanged: "The value matches the one entered.",
        suggestedValueReduced: "The value will be reduced compared to the one entered.",
        suggestedValueIncreased: "The value will be increased compared to the one entered.",
        supportSpacingSuggestionsInfo:
          "For each support, the closest configuration that spaces the supports at least 1.5 times their width apart. The element section and the joist spacing change first; deflection and live load only when those aren't enough.",
        noSupportSpacingSuggestion: "No configuration spaces the supports at least 1.5 times their width apart.",
        supportApplySuggestion: "Set values",
        noSupportCompatibleWithSlope:
          "No support is compatible with the entered slope: at least one would exist without it. Try reducing it, increasing H, or changing the deck dimensions.",
        noSupportCompatibleGeneric:
          "No support is compatible with the current configuration. Try changing the total height H, the selected support, or the other deck elements (board, connector, secondary item).",
        supportScrew: "Support screw",
        connectorSubstructureScrew: "Connector substructure screw",
        connectorBoardScrew: "Connector board screw",
        regularLongBoards: "Regular long boards",
        regularShortBoards: "Regular short boards",
        differentLongBoards: "Long boards with different width",
        differentShortBoards: "Short boards with different width",
      },
    },
    connectorFamilies: {
      singular: "Connector Family",
      plural: "Connector Families",
      isScrew: "Is screw",
      requiresBoardScrew: "Requires board screw",
      maxBoardThickness: "Max board thickness",
      description: "Description",
      errors: {
        nameAlreadyPresent: "A connector family with the same name already exists.",
      },
    },
    connectorMappings: {
      singular: "Connector Mapping",
      plural: "Connector Mappings",
      minimumGap: "Minimum gap",
      maximumGap: "Maximum gap",
    },
    connectors: {
      singular: "Connector",
      plural: "Connectors",
      minGroovingHeight: "Minimum grooving height",
      minBoardWidth: "Minimum board width",
      errors: {
        inUseByConfiguration: "The connector is used in a saved calculation and cannot be deleted.",
        itemAlreadyPresent: "A connector with the same item already exists.",
        screwAlreadyPresent: "A connector with the same screw already exists.",
      },
    },
    fasteningTypes: {
      singular: "Fastening Type",
      plural: "Fastening Types",
      errors: {
        nameAlreadyPresent: "A fastening type with the same name already exists.",
      },
    },
    groovingTypes: {
      singular: "Grooving Type",
      plural: "Grooving Types",
      errors: {
        nameAlreadyPresent: "A grooving type with the same name already exists.",
      },
    },
    connectorMaterials: {
      singular: "Connector Material",
      plural: "Connector Materials",
      errors: {
        inUseByConnector: "The material is used by a connector: change or delete the connector first.",
        nameAlreadyPresent: "A connector material with the same name already exists.",
      },
    },
    secondaryItems: {
      singular: "Secondary item",
      plural: "Secondary items",
      use: "Use secondary item",
      canUse: "Can use secondary item",
      errors: {
        inUseByConfiguration: "The secondary item is used in a saved calculation and cannot be deleted.",
        inUseByConnectorMapping: "The secondary item is used by a connector mapping: delete the mapping first.",
      },
    },
    screwFamilies: {
      singular: "Screw Family",
      plural: "Screw Families",
      errors: {
        inUseByScrew: "The screw family is used by a screw: delete its screws first.",
        inUseByConnectorFamily: "The screw family is assigned to a connector family: remove it there first.",
        inUseBySupportFamily: "The screw family is assigned to a support family: remove it there first.",
        nameAlreadyPresent: "A screw family with the same name already exists.",
      },
    },
    screws: {
      singular: "Screw",
      plural: "Screws",
      errors: {
        inUseByConfiguration: "The screw is used in a saved calculation and cannot be deleted.",
        inUseByConnector: "The screw is used as a connector: delete that connector first.",
        itemAlreadyPresent: "A screw with the same item already exists.",
      },
    },
    substructures: {
      singular: "Substructure",
      plural: "Substructures",
      base: "Base",
      secondMomentOfArea: "Second moment of area",
      countries: "Countries",
      countryOptions: {
        eu: "Europe",
        us: "United States",
        all: "All",
      },
      errors: {
        inUseByConfiguration: "The substructure is used in a saved calculation and cannot be deleted.",
        itemAlreadyPresent: "A substructure with the same item already exists.",
        baseHeightAlreadyPresent: "A substructure with the same base and height already exists.",
      },
    },
    substructureFamilies: {
      singular: "Substructure Family",
      plural: "Substructure Families",
      errors: {
        nameAlreadyPresent: "A substructure family with the same name already exists.",
      },
    },
    substructureMaterialClasses: {
      singular: "Substructure Material Class",
      plural: "Substructure Material Classes",
      modulusOfElasticity: "Modulus of elasticity",
      errors: {
        inUseByConfiguration: "The material class is used in a saved calculation and cannot be deleted.",
        inUseBySupport: "The material class is assigned to a support: remove it there first.",
        nameAlreadyPresent: "A substructure material class with the same name already exists.",
      },
    },
    substructureMaterials: {
      singular: "Substructure Material",
      plural: "Substructure Materials",
      errors: {
        nameAlreadyPresent: "A substructure material with the same name already exists.",
      },
    },
    supportFamilies: {
      singular: "Support Family",
      plural: "Support Families",
      maxLiveLoad: "Max live load",
      errors: {
        nameAlreadyPresent: "A support family with the same name already exists.",
      },
    },
    supports: {
      singular: "Support",
      plural: "Supports",
      errors: {
        inUseByConfiguration: "The support is used in a saved calculation and cannot be deleted.",
        itemAlreadyPresent: "A support with the same item already exists.",
      },
    },
    substuructures: {
      singular: "Substructure",
      plural: "Substructures",
    },
    substuructureMaterials: {
      singular: "Substructure Material",
      plural: "Substructure Materials",
    },
    substuructureFamilies: {
      singular: "Substructure Family",
      plural: "Substructure Families",
    },
  },

  lockCalculatorModule: {
    preview3d: "3D preview",
    preDrillingLegend: {
      header: "Header pre-drilling",
      joist: "Joist pre-drilling",
      yes: "yes",
      no: "no",
    },
    tabs: {
      codeNorm: "Norm",
      structure: "Structure",
      connectors: "Connectors",
      loading: "Loading",
      designInput: "Design Input",
      configuration: "Configuration",
      loads: "Loads",
      results: "Results",
      calculationComparison: "Calculation comparison",
      bom: "Bill of Materials",
      report: "PDF Report",
    },
    env: {
      title: "Environmental & Code Settings",
      standard: "Standard",
      serviceClass: "Service Class",
      loadDurationClass: "Load Duration Class",
      continuousCtrl: "Continuous Production Control",
      continuousCtrlLabel: "Production subject to continuous control (COV < 15%)",
      coefficients: "Design coefficients",
      kmod: "Modification factor (load duration and service class)",
      gammaM: "Partial safety factor for the connection (γM)",
      gammaMAluminium: "Partial safety factor for aluminium (γM)",
      customCoefficients: "Custom",
      customCoefficientsHint: "Edit kmod and γM for this calculation only, instead of the normative values",
      normativeValue: "Normative value",
      restoreNormative: "Restore",
    },
    structural: {
      title: "Structural Configuration",
      configType: "Lateral Resistance Configuration",
      connectionPosition: "Connector position",
      upliftResistanceConfig: "Uplift Resistance Configuration",
      inclinedFastener: "Inclined fastener",
      headerOrientation: "Header Orientation",
      lateralResistance: {
        columnNotch: "Column notch",
        headerBeamNotch: "Header beam notch",
        joistBeamNotch: "Joist beam notch",
        inclinedFasteners: "Inclined fasteners",
        lockstop: "LOCK STOP",
        exposed: "Exposed",
      },
      headerOrientationOptions: {
        beam: "Beam",
        column: "Column",
      },
      generateAllConfigurations: "Generate all configurations",
      goToManualConfiguration: "Go to manual/semi-manual configuration",
    },
    connection: {
      title: "Connection Configuration",
      standard: "Standard",
      serviceClass: "Service Class",
      loadDurationClass: "Load Duration Class",
      connector: "Connector",
      finish: "Finish",
      connectorOne: "Connector 1",
      connectorTwo: "Connector 2",
      productType: "Product Type",
    },
    fasteners: {
      headerFastener: "Header Fastener",
      joistFastener: "Joist Fastener",
      upliftFastener: "Uplift Fastener",
      lockstop: "Lockstop",
    },
    customMaterial: {
      create: "Create custom material",
      edit: "Edit custom material",
      remove: "Delete material",
      badge: "custom",
      name: "Name",
      baseGrade: "Based on",
      keepCurrentBase: "Keep the current base",
      baseGradeHint: "Category, normative and structural family are inherited from the base material",
      inherited: "Inherited from the base",
      family: "Structural family",
      category: "Category",
      normative: "Normative",
      properties: "Properties",
      advanced: "Advanced",
      rhoK: "Characteristic density ρk",
      rhoM: "Mean density ρm",
      fc0K: "Compression parallel to grain fc,0,k",
      fc90K: "Compression perpendicular to grain fc,90,k",
      fvK: "Shear fv,k",
      frK: "Rolling shear fr,k",
      faxK: "Axial withdrawal fax,k",
      usedInCalculations: (n: number) => `Used in ${n} calculations: they will update at their next recompute.`,
      deleteConfirm: "Do you really want to delete this custom material?",
      deleteBlocked: "Cannot be deleted: the material is used in other calculations.",
    },
    timber: {
      sectionTitle: "Timber Configuration",
      title: "Timber Elements",
      header: "Header",
      joist: "Joist",
      headerMaterialGrade: "Header Material Grade",
      headerWidth: "Header Width (b)",
      headerHeight: "Header Height (h)",
      joistMaterialGrade: "Joist Material Grade",
      joistWidth: "Joist Width (b)",
      joistHeight: "Joist Height (h)",
      joistAngle: "Joist Angle α",
      joistAngleBeta: "Joist Angle β",
      connectorTopDistance: "Connector distance from joist top",
      joistTopOffset: "Joist top offset from header top",
      joistTopAlignment: "Joist top position",
      joistTopAligned: "Aligned with the column top",
      joistTopOffsetOption: "Offset from the column top",
    },
    connector: {
      sectionTitle: "Connector & Fasteners",
      setupTitle: "Connection Setup",
      selectionTitle: "Connector Selection",
      fastenersTitle: "Fasteners",
      connectorPairCount: "Number of Connector Pairs",
      noCompatibleConnector: "No LOCK-T connector compatible with the entered cross-sections.",
      noCompatibleSecondConnector: "No second connector compatible with the one selected",
      generateFromData: "Generate configurations from entered data",
      generateManual: "Generate manual solution",
    },
    loads: {
      sectionTitle: "Design Actions (Ed)",
      title: "Loads (Ed)",
      fvD: "Shear Force (Fv,d)",
      faxD: "Axial Force (Fax,d)",
      flatD: "Lateral Force (Flat,d)",
      fupD: "Uplift Force (Fup,d)",
      alphaDeg: "Angle α",
    },
    combined: {
      title: "Combined Load Verification",
      description: "Select which load directions to include in the combined verification:",
      includeFv: "Fv,d (Vertical Shear)",
      includeFup: "Fup,d (Uplift)",
      includeFax: "Fax,d (Axial)",
      includeFlat: "Flat,d (Lateral)",
    },
    configuration: {
      title: "Configuration",
      configType: "Configuration Type",
      connectionCount: "Connection Count",
    },
    errors: {
      fillRequiredFields: "Please fill in all required fields.",
    },
    selectPlaceholder: "Select...",
    helpTexts: {
      // Code & Norm
      standard:
        "Select the reference design standard (e.g. EC5 – Eurocode 5, NTC). Determines safety factors and formulas used in verification.",
      serviceClass:
        "Defines the environmental operating conditions: Class 1 (dry, wood moisture ≤12%), Class 2 (humid, moisture ≤20%), Class 3 (exposed to weather). Affects the kmod coefficient.",
      loadDurationClass:
        "Indicates the duration of the applied load: Permanent (e.g. self-weight), Long-term (e.g. storage loads), Medium-term (e.g. snow), Short-term (e.g. wind), Instantaneous (e.g. impact). Affects kmod.",
      continuousCtrl:
        "Enable if production is subject to continuous quality control with coefficient of variation below 15%. When active, reduces the material partial safety factor γM.",
      // Structure
      lateralResistanceConfig:
        "Choose the connection configuration for lateral shear resistance calculation. Determines the applicable Johansen formula for load-bearing capacity.",
      upliftResistanceConfig:
        "Choose whether to use the inclined fastener for uplift resistance calculation. Shares the same fastener selected in the Connectors step for the lateral configuration.",
      headerOrientation:
        "Orientation of the header structural member: horizontal (beam) or vertical (column). Determines the static behaviour and applicable connection checks.",
      headerMaterialGrade:
        "Select the wood strength class for the header member (e.g. GL24h, C24, LVL). Determines the mechanical properties used in calculations.",
      headerWidth:
        "Cross-section width of the header member, in mm. Dimension perpendicular to the connector axis. When the header is column-oriented, the connector's own width must fit within this measurement minus a clearance (17.5mm for Mini, 24mm for Midi).",
      headerHeight:
        "Cross-section height of the header member, in mm. Dimension parallel to the connector axis. When the header is beam-oriented, it caps the connector module's maximum assembly_height (≤ header height − 15mm).",
      joistMaterialGrade:
        "Wood strength class for the joist member. It may differ from the header member. Density (rho_k) above 420 kg/m³ requires pre-drilling, which reduces the maximum connector width allowed (see joist width).",
      joistWidth:
        "Cross-section width of the joist, in mm. Dimension perpendicular to the connector axis. The connector's own width must fit within this measurement — exactly, if the wood requires pre-drilling, or with an extra allowance of 8mm (Mini) / 6mm (Midi) if it doesn't.",
      joistHeight:
        "Cross-section height of the joist, in mm. Dimension parallel to the connector axis. Caps the connector module's maximum assembly_height (≤ joist height), with no allowance.",
      angleAlpha: "",
      joistTopAlignment:
        "Column header. Aligned: the joist top is flush with the column top and c_min is checked on the connector's distance from the joist top. Offset: the joist top sits below the column top (offset field) and c_min is checked on the offset plus the connector's distance.",
      joistTopOffset:
        "How far the joist top sits below the column top. If empty, the c_min of the selected lock is used.",
      connectorTopDistance:
        "Vertical distance from the joist top edge to the connector assembly (the header moves with it). If left empty: with the joist aligned to the column the c_min of the selected lock is used; with the joist offset it is 0 (connector flush with the joist top). For LOCKT135 without pre-drilling the minimum is 5 mm. With a column header c_min must be reached: by this distance alone if the joist is aligned, by its sum with the offset if it is offset.",
      angleBeta:
        "Joist shear angle in the X-Z plane, independent of the α angle. Determines the maximum allowed joist fastener length: up to 20° the limit is 48.35mm (Mini) / 65.7mm (Midi); beyond 20° no fastener is compatible (limit 0).",
      // Connectors
      connectorPairCount:
        "Number of connectors to install on the same connection. Choose 2 for paired connectors (higher capacity). Selecting 2 shows a second connector selection menu.",
      headerLock:
        "A wider lock on the header side halves the connectors in the bill of materials and updates the header screws. Choosing or changing it may remove the lock stop from the configuration: the result then has to be recalculated. The wider lock and the lock stop exclude each other: choosing one removes the other.",
      connectionCount:
        "Total number of LOCK connections required for the project. Determines total quantities of products and fasteners to order.",
      connectorOneLock:
        "Select the desired LOCK concealed connector model. Each model has a different load-bearing capacity. Consult the technical catalogue for specifications.",
      connectorOneFinish:
        "Enable C4 EVO surface treatment for advanced corrosion protection in outdoor or aggressive environments (corrosion class C4).",
      connectorTwoLock:
        "Connector 2 LOCK model, used when two connector pairs are selected. It may use a different item from connector 1.",
      connectorTwoFinish: "Surface finish of connector 2. Same options as connector 1: ALUMINIUM or EVO (C4).",
      headerFastener: "Select the structural fastener used on the header side of each connector pair.",
      joistFastener: "Select the structural fastener used on the joist side of each connector pair.",
      upliftFastener:
        "Fasteners inserted at an angle to increase uplift and/or lateral shear resistance. Specify fastener type and quantity.",
      lockstop:
        "Select the LOCK STOP connector model (anti-lift safety device). Prevents accidental beam lifting in extreme conditions.",
      lockstopWidth:
        "Width adjustment for the LOCK STOP element, in mm. Allows adapting the device to the connection geometry.",
      // Loads
      fvD: "Vertical shear force acting on the connection, in kN. This is the main load stressing the connector in the vertical direction (parallel to gravity).",
      fupD: "Uplift force, in kN. Acts opposite to gravity, tending to separate the connected elements. Typical in wind or seismic conditions.",
      faxD: "Axial force acting along the joist beam axis, in kN. Tension (+) or compression (−). Usually negligible for simply supported beams.",
      flatD:
        "Lateral shear force (out-of-plane), in kN. Acts perpendicular to the main connection plane. Typical in seismic zones or out-of-plane actions.",
      combined:
        "Enable combined stress verification (multiple actions acting simultaneously). The verification formula is: (Fax,Ed / Rax,d)² + (Fv,Ed / Rv,d)² ≤ 1. Select which load directions to include in the combination.",
    },
    notices: {
      title: "Notices",
      goToField: "Go to field",
      goToAction: "Go to button",
      count: (n: number) => (n === 1 ? "1 notice" : `${n} notices`),
      severity: { error: "Error", warning: "Warning", info: "Info" },
      axes: { vertical: "vertical (Fv)", uplift: "uplift (Fup)", axial: "axial (Fax)", lateral: "lateral (Flat)" },
      members: { header: "header", joist: "joist" },
      reasons: {
        columnWidth: "the column width",
        topDistance: "the connector top distance",
        joistWidth: "the joist width",
        assemblyHeight: "the section heights",
        evoRequired: "the EVO requirement of service class 3",
      },
      coefficients: { kmod: "kmod", gammaM: "γM (connection)", gammaMAluminium: "γM (aluminium)" },
      slots: { header: "header fastener", joist: "joist fastener", inclined: "inclined fastener" },
      inputs: {
        standardId: "standard",
        serviceClassId: "service class",
        loadDurationClassId: "load duration class",
        "connectors[0].lockItemId": "first connector",
        "connectors[1].lockItemId": "second connector",
        headerFastenerItemId: "header fastener",
        joistFastenerItemId: "joist fastener",
        lockstopItemId: "lock stop",
        headerMaterialGradeId: "header material",
        joistMaterialGradeId: "joist material",
        headerOrientation: "header orientation",
        alphaDeg: "angle α",
        headerH: "header height",
        headerB: "header width",
        joistB: "joist width",
        joistH: "joist height",
        anyLoad: "at least one load",
        inclinedFastenerItemId: "inclined fastener",
        lateralResistanceConfig: "lateral resistance",
        kmod: "kmod",
        gammaM: "γM",
      },
      messages: {
        topDistanceBelowCMin: (p: NoticeParams) =>
          `Connector distance ${p.distance} mm + joist offset ${p.offset} mm is below the lock's minimum c_min of ${p.cMin} mm: the configuration is not verified. Increase the distance or the offset.`,
        topDistanceBelowLock135Min: (p: NoticeParams) =>
          `LOCKT135 on a joist that is not pre-drilled needs at least ${p.min} mm from the top (now ${p.distance} mm): the configuration is not verified.`,
        serviceClassNotAllowed: (p: NoticeParams) =>
          `Service class ${p.classNumber} is not allowed for the header material (allowed: ${p.allowed}): the configuration is not verified.`,
        axisInputsMissing: (p: NoticeParams) =>
          `The ${p.axis} axis has a load but cannot be calculated: missing ${p.missing}.`,
        noCompatibleLock: (p: NoticeParams) =>
          `No lock fits this structure: ${p.rejected} of ${p.total} were excluded by ${p.reason}.`,
        edgeDistanceUnavailable: (p: NoticeParams) =>
          `Minimum edge distances (§11) are not available for the ${p.member}: its density (${p.rhoK} kg/m³) is above the EC5 limit of 500.`,
        customCoefficientsEnabled: () =>
          "You can now edit kmod and γM for this calculation only: until you change a value, the normative one applies.",
        coefficientOverrideDiffers: (p: NoticeParams) =>
          `${p.coefficient} is ${p.value}; the normative value is ${p.normative}.`,
        topDistanceEqualsDefault: () => "Using the default value: the c_min of the selected lock.",
        resultStale: (p: NoticeParams) =>
          `Inputs changed since the last calculation: the final configuration's result may be out of date. Press "${p.button}" to recalculate the result with the current data.`,
        reportStale: (p: NoticeParams) =>
          `The inputs changed after the last report: the PDF shown is out of date. Press "${p.generateReport}" in the "${p.reportTab}" tab.`,
        draftsStale: (p: NoticeParams) =>
          `Inputs changed since the configurations were generated: press "${p.button}" to update the list.`,
        noDraftWithinLimits: (p: NoticeParams) =>
          `Locks fit the structure, but no generated configuration stays within the loads. Check the loads or the materials, then press "${p.button}" again.`,
        generationInputsMissing: (p: NoticeParams) =>
          `"${p.generateAll}" and "${p.generateFromData}" are not available: missing ${p.missing}.`,
        manualGenerationInputsMissing: (p: NoticeParams) =>
          `"${p.generateManual}" is not available: missing ${p.missing}.`,
        noCompatibleFastener: () =>
          `Locks fit the structure, but no fastener is compatible with them: check the header and joist heights, the angle and the materials.`,
        noCompatibleLockstop: () =>
          `Locks fit the structure, but no lock stop is compatible with them: choose another lateral resistance.`,
        combinedNeedsTwoAxes: (p: NoticeParams) =>
          `The combined verification needs at least two axes: only the ${p.axis} axis is selected. Select another one.`,
        lateralConfigCorrected: (p: NoticeParams) =>
          `Lateral resistance changed from "${p.fromConfig}" to "${p.toConfig}": the first is not available with the "${p.orientation}" header orientation.`,
        continuousControlCleared: () => "Continuous production control was cleared: it does not apply to Eurocode 5.",
        secondConnectorCleared: () => "The second connector was removed: a single connector pair is selected.",
        lockstopClearedByHeaderLock: () =>
          "The lock stop and the lateral resistance type were removed because a wider header lock was chosen: choose a new lateral resistance type and recalculate the result.",
        headerLockClearedByLockstop: () =>
          "The wider header lock was removed because a lock stop was chosen: recalculate the result.",
        lockstopCleared: () => "The lock stop was removed: lateral resistance is no longer set to lock stop.",
        combinedAxisDisabled: (p: NoticeParams) =>
          `The ${p.axis} axis was removed from the combined verification: it has no load.`,
        upliftConfigSetByLateral: () =>
          "Uplift resistance was set to inclined fasteners, as chosen for lateral resistance.",
        upliftConfigSetByLoad: () =>
          "Uplift resistance was set to inclined fasteners because an uplift load is entered.",
        inclinedFastenerCleared: () =>
          "The inclined fastener was removed: neither lateral nor uplift resistance uses inclined fasteners.",
        inclinedFastenerNotAllowed: () =>
          "The inclined fastener was removed: the selected lock takes no inclined fasteners.",
        fastenerCleared: (p: NoticeParams) =>
          `The ${p.slot} was removed: it is not compatible with the current selection.`,
        finishForced: () => "The finish was changed to aluminium: the selected lock has no EVO version.",
        finishSetByServiceClass: () => "The finish was set to EVO: service class 3 requires it.",
        secondLockCleared: () => "The second lock was removed: it no longer fits with the first one.",
        pairCountReduced: () => "A single connector pair was kept: no second lock fits with the first one.",
        topDistanceFollowedDefault: (p: NoticeParams) =>
          `The connector distance followed the new default of the selected lock: ${p.before} mm → ${p.after} mm.`,
        topOffsetFollowedDefault: (p: NoticeParams) =>
          `The joist offset followed the new default of the selected lock: ${p.before} mm → ${p.after} mm.`,
        connectorsInvalidated: () => "The chosen locks were cleared: they were picked for a different top distance.",
      },
    },
    wizard: {
      stepOf: (current: number, total: number) => `Step ${current} of ${total}`,
      completeFieldsToContinue: "Fill in all the fields on this page to continue",
    },
    finish: {
      aluminium: "Aluminium",
      evo: "EVO",
      aluminiumShort: "ALU",
      evoShort: "EVO",
    },
    continuousCtrl: {
      active: "Active",
      inactive: "Inactive",
      standard: "Standard",
      cov: "COV",
      std: "STD",
    },
    results: {
      title: "Verification Results",
      verified: "Verified",
      utilizationRatio: "Utilization Ratio",
      notYetCalculated: "Fill in the form to calculate verification.",
      notVerified: "Not verified",
      verifiedOk: "Verification passed",
      axes: {
        vertical: "Vertical (Fv)",
        uplift: "Uplift (Fup)",
        axial: "Axial (Fax)",
        lateral: "Lateral (Flat)",
      },
      load: "Load",
      resistance: "Resistance",
      eta: "η",
      failed: "FAILED",
      noLoad: "NO LOAD",
      formulaAltSingle: "η = Ed / Rd ≤ 1",
      formulaAltCombined: "Σ (Ed/Rd)² ≤ 1",
      finalConfiguration: "Final configuration",
      removeComparison: "Remove from comparison",
      slipModulus: "Slip modulus",
    },
    generatedConfigurations: {
      title: "Generated configurations",
      empty: "No configuration has been generated yet — fill in Code & Norm, Loads and Structure.",
      lock: "Lock",
      fastener: "Fastener",
      combined: "Combined",
      chosen: "Chosen",
      compare: "Compare",
      downloadData: "Download data",
      load: "Load",
      selectAll: "Select all configurations",
      selectRow: "Select this configuration",
      search: "Search configurations",
    },
    bom: {
      title: "Bill of Materials",
      noComponents: "No components configured yet.",
      component: "Component",
      code: "Code",
      quantity: "Qty",
      headerLock: "Wider header lock",
    },
    report: {
      title: "PDF Report",
      empty: "Choose a configuration to generate the calculation report.",
      error: "The report could not be generated.",
      retry: "Retry",
      generate: "Generate PDF",
      download: "Download PDF",
      fullscreen: "Full screen",
      exitFullscreen: "Exit full screen",
      savingChanges: "Saving your changes: the report is built from the saved data.",
      notGenerated: 'Click "Generate PDF" to generate the calculation report.',
    },
    helpModal: {
      illustrationAlt: "Help illustration",
    },
    aria: {
      calculationSteps: "Calculation steps",
      calculationResults: "Calculation Results",
    },
    admin: {
      tabs: {
        reference: "Reference",
        catalog: "Catalog",
        coefficients: "Coefficients",
        connectors: "Lock",
      },
      catalog: {
        fasteners: "Fasteners",
        materialGrades: "Material Grades",
      },
      coefficients: {
        kmodValues: "Kmod Values",
        gammaMValues: "γM Values",
      },
      reference: {
        standards: "Standards",
        serviceClasses: "Service Classes",
        loadDurationClasses: "Load Duration Classes",
      },
      fasteners: {
        singular: "Fastener",
        diameter: "Ø (mm)",
        length: "Length (mm)",
        threadLength: "Thread Length (mm)",
        myRk: "My,Rk (kNm)",
        ftensK: "Ftens,K (kN)",
        headType: "Head Type",
      },
      materialGrades: {
        singular: "Material Grade",
        rhoK: "ρk (kg/m³)",
        rhoM: "ρm (kg/m³)",
      },
      standards: {
        singular: "Standard",
        jurisdictionCode: "Region",
        normativeType: "Standard type",
        normativeTypes: {
          EUROCODE: "Eurocode",
          NATIONAL_CODE: "National code",
          HARMONIZED_STANDARD: "Harmonized standard",
          ETA: "ETA",
          DOP: "DoP",
        },
      },
      serviceClasses: {
        singular: "Service Class",
        classNumber: "Class #",
        nameIt: "Name IT",
        nameDe: "Name DE",
        nameFr: "Name FR",
        nameEs: "Name ES",
        description: "Description",
        typicalExamples: "Typical Examples",
        order: "Order",
      },
      loadDurationClasses: {
        singular: "Load Duration Class",
      },
      kmodValues: {
        singular: "Kmod Value",
        serviceClass: "Service Class",
        loadDurationClass: "Load Duration Class",
        kmod: "kmod",
      },
      gammaMValues: {
        singular: "γM Value",
        standard: "Standard",
        continuousCtrl: "Continuous Ctrl",
        notApplicable: "Not applicable (EC5)",
        gammaM: "γM",
      },
      connectors: {
        singular: "Lock Connector",
        family: "Family",
        nModules: "N. Modules",
        nInclinedUplift: "Inclined Fasteners (Uplift)",
        nInclinedLateral: "Inclined Fasteners (Lateral)",
        width: "Width (mm)",
        height: "Height (mm)",
        thickness: "Thickness (mm)",
        screwEdgeThickness: "Screw edge thickness (mm)",
        evoAvailable: "EVO Available",
        lockModule: "Lock Module",
        edgeDistance: "Edge Distance (mm)",
        assemblyHeight: "Assembly Height (mm)",
      },
      lockModules: {
        singular: "Lock Module",
        assemblyHeight: "Assembly height (mm)",
        sideHeight: "Side height (mm)",
        thickness: "Thickness (mm)",
        moduleWidth: "Module Width (mm)",
        joistFastenersPerModule: "Joist fasteners / module",
        headFastenersPerModule: "Head fasteners / module",
        kLockFactor: "kLock",
        f1AluRk: "F1,alu,Rk (kN)",
        f2AluRkPerModule: "F2,alu,Rk / module (kN)",
        kh2PerModule: "kh2 / module",
        contactAreaALock: "Contact Area ALock (mm²)",
        cMin: "c_min (mm)",
        coefficients: "Lateral/Vertical Coefficients",
      },
      lockStops: {
        singular: "Lock Stop",
        fyK: "fy,k (MPa)",
        area: "Area (mm²)",
        fLockstopRk: "FLockstop,Rk (kN)",
        isSingle: "Single",
      },
      connectorAccessories: {
        singular: "Connector Accessory",
        quantity: "Qty",
        notes: "Notes",
        accessorySku: "Accessory SKU",
        connector: "Connector",
      },
      fastenerUsages: {
        singular: "Fastener usages",
        fastener: "Fastener",
        usage: "Usage",
        usageStandard: "Standard (header / joist)",
        usageInclined: "Inclined",
        scope: "Select by",
        scopeFamilies: "Whole families",
        scopeFasteners: "Single fasteners",
        families: "Families",
        fasteners: "Fasteners",
        exceptions: "Exceptions (not to add)",
        resultAlreadyPresent: "Already added before",
        resultAdded: "Newly added",
        resultNothingToDo: "No elements to add or delete",
        resultRemoved: "Existed and were deleted because they are exceptions",
      },
      lockLockstopMappings: {
        singular: "Lock → Lockstop",
        lock: "Lock",
        lockstop: "Lockstop",
        quantity: "Quantity",
        installationLevels: "Installation levels",
      },
      lockPairLockstopMappings: {
        singular: "Pair → Lockstop",
        lockA: "Lock A",
        lockB: "Lock B",
        lockstop: "Lockstop",
        quantity: "Total quantity",
        installationLevels: "Installation levels",
      },
      model3d: {
        singular: "3D Model",
        quantity: "Quantity",
      },
      emptyValue: "--",
    },
  },
  beamReinforcementModule: {
    tabs: {
      generalData: "General data & loads",
      beams: "Beams",
      screw: "Screw & connection",
    },
    screwFamilies: {
      singular: "Screw family",
      plural: "Screw families",
      errors: {
        nameAlreadyPresent: "A screw family with the same name already exists.",
      },
    },
    screws: {
      singular: "Screw",
      plural: "Screws",
      errors: {
        nameAlreadyPresent: "A screw with the same name already exists.",
      },
      fields: {
        family: "Family",
        headDiameter: "Head diameter",
        threadedLength: "Threaded length",
        yieldMoment: "Yield moment",
        tensileCapacity: "Tensile capacity",
      },
    },
    timbers: {
      singular: "Timber class",
      plural: "Timber classes",
      errors: {
        nameAlreadyPresent: "A timber class with the same name already exists.",
      },
      fields: {
        density: "Density rho",
        bendingStrength: "Bending strength fmk",
        shearStrength: "Shear strength fvk",
        modulusOfElasticity: "Elastic modulus E0,mean",
        isGlulam: "Glulam",
      },
    },
    drawing: {
      view2D: "2D view",
      view3D: "3D view",
      enable3DHint: "Enter both beam lengths to enable the 3D view.",
    },
    calculation: {
      serviceClass: "Service class",
      serviceClass1: "1 — u ≤ 10%",
      serviceClass2: "2 — 10% < u ≤ 16%",
      serviceClass3: "3 — u ≥ 16%",
      loadDuration: "Load duration",
      loadDurationPerm: "Permanent",
      loadDurationLong: "Long duration",
      loadDurationMedium: "Medium duration",
      loadDurationShort: "Short duration",
      loadDurationInst: "Instantaneous",
      tipTolerance: "Tip tolerance",
      permanentLoad: "Permanent load",
      liveLoad: "Live load",
      beamLength: "Beam length",
      existingBeamTitle: "Existing beam",
      reinforcementBeamTitle: "Reinforcement beam",
      strengthClass: "Strength class",
      base: "Base",
      height: "Height",
      lengthOptionalHint: "Optional. If both beam lengths are defined, the preview can switch between 2D and 3D.",
      existingLength: "Existing beam length",
      reinforcementLength: "Reinforcement length",
      reinforcementSides: "Reinforcement sides",
      reinforcementSidesOne: "1 — reinforcement on one side",
      reinforcementSidesTwo: "2 — reinforcement on both sides",
      screw: "Screw",
      screwSpacing: "Screw spacing",
      preDrilled: "Pre-drilled pilot hole",
    },
    results: {
      summaryTitle: "Verification summary",
      bendingUtilization: "Bending utilization",
      shearUtilization: "Shear utilization",
      connectionUtilization: "Connection utilization",
      screwAdmissible: "Screw admissible",
      screwNotAdmissible: "Screw NOT admissible",
      reasonGeometry: "geometry (l_ef / length / t₂)",
      reasonHeight: "h₂ < a₄,t + a₄,c",
      reasonSpacing: "spacing < minimum a₁",
      reasonOverload: "η > 100%",
      incompleteWarning: "Fill in all fields to compute the verification.",
      warningInsufficientPenetration:
        "Insufficient thread penetration into the existing beam (l_ef = 0). Choose a longer screw.",
      warningScrewTooLong: (max: number) =>
        `The screw is longer than the available width b₁+b₂−10 mm = ${max} mm: the tip exits the existing beam.`,
      warningHeightInsufficient: (height: number, min: number) =>
        `Insufficient height: h₂ = ${height} mm < a₄,t + a₄,c = ${min} mm. Increase h₂ or reduce the screw diameter.`,
      warningSpacingInsufficient: (spacing: number, min: number) =>
        `The adopted spacing (${spacing} mm) is below the minimum a₁ = ${min} mm.`,
      warningConnectionOverload: (eta: number) =>
        `Connection not verified: η = ${eta}% > 100%. Reduce the spacing or increase the screw size.`,
    },
    details: {
      title: "Calculation details",
      loadsSection: "Coefficients, loads and penetration depth",
      kmod: "k_mod",
      selfWeight: "Self weight g₁",
      designLoad: "Design load q_d",
      spacingSection: "Minimum and effective spacing (EC5 §8.3)",
      minimum: "minimum",
      effective: "effective",
      outcome: "outcome",
      stiffnessSection: "Stiffness distribution",
      bendingSection: "Bending check",
      shearSection: "Shear check",
      axialSection: "Screw axial resistance",
      johansenSection: "Connection resistance (Johansen)",
      connectionSection: "Connection verification",
      existingBeamLabel: "Existing beam (1)",
      reinforcementBeamLabel: "Reinforcement beam (2)",
      designMoment: "Design moment M_d",
      designShear: "Design shear V_d",
      bendingStress: "Bending stress σ_m,d",
      bendingStrength: "Bending strength f_m,d",
      shearStress: "Shear stress τ_d",
      shearStrength: "Shear strength f_v,d",
      screwCount: "Screws along the span",
      designShearForce: "Design shear per screw V_Ed",
      connectionResistance: "Connection resistance F_v,Rd",
      yes: "Yes",
      no: "No",
    },
  },

  screwConnectorModule: {
    tabs: {
      screwFamilies: "Screw families",
      screwDiameters: "Screw diameters",
      screws: "Screws",
      washers: "Washers",
      timberMaterials: "Timber materials",
      normativeTables: "Normative tables",
    },
    screwFamilies: {
      singular: "Screw family",
      plural: "Screw families",
      errors: {
        nameAlreadyPresent: "A screw family with the same name already exists.",
      },
      fields: {
        description: "Description",
        threadType: "Thread type",
        material: "Material",
        coating: "Coating",
        ccCode: "CC code",
        tcCode: "TC code",
        scEuCode: "SC (EU) code",
        ecUsCode: "ESR / EC (US) code",
        wdUsCode: "WD (US) code",
        standardReference: "Standard reference",
        connectionTypes: "Connection types",
        substrates: "Substrates",
      },
    },
    threadTypes: {
      partial: "Partial thread",
      full: "Full thread",
    },
    headTypes: {
      countersunk: "Countersunk head",
      cylindrical: "Cylindrical head",
      hexagonal: "Hexagonal head",
      round_plate: "Round head (plate fastening)",
      pan_plate: "Pan head (plate fastening)",
      reinforced_pan_plate: "Reinforced pan head (plate fastening)",
      hexagonal_pan_plate: "Hexagonal pan head (plate fastening)",
    },
    tipTypes: {
      three_thorns: "3 THORNS",
      self_drilling: "Self-drilling tip",
      standard: "Standard tip",
      hardwood: "Hardwood tip",
    },
    connectionTypes: {
      shear_screws: "Shear screws",
      steel_to_timber: "Steel-to-timber",
      timber_to_timber: "Timber-to-timber",
    },
    substrateCategories: {
      solidwood_softwood: "Solid wood - softwood",
      glulam_softwood: "Glulam - softwood",
      clt_softwood: "CLT - softwood",
      lvl_softwood: "LVL - softwood",
      solidwood_hardwood: "Solid wood - hardwood",
      lvl_hardwood: "LVL - hardwood",
      panels: "Panels",
      steel: "Steel",
    },
    compatibilityLevels: {
      notCompatible: "Not compatible",
      restricted: "Restricted",
      compatible: "Compatible",
    },
    washers: {
      singular: "Washer",
      plural: "Washers",
      fields: {
        code: "Code",
        innerDiameter: "Inner diameter",
        outerDiameter: "Outer diameter",
        height: "Height",
      },
    },
    screwDiameters: {
      singular: "Screw diameter",
      plural: "Screw diameters",
      filterByFamily: "Filter by screw family",
      selectFamilyHint: "Select a screw family to manage its diameters.",
      fields: {
        screwFamily: "Screw family",
        diameter: "Diameter",
        coreDiameter: "Core diameter",
        threadOuterDiameter: "Thread outer diameter",
        headDiameter: "Head diameter",
        headType: "Head type",
        tipType: "Tip type",
        bit: "Bit",
        yieldMoment: "Yield moment",
        tensileCapacity: "Tensile capacity",
        washers: "Compatible washers",
        resistanceValues: "Resistance values",
      },
    },
    resistanceValues: {
      fields: {
        resistanceParam: "Parameter",
        substrateCategory: "Substrate category",
        substrateSpecies: "Substrate species",
        preDrilled: "Pre-drilled",
        valueNMm2: "Value",
        referenceDensity: "Reference density",
        densityRange: "Density range",
      },
      params: {
        fax_k: "f_ax,k",
        fhead_k: "f_head,k",
      },
      addRow: "Add resistance value",
    },
    screws: {
      singular: "Screw",
      plural: "Screws",
      filterByDiameter: "Filter by screw diameter",
      selectDiameterHint: "Select a screw diameter to manage its screws.",
      fields: {
        screwDiameter: "Screw diameter",
        length: "Length",
        partialThreadLength: "Partial thread length",
        fullThreadExtraLength: "Full thread extra length",
        tipLength: "Tip length",
      },
    },
    timberMaterials: {
      singular: "Timber material",
      plural: "Timber materials",
      fields: {
        materialCategory: "Material category",
        species: "Species",
        class: "Class",
        characteristicDensity: "Characteristic density ρk",
        meanDensity: "Mean density ρmean",
        compressionStrength0: "Compression strength f_c,0,k",
        compressionStrength90: "Compression strength f_c,90,k",
        rollingShearStrength: "Rolling shear strength f_r,k",
        shearStrength: "Shear strength f_v,k",
        standardReference: "Standard reference",
      },
    },
    normativeTables: {
      title: "Normative tables",
      loadDurationClasses: {
        singular: "Load duration class",
        plural: "Load duration classes",
        fields: {
          code: "Code",
          name: "Name",
          order: "Order",
        },
      },
      serviceClasses: {
        singular: "Service class",
        plural: "Service classes",
        fields: {
          code: "Code",
          name: "Name",
        },
      },
      kmod: {
        title: "k_mod values",
        hint: "k_mod modification factor by service class and load duration class.",
      },
      gammaM: {
        title: "Partial safety factors γM",
        fields: {
          standard: "Standard",
          columnA: "Column A",
          columnB: "Column B",
        },
      },
    },
    errors: {
      familyAlreadyExists: "A screw family with this name already exists.",
      familyMissingName: "The screw family name is required.",
      familyNotFound: "The screw family was not found.",
      gammaMValueNotFound: "The γM value was not found.",
      kmodValueNotFound: "The kmod value was not found.",
      loadDurationClassAlreadyExists: "A load duration class with this code already exists.",
      loadDurationClassMissingCode: "The load duration class code is required.",
      loadDurationClassNotFound: "The load duration class was not found.",
      screwAlreadyExists: "The item is already a screw.",
      screwDiameterNotFound: "The screw diameter was not found.",
      screwNotFound: "The screw was not found.",
      serviceClassAlreadyExists: "A service class with this code already exists.",
      serviceClassNotFound: "The service class was not found.",
      timberMaterialAlreadyExists: "A timber material with this category and class already exists.",
      timberMaterialNotFound: "The timber material was not found.",
      washerAlreadyExists: "A washer with this code already exists.",
      washerMissingCode: "The washer code is required.",
      washerNotFound: "The washer was not found.",
    },
    units: {
      unitMm2: "mm²",
      unitNMm2: "N/mm²",
      unitKgM3: "kg/m³",
    },
    calculation: {
      shell: {
        navLabel: "Module sections",
        openMenu: "Open menu",
        closeMenu: "Close menu",
        inputsGroup: "Project data",
        resultsGroup: "Results",
        toolsGroup: "Tools",
        resultsView: "3D view and checks",
        resultsSolutions: "Compatible solutions",
        resultsPositioning: "Screw positioning",
        generateReport: "Generate PDF report",
        reportQuestion: "Which report version?",
        reportCompact: "Compact report",
        reportCompactHint: "without methodological notes",
        reportFull: "Full report",
        reportFullHint: "with methodological notes",
        exportDxf: "Export DXF (detail)",
        dotOk: "Complete",
        dotBad: "To be checked",
        dotPending: "Pending",
        tutorial: "Tutorial",
      },
      tutorial: {
        dialogLabel: "Tutorial",
        stepOf: (step: number, total: number) => `Step ${step} of ${total}`,
        done: "Done",
        redo: "Repeat",
        skip: "Skip the tutorial",
        back: "Back",
        next: "Next",
        finish: "Finish",
        confirmTitle: "Start the tutorial?",
        confirmText:
          "A short demonstration of the main features on an example: every step is carried out for you, continue with Next. The fields are cleared for the demonstration, which only uses example data and shows a verified solution; when it closes your data is restored exactly as it was. The calculation is not saved during the tutorial.",
        cancel: "Cancel",
        start: "Start the tutorial",
        steps: {
          intro: {
            title: "How to fill in the calculation",
            text: "Work from top to bottom: at each step you will see the mouse move over the fields of the panel and enter an example. At the start θ, b1, d1, t1, b2, d2, t2, Fn, Fv and Fa are empty: until you fill them in, the calculation is not run.",
            note: "The fields have been cleared: when the tutorial closes, your data are restored.",
          },
          connectionType: {
            title: "2 · Connection type and angle",
            text: "Choose the type of joint by clicking one of the eight icons, each with its name below (timber-timber, timber-timber / single shear on two sides (3 members), timber-timber / double shear (3 members), steel-timber with the steel plate as element 1 or steel-timber / single shear on two sides, with two steel plates and the timber in the middle, panel-timber / single shear, with a wood-based panel as element 1, panel-timber / single shear on two sides, with two panels and the timber in the middle, or panel-timber / double shear, with the panel in the middle and the timber at the sides) and enter the angle θ between the two elements (from 1° to 180°).",
          },
          element1: {
            title: "3 · Element 1 data",
            text: "(In the 3-member case it is the central element.) Choose material, species and class (with panel-timber only wood-based panels: plywood, OSB/3-OSB/4, particleboard; for CLT panels also layers and insertion side: element 1 only on the wide side). Enter width b1, length d1 (or tick «Continuous element» if there is no end) and thickness t1, in mm.",
          },
          element2: {
            title: "4 · Element 2 data",
            text: "Same data for element 2 (in the 3-member case they also apply to element 3): material, b2, d2 and thickness t2 in mm. For CLT panels you can choose the wide or narrow side (narrow: a single row of screws on the axis).",
          },
          threeMembers: {
            title: "5 · 3-member connection",
            text: "With «timber-timber / single shear on two sides» or «timber-timber / double shear» element 1 becomes the central one (loaded, rotated by θ); elements 2 and 3 are the two side members, fixed and identical: the data of element 2 also apply to element 3. In the first case the screws are driven from both sides (head on 2 and on 3, tip in the central member) and the resistance is doubled (two single-shear connections, one per side); the tips from the two sides may overlap or, with the option to stagger the screws of the two sides by 1d, side 3 is staggered. In the second case a single screw passes through the three elements (head on element 2, tip in element 3): double shear EC5 eq. 8.7 and a single screw grid. In both cases the splitting check of element 1 appears. With «steel-timber / single shear on two sides» the two steel plates (elements 1 and 3) are at the sides of the central timber (element 2): screws from both sides as in the first case, splitting on the timber. CLT (always on the wide side) is allowed in one position only in timber-timber on two sides (CLT-timber-CLT or timber-CLT-timber, never three CLT) and cannot be selected in double shear.",
            note: "For the demonstration the connection type is set for a moment and then put back to the one you chose.",
          },
          panelTimber: {
            title: "6 · Panel-timber connection",
            text: "With «panel-timber / single shear» element 1 is a wood-based panel (plywood, OSB/3-OSB/4, particleboard) on the screw head side, loaded and rotated by θ; element 2 is the timber, fixed. Only HBS and TBS screws with head ≥ 2·d are used. The embedment of the panel follows EC5 §8.3.1.3 (the ETA does not give it), the minimum distances Table 8.2 with a1 and a2 ×0.85 on both elements, and kmod is the square root of the product of the panel's and the timber's kmod. With «panel-timber / single shear on two sides» the panels (elements 1 and 3, identical) are at the sides of the central timber (element 2): screws from both sides, doubled resistance, splitting on the timber, as for steel-timber on two sides. With «panel-timber / double shear» the panel is the central element 1 and the timber is at the sides (elements 2 and 3, identical): a single screw passes through the three elements, as in timber-timber double shear (eq. 8.7), without the splitting check. The calculation stops, giving the reason, if the panel thickness is below the minimum (6 mm plywood, 8 mm particleboard and OSB, in any case ≥ 1.2·d), if the head is smaller than 2·d or if the service class is not allowed for that panel. In double shear CLT cannot be selected for the side members; in the 3D view the panels are drawn in a darker wood than solid or glued laminated timber.",
            note: "For the demonstration the connection type is set for a moment and then put back to the one you chose.",
          },
          loads: {
            title: "7 · Design loads",
            text: "Enter Fn (force along the axis of element 1), Fv (shear, perpendicular) and Fa (withdrawal, positive when it pulls the screw out) in kN. Choose the service class and the load duration: γM is filled in automatically according to the standard.",
          },
          connection: {
            title: "8 · Connection choice",
            text: "Choose family, hole type (pre-drilled or not), diameter and length of the screw: the menus only offer screws compatible with thicknesses and materials. If the ETA requires pre-drilling (ρk above 550 kg/m³, 500 for stainless steel, except for the HARDWOOD, LBS and KKT families) the field is locked on «Pre-drilled» and a note explains why. With «Use washer» you can lengthen the screw. If «no compatible screw» appears, change the thicknesses or search for a solution.",
          },
          grid: {
            title: "9 · Screw grid",
            text: "Set rows and columns of screws: they are limited to the maximum allowed by the minimum distances. With «Spread the screws over the admissible area» (on by default, with the automatic minimum distances) the chosen rows and columns move to the limits of the admissible area instead of staying close together: the actual spacings appear below the s1 and s2 fields. You can also stagger the screws, move the centre of the grid or compute the maximum number of screws.",
          },
          view: {
            title: "10 · 3D view and checks",
            text: "Check the 3D view and the checks (shear and withdrawal): the outcome is «Admissible» or «Not admissible». The calculation values and the formulas are in the result cards.",
          },
          solutions: {
            title: "11 · Compatible solutions",
            text: "Not sure which screw to use? With «Find compatible solutions» the program proposes the screws and the minimum number that satisfy all the checks. The «Pre-drilled» and «Washer» options narrow the search; «Use this» applies the solution.",
          },
          positioning: {
            title: "12 · Screw positioning",
            text: "Here you see where the screws fall with respect to the edges of each element: the drawings give the dimensions from the edge to the screw axis and the table lists the distances of every screw. Now the card opens and scrolls down.",
            note: "The card goes back to how it was (open or closed) when you move to the next step.",
          },
          reportPreview: {
            title: "13 · Report preview",
            text: "With «Show report» you see the PDF report you download, in your language: now you see it open and scroll down, page after page. With the bar at the top you can change the zoom, switch to single or two pages and preview it in black and white.",
            note: "If data are missing, the example values are entered for the demonstration (removed when the tutorial closes).",
          },
          reportPdf: {
            title: "14 · PDF report",
            text: "From Tools › Generate PDF report choose the compact or full version; the same command is in the title bar and in the Calculation report card. The calculation data are saved automatically: there is no need to save them by hand.",
          },
          dxf: {
            title: "15 · Export DXF",
            text: "From Tools › Export DXF (detail), or from the «Export DXF» button of the Calculation report card, you download the construction detail for CAD: front view of the elements, views with the dimensions from the edges, screws with their real diameter, title block and table of positions (also for the 3-member connection: the staggered screws of side 3 are on a separate layer; the title block also gives the actual spacings when the screws are spread). The drawing is at full scale 1:1 and the command is active only when the connection is verified.",
            note: "In the tutorial the file is not downloaded: the cursor only shows where the command is.",
          },
        },
      },
      status: {
        pending: "Waiting for data",
        verified: "Verified",
        notVerified: "Not verified",
      },
      sections: {
        tipologia: "Connection type and angle",
        element1: "Element 1 data",
        element1Panel: "Element 1 data (panel)",
        element1Central: "Element 1 data (central)",
        element1PanelCentral: "Element 1 data (central panel)",
        element13Steel: "Elements 1 and 3 data (steel plates)",
        element13Panel: "Elements 1 and 3 data (panels)",
        element2: "Element 2 data",
        element2Central: "Element 2 data (central timber)",
        element23: "Elements 2 and 3 data (side members)",
        loads: "Design loads",
        connection: "Connection choice",
        grid: "Screw grid",
      },
      engine: {
        a4Reduce: {
          notThin: (t: string, fiveD: string) => `— not applied: thickness t=${t} mm is not less than 5d=${fiveD} mm.`,
          spacing: (twentyFiveD: string, values: string) =>
            `— not applied: it requires a1, a3,t and a3,c ≥ 25d=${twentyFiveD} mm (current: ${values}).`,
        },
        block: {
          noScrew:
            "no screw is compatible with the entered thicknesses and materials: change the thicknesses t1/t2 or the materials, or choose another family/diameter.",
          length: (L: number, rule: string, values: string, washerH: number) =>
            `the screw length (L=${L} mm) is not compatible with the thicknesses: it requires ${rule} (${values}${washerH > 0 ? `, washer h=${washerH} mm` : ""}).`,
          partialThread: (rule: string, aRule: { rule: string; values: string } | null, values: string) =>
            `partial-thread screw not compatible with the thicknesses (compatibility rule): it requires ${rule}${aRule ? ` and ${aRule.rule} (here ${aRule.values})` : ""} (here ${values}).`,
          penetrationRule: (screw: string) =>
            `screw not compatible with the thicknesses (compatibility rule on length/thread/penetration for ${screw}).`,
          minSize: (screw: string) =>
            `${screw} not compatible with the minimum thickness/width of the members (compatibility rule): increase the thicknesses or choose a smaller diameter.`,
          emptyFields: (tutorial: string) => `fill in the fields that are still empty. See ${tutorial} for guided input.`,
          cltNarrowPenetration: (lt1: string, pen: string, min: string, neededL: string) =>
            `penetration into the CLT panel (narrow side) L−${lt1} = ${pen} mm is less than the minimum 10·d = ${min} mm required by ETA-11/0030: the calculation method does not apply. Increase the screw length (L ≥ ${lt1}+10d = ${neededL} mm) or choose a screw of smaller diameter.`,
          penetration: (ptN: number, lt1: string, pen: string, min: string, neededL: string) =>
            `penetration into element ${ptN} L−${lt1} = ${pen} mm is not greater than the minimum 4·d = ${min} mm: the calculation method does not apply. Increase the screw length (L > ${lt1}+4d = ${neededL} mm) or choose a screw of smaller diameter.`,
        },
        cltWideOnly: {
          panelSides:
            "Panel-to-timber connection on two sides: the screws enter from the two wide faces of the central timber: only the wide side is allowed.",
          steelSides:
            "Steel-to-timber connection on two sides: the screws enter from the two wide faces of the central timber: only the wide side is allowed.",
          three:
            "Three-member connection: the screws pass through elements 2 and 3 from the wide face: only the wide side is allowed.",
        },
        epsNote: {
          element1:
            '— angle between the screw axis and the grain of element 1, used ONLY for f<sub>h,1,k</sub> (embedment): different from the force/grain angle used for the minimum distances a1/a2, which stays unchanged. Uncheck "automatic" to set it manually.',
          element2: (elementRef: string) =>
            `— angle between the screw axis and the grain of <span id="lbEps2n">${elementRef}</span>, used ONLY for f<sub>h,k</sub> (embedment): different from the force/grain angle used for the minimum distances a1/a2 (φ<sub>2</sub> above, linked to θ/Fn/Fv), which stays unchanged. Uncheck "automatic" to set it manually in both cases.`,
          cltWide:
            'CLT panel, wide side (ETA-11/0030): ε = angle between the force and the grain of the outer layer, used in f<sub>h,k</sub> (embedment as for solid timber with 110% of the minimum layer density). Automatic = calculated from θ, F<sub>n</sub>/F<sub>v</sub> and the outer layer orientation; uncheck "automatic" to set it manually.',
        },
        layout: {
          spacingBelowMin: (spacing: string, min: string) =>
            `⚠ ${spacing} mm is less than the required minimum (${min} mm).`,
          lengthBelowEndDistance: (name: string, current: number, required: string) =>
            `⚠ increase ${name} (current ${current} mm): it must be ≥ ${required} mm — the member length cannot be less than the required end distance.`,
          cltHalfThickness: (d2: string, required: string, minThickness: string) =>
            `⚠ d2 = b2/2 = ${d2} mm is less than ${required} mm: increase the panel thickness (a total thickness ≥ ${minThickness} mm is required) or reduce the screw diameter.`,
          cornerCapped: (rows: number, cols: number, max: string) =>
            `— rows and/or columns reduced to ${rows}×${cols} to keep the distance between the screws at the ends of the grid within 2·b1 = ${max} mm`,
        },
        maxScrews: {
          none: "<strong>No admissible layout</strong> — not even one screw simultaneously respects the minimum distances of both members.",
          max: (rows: number, cols: number, total: number) =>
            `<strong>Theoretical maximum: ${rows} rows × ${cols} columns = ${total} screws</strong>`,
          spacing: (s1: string, s2: string) =>
            ` (spacing s<sub>1</sub>=${s1} mm along the axis of element 1, s<sub>2</sub>=${s2} mm along the axis of element 2 — diagonal grid, code minimum for both axes)`,
          offset: (x: string, y: string) =>
            `— grid shifted from the centre (centre offset: horizontal=${x} mm, vertical=${y} mm) to fit entirely: a REGULAR grid (same uniform spacing, no stagger) centred on the intersection of the element1/element2 axes would contain fewer, because the admissible envelope is not symmetric about that intersection.`,
          cornerCapped: (rows: number, cols: number, max: string) =>
            `— grazing angle (θ≤20° or θ≥160°): rows and/or columns reduced to ${rows}×${cols} to keep the distance between the screws at the ends of the grid within 2·b<sub>1</sub> = ${max} mm. Uncheck the corresponding box to consider all geometrically admissible rows/columns.`,
          capped:
            "— search limited to a practical cap of 500×500: at the minimum spacing, in this geometry (element 2 with infinite upper side and/or element 1 with infinite F side), the number of admissible screws could be even higher.",
        },
        panel: {
          notAdmitted: (classe: string) =>
            `the panel «${classe}» is not admitted by ETA-11/0030 (admitted: plywood EN 636, OSB/3-OSB/4, particleboard EN 312): choose another material for element 1.`,
          noEmbedment: (d: string) =>
            `panel f_h,k cannot be determined for d = ${d} mm (EC5 §8.5.1.2: for d > 8 mm only plywood, OSB and particleboard).`,
          noKmod: (classe: string, serviceClass: string) =>
            `panel k_mod for «${classe}» is not defined in EC5 Table 3.1 for service class ${serviceClass}: the panel is not suitable for this service class.`,
          tooThin: (t1: string, minT: number, tMin: string) =>
            `panel thickness t1 = ${t1} mm is less than the minimum max(${minT} mm; 1,2·d) = ${tMin} mm (ETA-11/0030): the calculation method does not apply.`,
          smallHead: (dk: string, twoD: string) =>
            `head diameter dk = ${dk} mm is less than 2·d = ${twoD} mm: the EC5 §8.3.1.3 formulas for panel f_h,k require a head ≥ 2·d.`,
          onlyHbsTbs: "only HBS and TBS screws are allowed in the panel-to-timber module.",
        },
        predrill: {
          reason: (rho: string, limit: number, element: number, hard: boolean, stainless: boolean) =>
            `ρk = ${rho} kg/m³ > ${limit} kg/m³ (element ${element}${hard ? ", hardwood" : ""}, ${stainless ? "stainless steel" : "carbon steel"} screws)`,
          forced: (reasons: string) => `— Pre-drilling required (ETA-11/0030 p. 8 and 14): ${reasons}.`,
        },
        resistance: {
          ropeCap: (rp: string) =>
            ` — F<sub>ax</sub>/4 limited to 100% of the Johansen part (${rp} N, EC5 §8.2.2(2))`,
          mode: (mode: string) => `mode (${mode})`,
          steelPlate: {
            thin: "thin plate",
            thick: "thick plate",
            interp: "intermediate-thickness plate",
          },
        },
        results: {
          spreadNote: (s1: string, s2: string, s1Min: string, s2Min: string) =>
            `Screws distributed over the admissible area: s<sub>1</sub> = ${s1} mm, s<sub>2</sub> = ${s2} mm (required minimums ${s1Min} / ${s2Min} mm); the outer screws are at the minimum edge distances.`,
          staggered: (lEff: string, limit: string, d: string, dir: string, element: number) =>
            `ℹ screws on the two sides staggered: L−h = ${lEff} mm exceeds ${limit} mm, so the screws of element 3 are shifted by 1·d = ${d} mm (direction ${dir}) relative to those of element ${element}, to avoid the tips overlapping in the central member.`,
          tipsOverlap: (steel3: boolean, lEff: string, limit: string, tMinus: string, fourD: string, gridTitle: string) =>
            `ℹ tips of the two sides overlapping in the central ${steel3 ? "timber" : "member"}: L−h = ${lEff} mm exceeds ${limit} mm. This is allowed (EC5 §8.3.1.1(7)) because (t − t2) = ${tMinus} mm > 4d = ${fourD} mm; to stagger the screws of the two sides by 1·d, tick the box in the ${gridTitle} panel.`,
          washerNet: (h: string, lEff: string) => ` — net of the washer (h=${h} mm): L−h=${lEff} mm`,
          screwTooLong: (l: string, rule: string, margin: string, twoSides: boolean) =>
            `⚠ screw too long (L=${l}): it must be L < ${rule} (at least ${margin} mm from the tip to the opposite face${twoSides ? " of the central member, EC5: (t − t2) > 4d" : ""}).`,
          screwTooShortClt: (l: string, penetration: string, tenD: string, need: string) =>
            `⚠ screw too short (L=${l}): penetration into the CLT panel (narrow side) ${penetration}, minimum 10·d = ${tenD} mm (ETA-11/0030) — ${need} is required.`,
          screwTooShort: (l: string, rule: string, element: number) =>
            `⚠ screw too short (L=${l}): it must be ${rule} (minimum penetration of 4d into element ${element}).`,
          cltInfo: (thickness: string, rhok: string, classe: string) =>
            `Total panel thickness: ${thickness} mm · ρk = ${rhok} kg/m³ (class ${classe})`,
          cltNarrowThin: (threshold: string) =>
            ` — ⚠ less than 10·d = ${threshold} mm required by ETA-11/0030 Tab. B1 for narrow-side insertion.`,
        },
        screwLists: {
          noFamily: (el1: string, el2: string) =>
            `⚠ no compatible family (connection type/service class) has a screw compatible with the current thicknesses (${el1}mm, ${el2}mm): reduce the thickness.`,
          noDiameter: (el1: string, el2: string) =>
            `⚠ no diameter of this family has a length compatible with the current thicknesses (${el1}mm, ${el2}mm): reduce the thickness or choose another family.`,
          noLength: (lower: string, upper: string, washerH: number, el1: string, el2: string) =>
            `⚠ no length of this family/diameter falls between ${lower} and ${upper} mm${washerH > 0 ? ` (with washer h=${washerH} mm)` : ""} (current thicknesses ${el1}mm, ${el2}mm): reduce the thickness or choose a different diameter.`,
        },
        threeNotes: {
          twoSides:
            "Element 1 is central (loaded, rotated by θ). Elements 2 and 3 are side members, fixed and identical: the data of element 2 also apply to element 3. Screws inserted from the two outer sides (head on element 2 and on element 3, tip in element 1): same screw and mirrored grid; the load is split in half between the two connections (one per side, each in single shear with a single shear plane).",
          double:
            "Element 1 is central (loaded, rotated by θ). Elements 2 and 3 are side members, fixed and identical: the data of element 2 also apply to element 3. A single screw passes through the three elements: head on element 2, through element 1, tip in element 3 (double shear, EC5 eq. 8.7): one screw grid only, no stagger.",
          steelSides:
            "Element 2 is central timber and fixed. Steel plates on the sides: element 1 and its identical copy (element 3), loaded and rotated by θ; the data of element 1 also apply to element 3. Screws inserted from the two outer sides (head on the plates, tip in the timber): same screw and mirrored grid; the load is split in half between the two connections (one per side, each in single shear with a single shear plane).",
          panelDouble:
            "Element 1 = wood-based panel, central (loaded, rotated by θ). Elements 2 and 3 are timber, side members, fixed and identical: the data of element 2 also apply to element 3. A single screw (HBS and TBS only, head ≥ 2·d) passes through the three elements: head on element 2, through the panel, tip in element 3 (double shear, EC5 eq. 8.7): one screw grid only, no stagger. Panel embedment from EC5 §8.3.1.3 / §8.5.1.2; distances with a1, a2 ×0,85; kmod = √(kmod,panel·kmod,timber).",
          panelSides:
            "Element 2 is central timber and fixed. Wood-based panels on the sides: element 1 and its identical copy (element 3), loaded and rotated by θ; the data of element 1 also apply to element 3. HBS and TBS screws only with head ≥ 2·d. Screws inserted from the two outer sides (head on the panels, tip in the timber): same screw and mirrored grid; the load is split in half between the two connections (one per side, each in single shear with a single shear plane). Panel embedment from EC5 §8.3.1.3 / §8.5.1.2; distances with a1, a2 ×0,85; kmod = √(kmod,panel·kmod,timber).",
        },
        violations: {
          edge2: (code: string) => `element 2: edge (${code})`,
          end2: (code: string) => `element 2: lower end (${code})`,
          line1: (n: string, code: string) => `element 1: line ${n} (${code})`,
          side3: (text: string) => `element 3 side (staggered by 1d): ${text}`,
        },
      },
      elementRefs: {
        element1: "element 1",
        element1Central: "element 1, central",
        elements13Steel: "elements 1 and 3, steel plates",
        elements13Panel: "elements 1 and 3, panels",
        element2: "element 2",
        element2Central: "element 2, central timber",
        elements23: "elements 2 and 3",
      },
      typeSection: {
        theta: "θ — element 1 angle",
        thetaUnit: "0°→F down · 180°→F up · counter-clockwise",
        connectionType: "Connection type",
        types: {
          legno: "timber-timber / single shear",
          legno3: "timber-timber / single shear on two sides",
          legno3d: "timber-timber / double shear",
          acciaio: "steel-timber / single shear",
          acciaio3: "steel-timber / single shear on two sides",
          pannello: "panel-timber / single shear",
          pannello3: "panel-timber / single shear on two sides",
          pannello3d: "panel-timber / double shear",
        },
      },
      // Names of the engine's material type keys (and of the species fallbacks of panels/steel)
      materialTypes: {
        solidwoodSoftwood: "Solid softwood",
        glulam: "Glued laminated timber",
        clt: "CLT panels",
        lvl: "LVL (Laminated Veneer Lumber)",
        solidwoodHardwood: "Solid hardwood",
        panels: "Wood-based panels",
        steel: "Steel",
        panelsSpecies: "OSB / cement-bonded panels",
        steelSpecies: "Structural steel",
      },
      element: {
        materialType: "Material type",
        materialTypeUnit: "timber DB",
        species: "Wood species",
        class: "Class",
        customName: "Material name",
        customNameUnit: "custom",
        customNamePlaceholder: "e.g. Special timber XYZ",
        rhoHint:
          "ρ<sub>k</sub> and ρ<sub>mean</sub> are filled in from the database when the material is chosen and remain editable.",
        rhoMissing: "ρ<sub>k</sub> not available in the database, enter it manually",
        fiber: (n: number) => `φ${n === 1 ? "₁" : "₂"} — grain orientation`,
        fiberUnit1: "relative to the axis of element 1",
        fiberUnit2: "relative to the axis of element 2",
        fiberUnit23: "relative to the axis of elements 2 and 3",
        eps: (n: number) => `ε${n === 1 ? "₁" : "₂"} — screw axis/grain angle`,
        epsUnit1: "only for fₕ,ₖ of element 1",
        epsUnit2: "only for fₕ,ₖ of element 2",
        epsUnit23: "only for fₕ,ₖ of elements 2 and 3",
        epsAuto: (n: number) => `automatic ε<sub>${n}</sub>`,
        epsAutoUnit1: "always 90° (screw always ⟂ grain, independent of θ)",
        epsAutoUnit2: "90° for steel-timber (screw always ⟂ grain, independent of θ); θ-derived for timber-timber",
        continuous: "Continuous element",
        continuousUnit: "no real end: it continues beyond the joint on both sides",
        thickness: (n: number) => `thickness t${n}`,
        rhomeanUnit: "kg/m³ — for Kₛₑᵣ",
        rhomeanMissing: "Not available in the database for this class: enter it manually",
        douglas: "Douglas fir species",
        douglasUnit: "without pre-drilling: ETA-11/0030 requires a1 and a3 ×1.5",
        a4reduce: (n: number) => `Reduce a4,c,${n} (unloaded edge) to 3d`,
        a4reduceUnit1: "thin timber, requires a1 and a3,t,1 ≥ 25d",
        a4reduceUnit2: "thin timber, requires a1 and a3 ≥ 25d",
        a1e: (n: number) => `a1,element${n}`,
        a2e: (n: number) => `a2,element${n}`,
        a1Unit: "mm · min. spacing along the axis",
        a2Unit: "mm · min. spacing perpendicular",
        a3t1: "a3,t,1 (loaded end)",
        a3t2Unit: "mm · end, 0°≤θ≤90°",
        a3c2Unit: "mm · end, 90°<θ≤180°",
      },
      clt: {
        lato: "Insertion side",
        latoOptions: {
          narrow: "Narrow (narrow edge, screw parallel to the plane)",
          wide: "Wide (wide face, screw perpendicular to the plane)",
        },
        latoHint1: "Element 1 in a timber-timber connection: only the wide side is allowed.",
        latoHint2:
          "Narrow side: b<sub>2</sub> = total panel thickness and d<sub>2</sub> = b<sub>2</sub>/2 (automatic); t<sub>2</sub> (insertion depth) is editable.",
        numStrati: "Number of layers",
        disposizione: "Layer arrangement",
        disposizioni: {
          fisso: "Fixed (alternating 0°/90°)",
          alternati: "Alternating",
          accoppiati: "Paired (0° paired on the outer layers)",
          zeroDoppio: "All 0° layers doubled (11 layers only)",
          userDefined: "User-defined (per layer)",
        },
        spessoriMode: "Layer thickness",
        spessoriModes: {
          uniforme: "Uniform (same thickness for all layers)",
          differenziato: "Differentiated (0° / 90°)",
        },
        spessoreSingle: "Single layer thickness",
        spessore0: "Thickness of the 0° layers",
        spessore90: "Thickness of the 90° layers",
        classe: "Solid softwood class",
        userLayers: "Layers — thickness, direction, class",
        userLayersUnit: "from the outside inwards",
        layer: (i: number) => `Layer ${i}`,
        layerThickness: "Thickness (mm)",
        layerDirection: "Grain direction",
        layerClass: "Solid timber class",
      },
      loadsSection: {
        fn: "Fn, axial force",
        fnUnit: "kN · along the element 1 axis",
        fv: "Fv, shear force",
        fvUnit: "kN · perpendicular to the element 1 axis",
        fa: "Fa, withdrawal force",
        faUnit: "kN · along the screw axis, positive = withdrawal",
        resultantAngle: "resultant/grain angle",
        withdrawal: "withdrawal",
        notYetVerified: "not yet checked for pull-out",
        serviceClass: "Service class",
        loadDuration: "Load duration",
        gammaMRef: "γM reference",
        gammaMRefUnit: "connections/joints",
        standards: {
          EC5: "EC5 (Table 2.3 — connections)",
          NTC2018: "NTC2018 (Tab. 4.4.III — joints)",
        },
        column: "Column",
        columns: {
          A: "A — standard production",
          B: "B — with certified FPC control",
        },
        gammaMAuto: "automatic γ<sub>M</sub> (from the selected standard)",
      },
      connectionSection: {
        family: "Screw family",
        foro: "Hole type",
        predrilled: "Pre-drilled",
        nonPredrilled: "Without pre-drilling",
        diameter: "Outer thread diameter d",
        length: "Screw length L",
        lengthUnit: "mm · from the screw database",
        washer: "Use washer",
        washerUnit:
          "increases the effective head diameter (d<sub>h</sub>) in the head pull-through check (F<sub>ax,ε,Rk</sub>)",
        autoSpacing: "Automatic minimum distances (a1, a2, a3, a4 from the standard)",
      },
      notes: {
        panel:
          "Element 1 = wood-based panel (plywood, OSB/3-OSB/4, particle boards), on the screw-head side, loaded and rotated by θ. Element 2 = timber, fixed. Only HBS and TBS screws with head ≥ 2·d. Panel embedment from EC5 §8.3.1.3 (eq. 8.20–8.22) or §8.5.1.2 (eq. 8.36–8.37) for d &gt; 8 mm; panel minimum distances from EC5 Table 8.2 with α = 0 and no dependence on ρ<sub>k</sub>, and a<sub>1</sub>, a<sub>2</sub> ×0.85 on panel and timber (§8.3.1.3); k<sub>mod</sub> = √(k<sub>mod,panel</sub>·k<sub>mod,timber</sub>) (§2.3.2.2).",
        continuous: (n: 1 | 2): string =>
          n === 1
            ? "— d1 and a3,t,1 are no longer required (they lose meaning: there is no real end). The screw positioning limit becomes directly a virtual, internal and hidden end, equal to b2/(2·|cos(θ)|), measured from the intersection of the axes of the two elements."
            : "— d2 and a3,t,2/a3,c,2 are no longer required (they lose meaning: there is no real end). The screw positioning limit becomes directly a virtual, internal and hidden end, equal to b1/(2·|cos(θ)|), measured from the intersection of the axes of the two elements.",
        continuousDisabled:
          "— not available at θ=0°/180°: the axes of the two elements coincide (element 1 and element 2 would be aligned on the same line).",
        steel1Spacing:
          "— element 1 in steel plate: a plate has no grain of its own, so a1/a2 of element 1 are not a physical requirement and do not affect the grid spacing (EC5 §8.3.1.4): s1/s2 depend only on a1,element2/a2,element2 (already ×0.7).",
        steel1Dist:
          "— element 1 in steel plate: a4,t,1 = a4,c,1 = 1.5·1.1·d, a3,t,1 = 1.2·1.1·d (minimum hole distance in the plate, not timber Table 8.2)",
        a3t1Rule:
          "— rule: no screw may be closer than a3,t,1 to the real end of element 1 (line 3 in the drawing), in both timber and steel plate — screws in that zone are marked in red and do not count for resistance.",
        foro:
          '— "Without pre-drilling": the minimum-distance formula is derived automatically from the screw family selected above (KKT for KKT families, HBS formula for HBS families with d = 6…12 mm, EC5 Table 8.2 for all other screws and diameters, as per ETA-11/0030).',
        plateHw:
          "— HBS PLATE / LBS HARDWOOD EVO family for timber-to-timber connection: ETA-11/0030 requires a1 and a2 ×1.5 on both elements.",
        myk:
          "— My,k not present in the screw database for this family/diameter: some Johansen failure modes are not considered, the result may not be conservative. Check or complete the data in Screw.xlsx.",
        limitGrid:
          "— limit disabled: you can enter more screws than geometrically admissible; those outside the envelope remain marked in red and in the panel below, at your own risk",
      },
      gridSection: {
        rows: "rows (along the element 1 axis)",
        rowsUnit: "max from θ/screw",
        cols2: "columns (along the element 2 axis)",
        cols23: "columns (along the axis of elements 2 and 3)",
        cltRowNote: "Element 2 in CLT, narrow side: a single row of screws, on the axis of the element 2 thickness.",
        s1Unit: "mm · along the element 1 axis",
        s2Unit2: "mm · along the element 2 axis",
        s2Unit23: "mm · along the axis of elements 2 and 3",
        offsetX: "Center offset, horizontal",
        offsetY: "Center offset, vertical",
        spread: "Spread the screws over the admissible area (maximum spacing, only with automatic minimum distances)",
        stagger: "Stagger the screws in the row parallel to the grain by 1d (avoids the n<sub>ef</sub> reduction)",
        staggerTwoSides:
          "Stagger the screws of the two sides by 1d if the tips overlap (otherwise they stay overlapped, provided (t − t<sub>2</sub>) &gt; 4d)",
        maxScrews: "Compute maximum number of screws",
        applyMax: "Apply rows/columns",
        limitGrid: "Limit rows/columns to the maximum admissible number (from θ and screw diameter)",
        limitGrazing:
          "At a grazing angle (θ≤20° or θ≥160°) limit the distance between the screws at the ends of the grid to 2×b<sub>1</sub>",
      },
      view: {
        title: "3D view",
        showDims: "Show dimensions",
        branchMeta: (theta: string, branch: string) => `θ = ${theta}°  ·  branch ${branch}`,
        branchPending: "branch —",
        hintControls:
          "Drag = rotate · wheel = zoom · right button = pan · View menu = camera presets · Elements menu = show or hide components.",
        hintTail: "the geometric context; the screws are the product verified by the calculation. Screws outside the envelope are red.",
        hintSubjects: {
          two: "The two timber elements are",
          three: "The three timber elements are",
          panel: "The panel and the timber are",
          steel3: "The three elements (two steel plates and the timber) are",
          panel3: "The three elements (two panels and the timber) are",
        },
        legend: {
          el2: "element 2 (timber, fixed)",
          el2Central: "element 2 (central timber, fixed)",
          el23: "elements 2 and 3 (timber, fixed, identical)",
          el1: "element 1 (rotated by θ)",
          el1Panel: "element 1 (panel, rotated by θ)",
          el1Central: "element 1 (central, rotated by θ)",
          el1PanelCentral: "element 1 (central panel, rotated by θ)",
          el13Steel: "elements 1 and 3 (steel plates, rotated by θ)",
          el13Panel: "elements 1 and 3 (panels, rotated by θ)",
          screwOk: "admissible screw",
          screwBad: "non-admissible screw",
          dims: "dimensions (input values)",
          fn: "Fn (axial)",
          fv: "Fv (shear)",
          fa: "Fa (withdrawal, along the screw axis)",
        },
        toolbar: {
          views: {
            iso: "3D view",
            front: "Front view",
            top: "Top view",
            side: "Side view",
          },
          menuView: "View",
          menuElements: "Elements",
          resetView: "Reset view",
          elements: {
            element1: "Element 1",
            elements13: "Elements 1 and 3",
            element2: "Element 2",
            elements23: "Elements 2 and 3",
            screws: "Screws",
            forces: "Forces",
            grid: "Grid",
            solid: "Solid",
            ghost: "Semi-transparent",
            hidden: "Hidden",
          },
        },
        capture: {
          legibilityWarning: (lines: string) =>
            `Warning: some dimensions in the report views may not be legible (${lines}).`,
          legibilityLine: (view: string, clipped: number, overlapping: number) =>
            `${view}: ${clipped} dimensions clipped, ${overlapping} overlapping`,
        },
      },
      stats: {
        layout: "Screw layout",
        layoutOk: "Admissible",
        layoutBad: "Not admissible",
        combined: "Combined load (EC5 8.28)",
        fvRkScrew: "F<sub>v,Rk</sub> per screw",
        nefAxial: "effective n (axial)",
        nefShear: "effective n (shear)",
        admissibleScrews: (ok: number, total: number, twoSides: boolean) =>
          `adm. screws ${ok}/${total}${twoSides ? " per side (×2 sides)" : ""}`,
        fvRkUnion: "F<sub>v,Rk</sub> joint",
        totalKn: "kN total",
        faxRd: "F<sub>ax,Rd</sub>",
        kmodGammaM: "k<sub>mod</sub> · γ<sub>M</sub>",
        fvEd: "F<sub>v,Ed</sub>",
        fvRdUnion: "F<sub>v,Rd</sub> joint",
        kser: "K<sub>ser</sub> per shear plane",
        kserUnit: "(SLS)",
        notAvailable: "N/A",
        shearCheck: "Shear check F<sub>v,Ed</sub>/F<sub>v,Rd</sub>",
        axialCheck: "Withdrawal check F<sub>ax,Ed</sub>/F<sub>ax,Rd</sub>",
        splitCheck: "Splitting check of the central element",
        verified: "verified",
        notVerified: "NOT verified",
      },
      violations: {
        title: "Screws outside the envelope",
        allOk: "All screws respect the required minimum distances.",
        blockedPrefix: "Calculation not performed: ",
      },
      solutions: {
        title: "Compatible solutions",
        intro:
          "For each currently compatible screw (family/diameter/length, after the thickness/service class/connection type filters) it searches for the minimum number of screws (rows×columns, centered grid) that satisfies all active checks (shear; combined load and withdrawal only if F<sub>a</sub>&gt;0). Heavy calculation: it starts on request; after the first search it updates itself at every change of the input data (the previous table is cleared). During the search \"Automatic minimum distances\" and \"Center offset\"=0 are temporarily forced for all candidate screws, then the current state is restored.",
        predrilled: "Pre-drilled",
        washer: "Washer",
        optionsHint:
          "Without ticks: only solutions without pre-drilling and without washer (the form settings do not count; screws for which the ETA requires pre-drilling are still checked with pre-drilling). With a tick: only solutions using that option (with \"Washer\" all compatible washers are tried).",
        search: "Find compatible solutions",
        preparing: "Preparing...",
        progress: (processed: number, total: number, found: number) =>
          `Evaluating: ${processed}/${total} candidate screws (${found} solutions found so far)...`,
        missingTitle: (count: number): string =>
          count === 1
            ? "Unable to generate the solutions: fill in the indicated field."
            : "Unable to generate the solutions: fill in the indicated fields.",
        missingPanel: (panel: string) => `— panel «${panel}»`,
        none: (candidates: number) =>
          `No solution found among ${candidates} candidate screws: no family/diameter/length/grid combination satisfies all checks within the maximum geometrically admissible number of screws.`,
        summary: (found: number, candidates: number, shown: number) =>
          `${found} solutions found among ${candidates} candidate screws evaluated — first ${shown} shown, sorted by % shear (then % combined, then % withdrawal).`,
        selectNew:
          "The current configuration is no longer valid with the entered data: <b>select the new compatible solution</b> from the table (button «Use this»).",
        columns: {
          family: "Family",
          grid: "Rows×columns (tot.)",
          options: "Options",
          shear: "η shear",
          combined: "η combined",
          withdrawal: "η withdrawal",
        },
        predrilledOption: "pre-drilled",
        apply: "Use this",
      },
      formulas: {
        noDiameter:
          'data not available (no valid screw diameter selected — see the warning above "Outer thread diameter").',
        staggerNef: (n: number) =>
          `Staggering ≥1d active (EC5 Table 8.1, note 8): no reduction, n<sub>ef</sub> = n = ${n} (admissible screws).`,
        nefAxial: (n: number, pow: string, ninety: string, result: string) =>
          `n<sub>ef,axial</sub> = max(n<sup>0.9</sup>; 0.9·n) — ETA-11/0030 (self-tapping screws, row of n screws parallel to the load) — n=${n} → max(${pow}; ${ninety}) = ${result}`,
        staggerNefShear: (n: number) =>
          `Staggering ≥1d active (EC5 Table 8.1, note 8): no reduction, n<sub>ef,shear</sub> = n = ${n}.`,
        rowParallel: (
          nRow: number,
          a1: string,
          a1d: string,
          element: number,
          reps: number,
          extra: string,
        ) =>
          `Row of ${nRow} screws parallel to the load (actual spacing a1=${a1}mm=${a1d}d, element ${element}), repeated ${reps} times (independent lines)${extra}`,
        table81Steel: (a1Eff: string, ratio: string) =>
          ` (for Table 8.1, steel-timber: a<sub>1</sub>/0.7 = ${a1Eff}mm=${ratio}d)`,
        kefLine: (kef: string, n: number, nef0: string) =>
          `k<sub>ef</sub> = ${kef} (EC5 Table 8.1) → n<sub>ef,0</sub> = n<sup>k<sub>ef</sub></sup> = ${n}<sup>${kef}</sup> = ${nef0}`,
        angleLine: (angle: string, panel: string, nef0: string, nRow: number, result: string) =>
          `actual force/grain angle = ${angle}°${panel} → n<sub>ef,shear</sub> = n<sub>ef,0</sub> + (n − n<sub>ef,0</sub>)·(angle/90°) = ${nef0} + (${nRow} − ${nef0})×(${angle}/90) = ${result}`,
        panelNoInterp: " (panel-timber: no interpolation with the angle, as in the reference report)",
        totalShear: (reps: number, result: string, crit: string) =>
          `n<sub>ef,shear</sub> (total) = ${reps} × ${result}${crit}`,
        critElement: (element: number) => ` (element ${element}, largest number of screws in a row)`,
        doubleNote: "double shear: the same screw passes through the three elements, 2 shear planes",
        steelNote: (sides: string) => `two single-shear connections, one per side: ${sides} with central timber 2`,
        threeNote: "two single-shear connections, one per side: elements 2 and 3 with element 1",
        sidePanels: "panel 1 and panel 3",
        sidePlates: "plate 1 and plate 3",
        govPlane: "governing",
        kserNoRho:
          "ρ<sub>mean</sub> not available (N.D. in the database for this class): enter ρ<sub>mean</sub> manually to calculate K<sub>ser</sub>.",
        kserLvlNote: (family: string) =>
          `<span class="report-formula">ETA-11/0030 — screw ${family} in LVL softwood, 5≤d≤10mm, independent of the angle ε</span>`,
        kserPredrilled: "pre-drilled",
        kserNotPredrilled: "not pre-drilled",
        kserPanelRho: (rhok: string, rhoMean: string, rhoM: string) =>
          `ρ<sub>m</sub> = √(ρ<sub>panel</sub>·ρ<sub>mean,2</sub>) = √(${rhok}×${rhoMean}) = ${rhoM} kg/m³ (panel ρ<sub>mean</sub> assumed equal to ρ<sub>k</sub>)`,
        kserMean2: (rhoM: string) => `ρ<sub>mean,2</sub> = ${rhoM} kg/m³`,
        kserTimberRho: (rho1: string, rho2: string, rhoM: string) =>
          `ρ<sub>m</sub> = √(ρ<sub>mean,1</sub>·ρ<sub>mean,2</sub>) = √(${rho1}×${rho2}) = ${rhoM} kg/m³`,
        kserSteelNote: (predrilled: string, rho: string) =>
          `EN 1995-1-1 §7.1/Table 7.1, as for nails ${predrilled} — ${rho}; ×2.0 for steel-timber connections (EC5 §7.1(3))</span>`,
        kserTimberNote: (predrilled: string, rho: string) =>
          `EN 1995-1-1 §7.1/Table 7.1, as for nails ${predrilled} — ${rho}</span>`,
      },
      positioning: {
        title: "Screw positioning relative to the edges",
        meta: "element 1 and element 2, dimensions in mm",
        metaThree: "element 1 (central) and elements 2 and 3, dimensions in mm",
        metaSteel3: "elements 1 and 3 (steel plates) and element 2 (central timber), dimensions in mm",
        metaPanel3: "elements 1 and 3 (panels) and element 2 (central timber), dimensions in mm",
        notAvailable: "Screw positioning not available: calculation not performed.",
        notAvailableSee: "Screw positioning not available: calculation not performed (see the warning on the right).",
        none: "No admissible screw to position: change rows/columns or the input data.",
        missingDims: "Complete the geometric data to see the screw positioning.",
        captionElements13Plates: "Elements 1 and 3 (identical steel plates)",
        captionElements13Panels: "Elements 1 and 3 (identical panels)",
        captionElement1: "Element 1",
        suffixPlate: " (steel plate)",
        suffixPanel: " (panel)",
        suffixCentral: " (central)",
        captionView1: " — view in its own plane: end at the bottom (opposite F), F upwards",
        captionView2: " — view in its own plane: lower end at the bottom",
        captionElements23: "Elements 2 and 3 (identical)",
        captionElement2Timber: "Element 2 (central timber)",
        captionElement2: "Element 2",
        continuousNoEnd: " · continuous element: no end dimension",
        axisElement1: "element 1",
        axisElements23: "elements 2 and 3",
        axisElement2: "element 2",
        note: (p: {
          s1: string;
          s2: string;
          axis2: string;
          spread: boolean;
          s1Min: string;
          s2Min: string;
          rows: number;
          cols: number;
          staggered: boolean;
          nShow: number;
          nTotal: number;
          threeD: boolean;
          twoSides: boolean;
          steel3: boolean;
          panel1: boolean;
          stagger: boolean;
          d: string;
          dir: string;
        }) =>
          `Dimensions in mm, from the element edge to the screw axis. Grid spacings: s<sub>1</sub> = ${p.s1} mm (along the element 1 axis), s<sub>2</sub> = ${p.s2} mm (along the ${p.axis2} axis)${p.spread ? ` — screws spread to the limits of the admissible area (minimum required spacings ${p.s1Min} / ${p.s2Min} mm)` : ""} · ${p.rows} × ${p.cols} screws${p.staggered ? ", staggered" : ", aligned (not staggered)"} · ${p.nShow} admissible${p.nShow < p.nTotal ? ` out of ${p.nTotal}; screws outside the envelope are in red and must not be installed` : ""}${p.threeD ? " · elements 2 and 3 identical: the same screw passes through the three elements (a single grid)" : ""}${p.twoSides ? ` · elements 2 and 3 identical: the screws of element 3 are the mirror copy of those of element 2 (${p.nShow} per side, ${2 * p.nShow} in total)${p.stagger ? `, staggered by 1·d = ${p.d} mm (direction ${p.dir}) because L−h exceeds t2 + t1/2: hollow circles = screws of element 3 (not dimensioned)` : ""}` : ""}${p.steel3 ? ` · ${p.panel1 ? "panels 1 and 3 identical" : "plates 1 and 3 identical"}: the screws of side 3 are the mirror copy of those of side 1 (${p.nShow} per side, ${2 * p.nShow} in total)${p.stagger ? `, staggered by 1·d = ${p.d} mm (direction ${p.dir}) because L−h exceeds t1 + t2/2: hollow circles = screws of plate 3 (not dimensioned)` : ""}` : ""}.`,
        tableNo: "No.",
        tableElements13: (plates: string) => `Elements 1 and 3 (${plates})`,
        tableElement1: "Element 1",
        tableElements23: "Elements 2 and 3",
        tableElement2: "Element 2",
        tableDistance: "distance from the edge [mm]",
        tableEnd: "end",
        tableLeft: "left",
        tableRight: "right",
        tableMinMaxTitle: "Minimum/maximum distance from the edge [mm]",
        tableMin: "minimum",
        tableMax: "maximum",
        tableFromEnd1: "Element 1 — from the end",
        tableFromLeft1: "Element 1 — from the left edge",
        tableFromLeft2: (el2: string) => `${el2} — from the left edge`,
        tableFromBottom2: (el2: string) => `${el2} — from the bottom end`,
        tableMoreThan40:
          "With more than 40 screws the coordinates of each screw are not listed: the overall dimensions in the figure and the grid spacings s<sub>1</sub>/s<sub>2</sub> apply.",
        platesWord: "plates",
        panelsWord: "panels",
      },
      report: {
        title: "Calculation report",
        note: "Preview of the PDF report that is printed (data, views, results, methodological notes).",
        versionLabel: "Report version",
        full: "Full",
        fullHint: "(with methodological notes)",
        compact: "Compact",
        compactHint: "(without methodological notes)",
        show: "Show report",
        hide: "Hide report",
        generating: "Generating the report…",
        generatePdf: "Generate PDF report",
        exportDxf: "Export DXF",
        viewer: {
          toolbar: "Report viewer controls",
          previousPage: "Previous page",
          pageNumber: "Page number",
          nextPage: "Next page",
          zoomOut: "Zoom out",
          zoom: "Zoom",
          zoomIn: "Zoom in",
          fitWidthOption: (pct: number) => `Fit width (${pct} %)`,
          fitPageOption: (pct: number) => `Fit page (${pct} %)`,
          fitWidth: "Fit to width",
          fitPage: "Fit to page",
          continuous: "Continuous scrolling",
          singlePage: "Single page",
          twoPages: "Two pages side by side",
          blackWhite: "Black and white preview",
          blackWhiteShort: "B/W",
          paginating: "Laying out the report…",
        },
        previewError: "The report preview could not be generated: try again.",
      },
    },
  },

  envelopeDesignerModule: {
    /** the server's refusals and warnings, by code (an unknown code keeps the server's English) */
    resultCodes: {
      errors: {
        INVALID_REQUEST_BODY: "Invalid request body.",
        INVALID_REQUEST_UNKNOWN_ACTION: "Invalid request: unknown action.",
        INVALID_REQUEST_NO_LAYERS: "Invalid request: no layers to calculate.",
        INVALID_REQUEST_NO_MATERIALS: "Invalid request: material records are missing.",
        INVALID_REQUEST_NO_MONTHLY_CLIMATE: "Invalid request: the monthly climate (12 temperatures and vapour pressures) is missing - load an EPW file.",
        INVALID_REQUEST_OUTSIDE_SIDE: "Monthly Glaser: the request does not give the side of the outside.",
        LAYERS_TOO_MANY: (p: { max: number }) => `Invalid request: at most ${p.max} layers.`,
        MONTHLY_OUTSIDE_TEMPERATURE_INVALID: "Invalid input: a monthly mean outside temperature is outside -60 to 60 °C.",
        MONTHLY_OUTSIDE_VAPOUR_PRESSURE_INVALID: "Invalid input: a monthly mean outside vapour pressure is not between 0 and 10000 Pa.",
        MONTHLY_METHOD_UNKNOWN: "Invalid input: unknown calculation method.",
        MONTHLY_CONDENSATION_TOLERANCE_INVALID: "Invalid input: the condensation tolerance must be 0 (exact) or 1e-9.",
        MONTHLY_SERVER_ERROR: "Monthly Glaser: the analysis failed on the server.",
        CALCULATION_SERVER_ERROR: "The calculation failed on the server.",
        HUMIDITY_CLASS_INVALID: "Invalid input: the UNI EN ISO 13788 humidity class must be 1, 2, 3, 4 or 5.",
        INSIDE_TEMPERATURE_INVALID: "Invalid input: the inside temperature must be a number between -10 and 50 °C.",
        INSIDE_RELATIVE_HUMIDITY_INVALID: "Invalid input: the inside relative humidity must be a number above 0 and at most 100 %.",
        EN15026_MONTHLY_VALUES_MISSING: "Invalid input: the EN 15026 Annex D inside climate needs the monthly values computed from the climate file (load the EPW file again).",
        ASHRAE160_UNAVAILABLE: "ASHRAE 160 inside climate: not available yet - the values of its simplified method are not verified.",
        INSIDE_MODEL_UNKNOWN: "Invalid input: unknown inside climate model.",
      },
      warnings: {} as Record<string, { title: string; detail: string }>,
    },
    inputErrors: {
      layer: (position: number, material: string) => `Layer ${position} (${material})`,
      stud: (layer: string, material: string) => `${layer}, stud ${material}`,
      bad: { nan: "not a number", infinite: "infinite", negative: (value: number) => `negative (${value})`, zero: "0" },
      messages: {
        layerThickness: (subject: string, got: string) => `${subject}: the thickness must be greater than 0 (got ${got}).`,
        layerLambda: (subject: string, got: string) => `${subject}: the thermal conductivity λ must be greater than 0 (got ${got}).`,
        layerMu: (subject: string, got: string) => `${subject}: the vapour resistance factor μ must be greater than 0 (got ${got}).`,
        studWidth: (subject: string, got: string) => `${subject}: the width must be greater than 0 (got ${got}).`,
        studSpacing: (subject: string, got: string) => `${subject}: the spacing must be greater than 0 (got ${got}).`,
        studDepth: (subject: string, got: string) => `${subject}: the depth must be greater than 0 (got ${got}).`,
        studDepthExceeds: (subject: string, _got: string, depthMM: number, thicknessMM: number) =>
          `${subject}: the depth (${depthMM} mm) exceeds the thickness of its layer (${thicknessMM} mm).`,
        studLambda: (subject: string) => `${subject}: the material has no valid thermal conductivity λ.`,
        studMu: (subject: string) => `${subject}: the material has no valid vapour resistance factor μ.`,
        rsi: (_subject: string, got: string) => `The inside surface resistance Rsi must be greater than 0 (got ${got}).`,
        rse: (_subject: string, got: string) => `The outside surface resistance Rse must be greater than 0 (got ${got}).`,
        tInt: (_subject: string, got: string) => `The inside temperature must be a number (got ${got}).`,
        tExt: (_subject: string, got: string) => `The outside temperature must be a number (got ${got}).`,
        rhInt: (_subject: string, got: string) => `The inside relative humidity must be between 0 and 100 % (got ${got}).`,
        rhExt: (_subject: string, got: string) => `The outside relative humidity must be between 0 and 100 % (got ${got}).`,
      },
    },
    admin: {
      materials: "Materials",
      examples: "Example stratigraphies",
      manufacturer: "Manufacturer",
      variable: "variable",
      deleteMaterialQuestion: "Do you really want to delete this material? A material used by a calculation or an example cannot be deleted: hide it instead.",
      material: "material",
      subcategory: "Subcategory",
      category: "Category",
      categories: "Categories",
      subcategories: "Subcategories",
      condCategories: "Condensation categories",
      rule: "Rule",
      condCategory: "Condensation category (UNI EN ISO 13788)",
      hatch: "Hatch",
      thicknessMode: "Thickness mode",
      defaultThickness: "Reference thickness",
      knownThicknesses: "Known thicknesses (comma separated)",
      minThickness: "Minimum thickness",
      maxThickness: "Maximum thickness",
      sdCurveHint: "Variable-Sd membranes only: points with RH as a 0-1 fraction and Sd in metres.",
      sectionMembrane: "Membrane",
      sectionTestDatabase: "Test database (hygric values of the file)",
      sectionCurves: "Measured curves",
      sectionProvenance: "Data sources",
      sdVariableMin: "Variable Sd, min [m]",
      sdVariableMax: "Variable Sd, max [m]",
      dryCupPerm: "Dry-cup permeance [US perm]",
      curvesHint: "Each curve is a JSON list of points, with the keys shown in its field (empty: no curve).",
      moistureCurve: "Sorption curve w(RH) [RH %, w kg/m³]",
      lambdaMoistureCurve: "λ(w) [w m³/m³, λ W/(m·K)]",
      muMoistureCurve: "μ(w) [w m³/m³, μ -]",
      sdHumidityCurve: "Humidity-dependent Sd curve Sd(RH) [RH 0-1, Sd m]",
      suctionCurve: "Suction curve [log10 p_C Pa, w kg/m³]",
      liquidConductivityCurve: "Liquid conductivity [w kg/m³, log10 K_l s]",
      datasheetUrl: "Data sheet (URL)",
      productPageUrl: "Product page (URL)",
      datasheetChecked: "Data sheet checked on",
      formerlyNamed: "Former name",
      sourceDatabase: "Source database",
      sourceLaboratory: "Laboratory",
      sourceDate: "Measurement date",
      sourceFile: "Source file",
      assumedFields: "Assumed values (not on the data sheet)",
      derivedFields: "Derived values (computed from the data sheet)",
      dataNote: "Note on the data sheet",
      dataNoteLambda: "λ of the data sheet [W/(m·K)]",
      invalidCurve: (curve: string) => `${curve}: not a valid list of points.`,
      label: "Label",
      element: "Element",
      structure: "Structure",
      regions: "Regions",
      layerCount: "Layers",
      default: "default",
      deleteExampleQuestion: "Do you really want to delete this example?",
      example: "example",
      description: "Description",
      layersInsideOut: "Layers (inside → outside)",
      addBridge: "Add stud/rafter",
      width: "width",
      spacing: "clear spacing",
      fullDepth: "full depth",
      addLayer: "Add layer",
    },
    errors: {
      materialAlreadyExists: "A material with the same name and manufacturer already exists.",
      materialInUse: "The material is used by a calculation or an example: hide it instead of deleting it.",
      exampleNotFound: "Example not found.",
      invalidExample: "Invalid example stratigraphy.",
      exampleAlreadyExists: "An example with the same code already exists.",
      invalidMaterial: "Invalid custom material.",
      materialNotFound: "A layer uses a material that is not in the catalog.",
      customMaterialNotFound: "A layer uses a custom material that does not exist.",
      invalidLayer: "Invalid layer.",
      invalidBridge: "Invalid thermal bridge.",
      condCategoryNotFound: "Unknown condensation category.",
      categoryAlreadyExists: "A category with the same code already exists.",
      categoryInUseByMaterial: "Materials are of this category: hide it instead of deleting it.",
      categoryInUseBySubcategory: "Subcategories are of this category: delete or move them first.",
      subcategoryAlreadyExists: "A subcategory with the same code already exists.",
      subcategoryInUseByMaterial: "Materials are of this subcategory: hide it instead of deleting it, and move it only to the category of its materials.",
    },
    nav: {
      project: "Project",
      data: "Data",
      dataTitle: "Data: project, site, element, analysis and calculation mode",
      stratigraphy: "Stratigraphy",
      stratigraphyTitle: "Stratigraphy: layers and materials of the component",
      report: "Report",
      reportTitle: "Report: preview and print",
      general: "General",
      materialsDatabase: "Materials database",
      materialsDatabaseTitle: "Materials database: browse, filter and add materials",
      settings: "Settings",
      settingsTitle: "Settings: units, standards, calculation and keyboard shortcuts",
      tutorial: "Tutorial",
      tutorialTitle: "Tutorial - a demonstration of the main functions",
      toggleSidebar: "Collapse/expand the sidebar",
      analysisResults: "Analysis & results",
      analysisResultsTitle: "Analysis & results: the static and the quasi-dynamic analysis",
      quasiDynamic: "Quasi-dynamic",
      quasiDynamicLockedTitle: "Quasi-dynamic analysis: not available in this beta",
      openQuasiDynamicTitle: "Open the quasi-dynamic analysis too?",
      openQuasiDynamicMessage: "The static analysis was selected in the Data tab. Do you want to proceed with the quasi-dynamic analysis as well?",
      openQuasiDynamicConfirm: "Yes, open it",
      reportLockedNote: "Available once the static analysis is calculated for this stratigraphy",
      reportLockedToast: "The report: available once the static analysis is calculated for this stratigraphy.",
      completeDataFirst: "Complete the Data tab first (project, element, analysis)",
      addLayerFirst: "Add at least one layer to the stratigraphy first",
      openStratigraphyFirst: "Open the Stratigraphy first",
      statusTodo: "To be completed",
      statusOk: "Complete",
      statusWarn: "Satisfied, with a warning",
      statusBad: "At least one check not satisfied",
      statusBusy: "Calculating…",
      undoPrevious: "Undo the previous action",
      showRecentActions: "Show recent actions",
      redoNext: "Redo the next action",
      recentUndoHistory: "Recent undo history",
      undoMultiple: "Undo multiple actions",
      noActions: "No actions",
      historyEmpty: "Your recent changes will appear here.",
      historyFoot: "Select an action to return to the state immediately before it. Hover for the full change details.",
    staticAnalysis: "Static",
    staticAnalysisTitle: "Static analysis: results (1D or 2D), Monthly Glaser, settings",
    quasiDynamicTitle: "Quasi-dynamic analysis: inputs and results",
    },
    preferences: {
      preferences: "Preferences",
      preferencesTitle: "Preferences: units and standards",
      units: "Units",
      unitsTitle: "SI: metric. Imperial: every value converted (vapour diffusion as permeance in US perm). UK: SI, except vapour diffusion shown as vapour resistance in MN·s/g. Calculations themselves are always done in SI - this only changes what's displayed/printed.",
      ukTitle: "Same SI units as everywhere else, except vapour diffusion, shown as vapour resistance in MN·s/g - the unit UK building-physics practice uses for it",
      unitsHint: "Saved with the calculation; a new calculation starts with the units last chosen (else SI).",
      standards: "Standards",
      standardEuTitle: "Menus show the UNI / EN options (EN 15026, UNI EN ISO 13788)",
      standardNaTitle: "Menus show the ASHRAE options (ASHRAE 160)",
      standardsHint: "Menus with alternatives by standard (rain in the quasi-dynamic analysis, inside climate of the Monthly Glaser analysis) show only the chosen standard's options.",
    },
    settings: {
      changesPending: "Changes not applied yet - Done applies them",
      changesApplyOnDone: "Changes apply when you press Done",
      done: "Done",
      title: "Settings",
      keyboardShortcuts: "Keyboard shortcuts",
      solver2d: "2D solver",
      climate: "Climate",
      quasiDynamic: "Quasi-dynamic",
      quasiDynamicLockedTitle: "Quasi-dynamic analysis: not available in this beta",
      quasiDynamicLockedLabel: "Quasi-dynamic: not available in this beta",
      about: "About",
      shortcutsHint: "Click a field, then press the key combination you want.",
      resetToDefaults: "Reset to defaults",
      solver2dHeading: "2D Steady-State Hygrothermal method — mesh, iterations and tolerances",
      solver2dIntro: "Governs the coupled temperature / vapour-pressure finite-volume solve of the 2D mesh mode. Has no effect on the 1D Glaser method.",
      solvingMethod: "Solving method",
      solverCg: "Conjugate Gradient (Jacobi-preconditioned)",
      solverCgHint: "The static 2D analysis always uses the Jacobi-preconditioned conjugate gradient: it converges also with vapour barriers, where Gauss-Seidel and the V-cycle stop before the energy balance is met.",
      meshRows: "Mesh (rows through thickness)",
      meshHint: "Auto (first position) re-solves at 20→40→70→100→160→250→300 rows and stops once the heat flux changes by less than 0.1% between two meshes and the solver itself has converged (see the Diagnostics card). Slide right to fix the mesh: 20, 40, 70, 100, 160, 250 or 300 rows - finer is more precise and slower.",
      maxIterationsBefore: "Max iterations (T and p",
      maxIterationsAfter: ", coupled loop)",
      epsU: "εU — U-value / transmittance tolerance [-]",
      epsRH: "εRH — vapour pressure / RH tolerance [-]",
      toleranceHintBefore: "Tolerance is only ever applied to the two quantities that actually matter for the engineering result: εU is the relative change in U-value/heat flux between coupled rounds (since the temperature field comes from the same thermal solve, this also governs how tightly temperatures settle); εRH is the change in relative humidity between coupled rounds (RH=p",
      toleranceHintAfter: "(T), so this only stays flat once both the vapour-pressure field and the temperature it depends on have settled). A coarser mesh, looser tolerances or fewer iterations converge faster but resolve the field around a framing member less precisely - see the mesh status cards above the Examples button for whether the current settings actually reach convergence. Gauss-Seidel and Conjugate Gradient solve the fine mesh directly; V-cycle multigrid additionally uses a hierarchy of coarser, algebraically-aggregated meshes to accelerate that same solve - all three converge to the same field.",
      aboutNotice: "© 2026 Rotho Blaas Srl · Envelope Designer. Beta - unofficial version, still under development.",
      methodology: "Methodology & limitations",
      aboutBuild: "About this build",
      climateSources: "Climate sources",
      climateSourcesHint: "Choose, quantity by quantity, where the climate comes from (PVGIS, your EPW file, estimates), see which provider offers what, and download the combined year as .epw.",
      epwAdvanced: ".epw advanced…",
      epwWhereToGet: "Where to get EPW weather files",
      epwHint: "Choose the station nearest the site, with similar elevation and exposure (EN 15026 §5.2.1); a coastal airport can differ a lot from an inland or mountain site. Use a typical year for the design case and the −2 K option of the panel for winter problems. The file must hold a full year of hourly values (8760 rows); the panel checks it and reports gaps. An uploaded file is sent with each calculation and not stored (the request log keeps only a fingerprint).",
      epwOneBuilding: "free typical years (TMYx) for thousands of weather stations worldwide, one ZIP per station with the EPW inside. Follow the repository’s terms of use; do not redistribute the files.",
      epwMap: "a map of the same stations: click the one nearest the site to download it.",
      epwPvgis: "the \"Typical meteorological year\" tool exports as EPW the same typical year the PVGIS source here downloads for the map point.",
      epwEnergyPlus: "the EnergyPlus page for weather files, which points to the repository above.",
      epwNsrdb: "satellite-based typical years for North America (free registration); downloaded as CSV, to be converted to EPW.",
      epwLicensed: "licensed, quality-controlled data sets (Meteonorm: meteonorm.com), when a project needs them.",
      qdHeading: "Quasi-dynamic analysis — calculation",
      qdIntro: "Applies to the next run of the Quasi-dynamic panel.",
      timeStep: "Time step",
      timeStepAuto: "Automatic (1D: 5 min, 2D: 1 h)",
      timeStepHint: "Implicit time integration: any step is stable, but steps longer than 1 h use the climate averaged over the step, so the daily cycle (night cooling, midday sun) is smoothed out. EN 15026 works with hourly climate data: keep 1 h or shorter for design results. Measured on a 2x6 stud wall (2D, 20 rows, one year): the condensate peak was 94 g/m² with 1 h, 100 with 6 h, 112 with 24 h.",
      solutionMethod: "Solution method (2D mesh)",
      qdMethods: {
        pcg: "Conjugate gradient, line-preconditioned (PCG)",
        "pcg-jacobi": "Conjugate gradient, Jacobi-preconditioned",
        gs: "Gauss-Seidel",
        jacobi: "Jacobi",
        thomas: "Direct tridiagonal (Thomas)",
      },
      defaultDash: " - default",
      solutionMethodHint: "Solver of the heat and vapour systems of every time step, all stopped on the same relative residual. The line-preconditioned PCG solves each column of the grid exactly inside every iteration (thin membranes and surface films couple the cells across the thickness much more than sideways) and is the fastest; the Jacobi-preconditioned PCG is the reference algorithm of the validation report 05. Gauss-Seidel and Jacobi need far more iterations (on a 2x6 wall one year took more than 12 minutes, beyond the server's 300 s): use them to cross-check short runs. The Convergence section shows every hour. The 1D analysis always uses the direct tridiagonal (Thomas) solver.",
      tolerance: "Tolerance",
      defaultParen: " (default)",
      maxIterations: "Max. iterations",
      toleranceQdHint: "A solve has converged when ‖b − Ax‖ / ‖b‖ is below the tolerance. At 1e−10 the 2D grids with thin membrane rows left mass balance residuals of about 1e−5; 1e−12 keeps them below 1e−6. Max. iterations: per solve.",
      gridRows: "2D grid rows",
      gridRowsOption: (rows: number) => `${rows} rows`,
      gridRowsHint: "2D mesh mode only. Finer grids resolve the interfaces better but take longer; the server stops a calculation after 300 s. Measured on a 2x6 stud wall, one simulated year with the line-preconditioned PCG: 20 rows 12 s, 40 rows 18 s, 70 rows about 4 min (one year only). Finer grids would not finish in time.",
      restoreDefaults: "Restore defaults",
      lockedNote: "Not available in this beta",
    notAppliedTitle: "Settings not applied",
    notAppliedMessage: "You changed settings that are not applied yet: no tab is updated until you press Done. What do you want to do?",
    keepEditing: "Keep editing",
    discardChanges: "Discard the changes",
    applyDone: "Apply (Done)",
    meshAuto: "Auto",
    meshRowsReadout: (rows: number) => `${rows} rows`,
    },
    confirm: {
      cancel: "Cancel",
    },
    data: {
      stepLabel: (n: number, title: string) => `Step ${n}: ${title}`,
      stepSite: "Site",
      stepElement: "Element",
      stepAnalysis: "Analysis",
      stepCalculation: "Calculation",
      useThisSite: "Use this site",
      elements: {
        wall: "Wall",
        roof: "Roof",
        ground: "Ground connection",
      },
      analyses: {
        static: "Static analysis",
        dynamic: "Quasi-dynamic analysis",
      },
      wallDesc: "External wall",
      roofDesc: "Roof or top floor",
      groundDesc: "Floor on the ground",
      groundDynamicNote: "The quasi-dynamic analysis does not model contact with the ground: the outside surface is exposed to the outdoor air.",
      staticOption: "Static",
      staticDesc: "Design condition and monthly Glaser",
      quasiDynamic: "Quasi-dynamic",
      quasiDynamicDesc: "Hour by hour over several years",
      simplified: "Simplified",
      simplifiedDesc: "1D through the layers",
      meshMode: "Mesh mode",
      meshModeDesc: "2D mesh, studs resolved",
      meshNoteLabel: "2D mesh mode: numerical methods (note)",
      numericalMethods: "Numerical methods.",
      meshNote: "The 2D mesh mode uses numerical methods: verifying their validity, and the suitability of every material and product selected in the stratigraphy, for the specific case, is the responsible designer's own responsibility.",
      missing: {
        site: "site",
        element: "element",
        analysis: "analysis",
      },
      stillToChoose: (list: string) => `Still to choose: ${list}.`,
      continueToStratigraphy: "Continue to Stratigraphy →",
      continueToAnalysis: {
        static: "Continue to Static analysis →",
        dynamic: "Continue to Quasi-dynamic analysis →",
      },
      backToData: "← Data",
      howToStart: "How do you want to start?",
      fromExample: "From an example",
      fromExampleDesc: (n: number, element: string) => `Pick one of the ${n} ${element} assemblies and adapt it`,
      fromScratch: "From scratch",
      fromScratchDesc: "An empty stratigraphy: add the layers from the material library",
      back: "← Back",
    },
    examples: {
      close: "Close",
      examplesTitle: (element: string) => `Examples: ${element}`,
      elementName: {
        wall: "wall",
        roof: "roof",
        ground: "ground connection",
      },
      exampleLoaded: (name: string) => `Example loaded: ${name}`,
      changeExample: "Change example",
      changeExampleTitle: (element: string) => `Replace the layers with another ${element} example (Undo brings them back)`,
      structures: {
        timber: "Timber frame",
        mass_timber: "Mass timber",
        masonry: "Masonry",
        concrete: "Concrete",
        steel: "Steel",
        other: "Other",
      },
      types: {
        wall: "Wall",
        roof: "Roof / floor",
        ground: "Ground floor",
      },
      searchPlaceholder: "Search examples...",
      exampleSingular: "example",
      examplePlural: "examples",
      filtersLabel: "Example filters",
      region: "Region",
      yourLocation: (country: string) => `your location: ${country}`,
      type: "Type",
      structure: "Structure",
      resetFilters: "Reset filters",
      noMatch: "No assembly matches the search or the selected filters.",
      noneInRegion: "No assembly of this type in the selected region.",
      backToTypes: "Back to the types",
    },
    climate: {
      loadError: "The climate data could not be loaded.",
      suggestedCorrections: "Suggested corrections",
      applyAndReload: "Apply and reload",
      correctionsHint: "Nothing is corrected unless you choose it; the applied corrections are listed with the results.",
      correctionsChosen: (list: string) => `Corrections chosen: ${list} (nothing to correct for this source).`,
      undo: "Undo",
      cardTitle: "Climate of the site",
      autoEpwTitle: "The climate of the map point, built automatically from a provider (PVGIS, ERA5 via Open-Meteo, TMYx station).",
      autoEpw: "Auto EPW",
      importEpwTitle: "Upload an EPW file (TMYx, NSRDB, Meteonorm or any other source).",
      importEpw: "Import EPW",
      yearType: "Year type",
      yearTypical: "Typical year, repeated",
      yearMinus2: "Typical year −2 K (winter problems, EN 15026 §5.2.1 c)",
      yearPlus2: "Typical year +2 K (summer problems)",
      provider: "Climate provider",
      providerPvgis: "PVGIS typical year (EU JRC) - no rain",
      providerOpenMeteo: "ERA5 via Open-Meteo - rain, soil temperatures",
      providerTmyx: "TMYx weather station (climate.onebuilding.org)",
      era5Year: "ERA5 year",
      era5YearTitle: "The first typical year of a site downloads 10 years of hourly data (about 10-20 s); then it is kept for that grid cell.",
      era5Typical: "Typical year (EN ISO 15927-4, last 10 years)",
      actualYear: (year: number) => `Actual year ${year}`,
      tmyxStation: "TMYx station",
      tmyxStationTitle: "Distance and elevation difference to the site. The best match counts 100 m of height as 10 km; the temperature is corrected for the elevation difference.",
      bestMatch: (station: string) => `Best match: ${station}`,
      lat: "Lat",
      latitude: "Latitude [°]",
      lon: "Lon",
      longitude: "Longitude [°]",
      elevTitle: "Site elevation [m]. Empty: the elevation of the climate data (shown in grey). Type a value to correct the temperature to the site (0.65 K per 100 m).",
      elev: "Elev",
      elevAuto: "auto",
      siteElevation: "Site elevation [m]",
      viewClimateData: "View climate data",
      viewClimateDataLoading: "View climate data (loading)",
      climateDataLoading: "Climate data loading…",
      loadingClimateData: "Loading climate data",
      climateData: "Climate data",
      backToMap: "Back to the map",
      map: "Map",
      epwFile: "EPW file",
      fileUnreadable: (name: string) => `The file ${name} could not be read.`,
      weatherStationHint: "A weather station near the site is the most reliable source (EN 15026 §5.2.1).",
      elevationFromEpw: (elevation: number) => `${elevation} (from the EPW)`,
      fromEpw: "from the EPW",
      epwElevationTitle: "Empty: the elevation of the EPW. Type a value to correct the temperature to the site (0.65 K per 100 m).",
      notEpwFile: (name: string) => `${name} is not an EPW file: choose a file ending in .epw.`,
      readingClimate: "reading the climate…",
      loaded: "loaded",
      replace: "Replace…",
      remove: "Remove",
      dropEpwHere: "Drop an EPW file here",
      epwSources: "TMYx, NSRDB, Meteonorm or any other source",
      chooseEpw: "Choose EPW file…",
      noWindData: "No wind data in this climate file.",
      loadingTheClimateData: "Loading the climate data…",
      insideBasis: "UNI EN ISO 13788 class 3",
      ashraeNotAvailable: ", ASHRAE 160 not available yet",
      heatingDesignBasis: "heating design temperature 99.6 %",
      coldestHour: (day: number, month: string, time: string) => `coldest hour, ${day} ${month} ${time}`,
      aboveSaturation: (rh: string) => ` That vapour pressure is above saturation at Te (RH ${rh} %): 100 % is offered.`,
      climateOfSite: "the climate of the site",
      suggestionTitle: (method: string, source: string, notes: string) => `${method}. Climate: ${source}.${notes} The fields stay editable.`,
      fromTheClimate: (basis: string) => `From the climate (${basis}):`,
      inUse: "in use",
      use: "Use",
      searchFailed: "The search failed.",
      noPlaceFound: "No place found.",
      searchNotSent: "The search could not be sent.",
      mapLabel: "Map: click to choose the site",
      searchPlaceholder: "Search a city, street or place",
      searchLabel: "Search a place",
      searching: "Searching",
      usZonesTitle: "IECC climate zones of the United States (PNNL Building America Solution Center)",
      usZones: "US climate zones",
      addToFavourites: "☆ Add to favourites",
      favouritesFull: "Favourites full (10): remove one first.",
      favouriteName: "Name of the favourite",
      cancel: "Cancel",
      add: "Add",
      renameFavourite: (name: string) => `New name of ${name}`,
      favouriteHint: "drag to reorder, double-click to rename",
      removeFavourite: (name: string) => `Remove ${name} from the favourites`,
      morePlaces: (n: number) => `${n} more places`,
      more: (n: number) => `${n} more`,
    monthsShort: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
    chartLabel: "Reference year: daily mean and daily range of each quantity",
    quantitiesLabel: "Quantities shown (click to hide or show)",
    allHidden: "Every quantity is hidden.",
    legendTitle: (name: string, hidden: boolean) => `${name} - click to ${hidden ? "show" : "hide"}`,
    series: {
      outsideTemperature: "Outside air temperature",
      insideTemperature: "Inside temperature",
      outsideRh: "Outside relative humidity",
      insideRh: "Inside relative humidity",
      solar: "Global solar radiation on the horizontal",
      wind: "Wind speed at 10 m",
      rain: "Rain on the horizontal",
    },
    windRoseLabel: "Wind rose: share of hours by direction and speed",
    windClassesLabel: "Wind speed classes (click to hide or show)",
    windSpeed: "wind speed",
    windFrom: (direction: string, percent: string) => `From ${direction}: ${percent} % of hours`,
    windMean: (value: string, unit: string) => `mean ${value} ${unit}`,
    windMax: (value: string, unit: string) => `max ${value} ${unit}`,
    },
    stratigraphy: {
      flipInsideOutsideTitle: "Flip inside/outside - swaps which end is Inside/Outside (and Ti/RHi/Rsi with Te/RHe/Rse) without reordering the layers",
      resetZoomTitle: "Reset zoom (scroll over the drawing to zoom in/out, drag to pan while zoomed)",
      clearLayersTitle: "Delete all layers (asks first; Undo brings them back)",
      condensationToggleTitle: "Show/hide interstitial condensation on the drawing - wet layer(s) tinted blue, with droplet markers at the condensation point(s)",
      fluxVectorsTitle: "Show heat-flux vectors over the section (2D Hygrothermal mode only) - arrow length follows the local flux magnitude, direction follows the temperature gradient (perpendicular to the isotherms)",
      overlayTemperatureTitle: "Overlay the temperature profile on the drawing (drag the handle to set how much of the width it covers)",
      overlayHumidityTitle: "Overlay the humidity (RH) profile on the drawing (drag the handle to set how much of the width it covers)",
      meshGridTitle: "Show the 2D Hygrothermal calculation mesh's own individual cells, with or without a temperature/humidity overlay (2D mesh mode only, reveal set by the same handle)",
      thickness: "Thickness",
      editMaterial: "Edit material…",
      changeMaterial: "Change material…",
      removeBridge: "Remove bridge",
      resizeHandleTitle: "Drag to resize this box - double-click to reset",
      layerTable: "Layer table",
      layerTableHide: "hide",
      layerTableShow: "show the computed properties of every layer",
      layerTableHint: "Computed thermal properties for the current stratigraphy, interior to exterior. Drag a row's handle to reorder, click a material's name (or its pencil icon) to view/edit it - or use the graphical section above for more actions (right-click for insert/duplicate/delete/bridges).",
      layerTable2DNote: "2D Hygrothermal mode: figures below are sampled from the converged 2D field down the mid-bay column (no framing member), not a genuine per-layer 1D value - see the Results page's 2D Hygrothermal panel for the full field.",
      colMaterial: "Material",
      colTempMin: "Temp min",
      colTempMinTitle: "Temperature at this layer's colder face, under the design condition",
      colTempMax: "Temp max",
      colTempMaxTitle: "Temperature at this layer's warmer face, under the design condition",
      colSdTitle: "Vapour diffusion-equivalent air layer thickness (μ × thickness)",
      colCondensate: "Condensate",
      colCondensateTitle: "Interstitial condensation rate, if any, at this layer's interior face",
      colWeight: "Weight",
      colWeightTitle: "Surface mass per unit area",
      colHeatCapacity: "Heat capacity",
      colHeatCapacityTitle: "Specific heat capacity",
      makeSideInside: "Click to make this side inside",
      makeSideOutside: "Click to make this side outside",
      sideSwitched: (top: boolean, outside: boolean) => `${top ? "Top" : "Bottom"} side: ${outside ? "OUTSIDE" : "INSIDE"}`,
      differsFromLibrary: "Differs from the library material",
      variable: "variable",
      thicknessField: "thickness",
      checkPositive: (fields: string) => `Check ${fields}: a positive number is needed.`,
      stud: "Stud",
      material: "Material",
      thermalHygrometric: "Thermal & hygrometric",
      acoustic: "Acoustic",
      permeance: "Permeance",
      membraneLocked: "Rothoblaas membrane: values from the datasheet, not editable.",
      layerValuesHint: "Values of this layer only, applied on Enter or when leaving a field.",
      studValuesHint: "Values of this stud only, applied on Enter or when leaving a field.",
      checkedOn: (date: string) => `Checked ${date}`,
      dataSheet: "Data sheet",
      resetToLibrary: "Reset to library values",
    outside: "OUTSIDE",
    inside: "INSIDE",
    noLayersDrawing: "No layers yet - add a layer or load a preset.",
    noLayersTable: "No layers - drag a material from the library onto the graphical section above.",
    moveUp: "Move up (outwards)",
    moveDown: "Move down (inwards)",
    excludedClickToInclude: "Excluded from the calculation - click to include",
    includedClickToExclude: "Included in the calculation - click to exclude",
    exteriorResistance: "Exterior surface resistance (Rse)",
    interiorResistance: "Interior surface resistance (Rsi)",
    dragToReorder: "Drag to reorder",
    viewEditMaterial: "View/edit material",
    customMaterialRow: "This layer uses a custom/edited material, not a standard library reference",
    supersaturated: "RH>100%",
    editStud: "Edit stud/rafter",
    removeStud: "Remove stud/rafter",
    studSummary: (name: string, width: string, spacing: string, depth: string, unit: string) => `${name} · ${width}×${spacing}${unit}, depth ${depth}${unit}`,
    wholeComponent: "Whole component",
    wholeComponentMidBay: "Whole component (mid-bay column)",
    sdName: {
      sdValue: "sd-value",
      permeance: "permeance",
      vapourResistance: "vapour resistance",
    },
    insertInterior: "↑ Insert layer (interior side)",
    insertExterior: "↓ Insert layer (exterior side)",
    placeStuds: "Place studs or rafters…",
    removeStuds: "Remove studs or rafters",
    duplicateLayer: "Duplicate layer",
    disableLayer: "Disable layer (exclude from calc)",
    enableLayer: "Enable layer",
    deleteLayer: "Delete layer",
    changeMaterialTitle: "Change material",
    clearAllTitle: "Delete all layers?",
    clearAllMessage: (n: number) => `All ${n} layer${n === 1 ? "" : "s"} of this stratigraphy will be deleted. You can bring them back with Undo (Ctrl+Z).`,
    clearAllConfirm: "Delete all",
    ashrae160Note: "ASHRAE 160 not available yet (its values are being verified): the UNI / EN model is used",
    },
    materialLibrary: {
      title: "Material library",
      materials: "Materials",
      collapseExpand: "Collapse/expand material library",
      openLibrary: "Open the material library",
      searchPlaceholder: "Filter materials...",
      materialSingular: "material",
      materialPlural: "materials",
      noMatches: "No materials match.",
      backTo: (name: string) => `Back to ${name}`,
      backToCategories: "Back to the categories",
      dragToInsert: "Drag onto the layer table to insert",
      dragHint: "Drag a material onto the graphical section to insert it as a new layer (Esc cancels).",
      chooseCategoryHint: "Choose a category, then drag a material onto the graphical section to insert it as a new layer.",
      generic: "Generic",
      filtersLabel: "Material filters",
      min: "min",
      max: "max",
      minLambda: "Minimum thermal conductivity",
      maxLambda: "Maximum thermal conductivity",
      membranes: "Membranes",
      vapourDiffusionAs: "Vapour diffusion as",
      minimumOf: (name: string) => `Minimum ${name}`,
      maximumOf: (name: string) => `Maximum ${name}`,
      permeance: "Permeance",
      vapourResistance: "Vapour resistance",
      manufacturer: "Manufacturer",
      resetFilters: "Reset filters",
      thickness: "Thickness",
      variable: "variable",
      subcategories: {
        custom: "Custom",
      },
    },
    shell: {
      calculating: "Calculation…",
      cancelCalculation: "Cancel calculation",
    },
    shortcuts: {
      undo: "Undo",
      redo: "Redo",
      flip: "Flip inside/outside",
      toggleMode: "Toggle calculation mode",
      changeMaterial: "Change material of the selected layer",
      toggleLibrary: "Open/close the material library",
      togglePanels: "Collapse/expand the sidebar and the results bar",
      pressKeys: "Press keys…",
      none: "(none)",
    },
    history: {
      saved: (time: string) => `Saved ${time}`,
      undoTitle: (label: string) => `Undo: ${label}`,
      undoLabel: (label: string) => `Undo ${label}`,
      nothingToUndo: "Nothing to undo",
      redoTitle: (label: string) => `Redo: ${label}`,
      redoLabel: (label: string) => `Redo ${label}`,
      nothingToRedo: "Nothing to redo",
      menuCount: (shown: number, available: number) => `${shown} of ${available}`,
      steps: (steps: number) => `${steps} step${steps === 1 ? "" : "s"}`,
      actionTitle: (detail: string, steps: number) => `${detail} Click to undo ${steps} action${steps === 1 ? "" : "s"}.`,
      initialState: "Initial state",
      initialStateDetail: "State loaded when this session started.",
      sessionStart: "Session start",
      fieldLabels: {
        thickness: "thickness",
        materialId: "material",
        enabled: "calculation state",
        widthMM: "width",
        spacingMM: "spacing",
        depthMM: "depth",
        mode: "calculation mode",
        stratFlipped: "inside/outside orientation",
        Rsi: "interior surface resistance",
        Rse: "exterior surface resistance",
        patm: "atmospheric pressure",
        meshRows: "simplified mesh rows",
        meshRowsHygro2D: "2D mesh rows",
        maxIterHygro2D: "coupling iteration limit",
        epsU2D: "transmittance tolerance",
        epsRH2D: "vapour pressure / RH tolerance",
        solverMethod2D: "2D solver",
        autoMesh: "automatic mesh",
        meshTolerance: "mesh accuracy",
        Ti: "interior temperature",
        Te: "exterior temperature",
        RHi: "interior relative humidity",
        RHe: "exterior relative humidity",
        name: "name",
        notes: "notes",
      } as Record<string, string>,
      unknownMaterial: "Unknown material",
      layerName: (position: number, material: string) => `layer ${position} (${material})`,
      enabled: "enabled",
      disabled: "disabled",
      notSet: "not set",
      scopeChanged: (scope: string) => `${scope} changed.`,
      scopeDiff: (scope: string, changes: string) => `${scope}: ${changes}.`,
      layerAdded: "Layer added",
      layerAddedDetail: (layer: string, position: number, thickness: string) =>
        `Added ${layer} at position ${position}, thickness ${thickness} mm.`,
      layerDeleted: "Layer deleted",
      layerDeletedDetail: (layer: string, position: number, thickness: string) =>
        `Deleted ${layer} from position ${position}, thickness ${thickness} mm.`,
      stratigraphyReplaced: "Stratigraphy replaced",
      stratigraphyReplacedDetail: (from: number, to: number, added: number, removed: number) =>
        `Layer count changed from ${from} to ${to}; ${added} added and ${removed} removed.`,
      layersReordered: "Layers reordered",
      layersReorderedDetail: (layer: string, from: number, to: number) => `Moved ${layer} from position ${from} to ${to}.`,
      bridgeAdded: "Thermal bridge added",
      bridgeAddedDetail: (material: string, layer: string, width: string, spacing: string, depth: string) =>
        `Added ${material} bridge to ${layer}; width ${width} mm, spacing ${spacing} mm, depth ${depth} mm.`,
      bridgeDeleted: "Thermal bridge deleted",
      bridgeDeletedDetail: (material: string, layer: string) => `Deleted ${material} bridge from ${layer}.`,
      bridgeMoved: "Thermal bridge moved",
      bridgeMovedDetail: (material: string, from: string, to: string) => `Moved ${material} bridge from ${from} to ${to}.`,
      bridgeModified: "Thermal bridge modified",
      bridgeScope: (material: string, layer: string) => `${material} bridge in ${layer}`,
      layerMaterialChanged: "Layer material changed",
      layerMaterialChangedDetail: (position: number, from: string, to: string) =>
        `Changed layer ${position} material from ${from} to ${to}.`,
      layerThicknessChanged: "Layer thickness changed",
      layerThicknessChangedDetail: (layer: string, from: string, to: string) =>
        `Changed ${layer} thickness from ${from} mm to ${to} mm.`,
      layerDisabled: "Layer disabled",
      layerDisabledDetail: (layer: string) => `Excluded ${layer} from calculations.`,
      layerEnabled: "Layer enabled",
      layerEnabledDetail: (layer: string) => `Included ${layer} in calculations.`,
      layerModified: "Layer modified",
      flipped: "Inside/outside flipped",
      flippedDetail: "Reversed the displayed interior/exterior orientation of the stratigraphy.",
      modeChanged: "Calculation mode changed",
      modeChangedDetail: (from: string, to: string) => `Calculation mode: ${from} → ${to}.`,
      projectDetailsModified: "Project details modified",
      projectDetailsScope: "Project details",
      customMaterialAdded: "Custom material added",
      customMaterialAddedDetail: (name: string) => `Added custom material “${name}” to the material library.`,
      customMaterialDeleted: "Custom material deleted",
      customMaterialDeletedDetail: (name: string) => `Deleted custom material “${name}” from the material library.`,
      customMaterialModified: "Custom material modified",
      customMaterialModifiedDetail: (name: string) => `Modified custom material “${name}”.`,
      materialLibraryModified: "Material library modified",
      materialLibraryModifiedDetail: "Multiple custom-material records changed.",
      climateModified: "Design climate modified",
      climateScope: "Design climate",
      settingsModified: "Calculation settings modified",
      settingsScope: "Calculation settings",
      projectModified: "Project modified",
      projectModifiedDetail: "Multiple project values changed in this saved action.",
    },
    materials: {
      panelDesc: "Built-in and custom materials. Custom materials are stored in this browser's local storage.",
      newMaterial: "+ New material",
      searchPlaceholder: "Search by name...",
      allCategories: "All categories",
      allManufacturers: "All manufacturers",
      sortName: "Sort: Name",
      sortDensity: "Sort: Density ρ",
      sortLambda: "Sort: λ",
      sortMu: "Sort: μ",
      sortWcap: "Sort: wcap",
      sortDirectionTitle: "Toggle ascending/descending",
      sortAsc: "▲ Asc",
      sortDesc: "▼ Desc",
      colSymbol: "Symbol",
      colName: "Name",
      colManufacturer: "Manufacturer",
      customBadge: "custom",
      edit: "Edit",
      delete: "Delete",
      deleteConfirm: "Delete this custom material?",
      variableSdTitle: (min: number, max: number) => `Variable Sd: ${min}–${max} m (data sheet)`,
      navHint: "Choose a category, or search by name above.",
      sourceAssumed: "Not in the data sheet: assumed value",
      sourceDerived: "Not in the data sheet: derived from its values",
      dataNotes: {
        lambdaBelowAir: (lambda: number) => `the data sheet's λ = ${lambda} W/(m·K) is below still air: not used`,
        densityDerived: "density derived from the data sheet values",
      },
    },
    materialModal: {
      titleMembrane: "Rothoblaas membrane",
      titleNew: "New material",
      titleEditCopy: "Edit material (saved as a custom copy)",
      titleEdit: "Edit material",
      unnamed: "Unnamed material",
      membraneLockedHint: "Rothoblaas membranes are shown with their datasheet values and cannot be edited or copied.",
      builtinHint: "Built-in materials cannot be changed: saving creates a custom copy, and the layers of this stratigraphy that use this material switch to the copy.",
      technicalDataSheet: "Technical data sheet ↗",
      productPage: "Product page ↗",
      checked: (date: string) => `checked ${date}`,
      laboratoryNotStated: "laboratory not stated",
      propertiesLabel: "Material properties",
      tabIdentity: "Identity",
      tabGraphics: "Graphics",
      tabThermal: "Thermal",
      tabMoisture: "Moisture",
      tabAcoustic: "Acoustic",
      name: "Name",
      manufacturer: "Manufacturer",
      optional: "optional",
      category: "Category",
      categoryPlaceholder: "e.g. insulation",
      thickness: "Thickness",
      thicknessVariable: "Variable thickness",
      thicknessKnown: "Known thicknesses (from manufacturer)",
      reference: "Reference",
      referencePlaceholder: "e.g. 100",
      min: "Min",
      max: "Max",
      knownThicknesses: "Known thicknesses",
      knownThicknessesHint: ", comma-separated - the first is the reference",
      knownThicknessesPlaceholder: "e.g. 40, 60, 80, 100, 120",
      source: "Source",
      laboratory: "Laboratory",
      date: "Date",
      file: "File",
      sectionSymbolCaption: "Section symbol (cut pattern):",
      automaticFromCategory: " - automatic, from the category",
      sectionSymbol: "Section symbol",
      auto: "Auto",
      hatches: {
        "": "Auto (from category)",
        concrete: "Concrete / screed (stipple)",
        masonry: "Masonry, solid (brick coursing)",
        "masonry-hollow": "Masonry, hollow (brick coursing + voids)",
        render: "Render / plaster / mortar (fine dots)",
        board: "Rigid board, gypsum-like (plain)",
        wool: "Fibrous insulation (loops)",
        foam: "Rigid foam insulation (circles)",
        fiberboard: "Wood-fiber / granular board (speckle)",
        wood: "Wood (grain lines)",
        membrane: "Membrane / foil (bold line)",
        stone: "Natural stone (irregular crazing)",
        metal: "Metal (fine diagonal)",
        generic: "Generic (diagonal hatch)",
      },
      thermalHint: "One thermal conductivity per material (no λ(w) in the web version).",
      sdValue: "Sd-value",
      vapourResistanceZ: "Vapour resistance Z",
      variableSd: "Variable Sd",
      variableSdRange: (min: number, max: number) => `: ${min}–${max} m on the data sheet`,
      variableSdCurve: " (measured Sd(RH) curve: see Hygrothermal properties)",
      admissibleCondensate: "Admissible condensate",
      admissibleCondensateTitle: "Admissible condensate of the monthly Glaser analysis (legacy table frmInfoVAP)",
      condDefault: (label: string) => `Default: ${label}`,
      vapourNote: (thickness: string, unit: string, mu: string) => `For ${thickness} ${unit}: μ = ${mu}`,
      vapourNoteNeedsThickness: "Needs a thickness (reference or known thicknesses).",
      porosity: "Porosity",
      saturation: "Saturation",
      capillary: "Capillary",
      waterAbsorption: "Water absorption",
      airflow: "Airflow",
      acousticHint: "Optional: used by the acoustic tools, not by the hygrothermal calculations.",
      errorMembrane: "Membranes are Rothoblaas products only: a custom material cannot be in the Membrane category.",
      errorSdThickness: (label: string) => `To enter the vapour diffusion as ${label}, give the material a thickness first (reference thickness, or known thicknesses).`,
      errorSdPositive: (label: string) => `${label} must be a number greater than 0.`,
      save: "Save material",
      cancel: "Cancel",
      close: "Close",
      density: "Density ρ",
      lambdaDry: "Dry thermal conductivity λ",
      cp: "Specific heat capacity c",
      mu: "Vapour resistance factor μ",
      wCap: "Moisture capacity wcap",
      emissivity: "Emissivity ε",
    },
    materialHygro: {
      title: "Hygrothermal properties",
      showTitle: "Show the hygrothermal properties",
      hideTitle: "Hide the hygrothermal properties",
      tabSorption: "Sorption",
      tabSuction: "Suction",
      thickness: "Thickness",
      sdNamePermeance: "permeance",
      sdNameVapourResistance: "vapour resistance",
      dryCup: "Dry cup [US perm]",
      muDry: "μ dry [-]",
      w80ClassAssumed: (cls: string) => `No measured sorption curve: class value “${cls}” of the quasi-dynamic model`,
      noSorptionCurve: "No measured sorption curve",
      porosity: "Porosity [%]",
      sdChartTitle: (thickness: string, unit: string) => `Sd against relative humidity (measured, ${thickness} ${unit})`,
      sorptionMeasured: "Sorption isotherm (measured)",
      sorptionTable: "Sorption isotherm (material table)",
      sorptionLinear: "Water content: straight line of the quasi-dynamic model",
      suctionTitle: "Suction curve (moisture storage)",
      klTitle: "Liquid conductivity k(θ)",
      muwTitle: "Vapour resistance factor μ(w)",
      membraneVariableNote: "Variable Sd on the data sheet, but no measured Sd(RH) curve: the calculations use the tightest state (fixed Sd).",
      membraneFixedNote: "Membrane: non-hygroscopic film (w = 0), Sd independent of the humidity.",
      footSdCurve: "Sd(φ): measured to EN ISO 12572, read log-linearly between the points (as in the 1D, monthly and quasi-dynamic analyses); the 2D analysis uses the tightest state.",
      footTable: "w(φ) and μ(w) as the 2D and quasi-dynamic analyses use them.",
      footTableMeasured: "w(φ) and μ(w) as the 2D and quasi-dynamic analyses use them (sorption from the storage function by Kelvin at 20 °C).",
      footNoCurve: (cls: string) => `No measured sorption curve: the 2D analysis uses no water content; the quasi-dynamic analysis stores w = ξ·φ (class “${cls}”).`,
      footLambda: "λ is one value (no λ(w)).",
      assumedNoData: "Assumed: no measured data for this material",
      assumedTag: "assumed",
      sorptionClasses: {
        membrane: "Membrane",
        woodBased: "Wood, wood-based and natural fibres",
        gypsum: "Gypsum boards and plasters",
        nonHygroInsulation: "Non-hygroscopic insulation",
        aac: "Autoclaved aerated concrete",
        lightweightConcrete: "Lightweight concrete",
        concrete: "Concrete",
        concreteBlock: "Concrete blocks",
        calciumSilicate: "Calcium silicate",
        firedClayBrick: "Fired clay brick",
        naturalStone: "Natural stone",
        metal: "Metal",
        mineralRender: "Mineral renders, mortars and boards",
        unclassified: "Unclassified",
      },
    },
    calculation: {
      panelDesc: "Settings of the static analyses: surface resistances and atmospheric pressure. The surface resistances are also set on the stratigraphy drawing.",
      advancedSettings: "Advanced settings",
      showHide: "show / hide",
      atmosphericPressure: "Atmospheric pressure [Pa]",
      modelsHint: "Saturation pressure model: Magnus-Tetens approximation (fixed). Diffusion model: steady-state Glaser method / 2D vapour diffusion at the design condition (fixed). Condensation model: vapor flux discontinuity at the condensation plane (1D) / local RH>100% flag (2D) (fixed). These are noted here for transparency; they are not yet user-swappable. The 2D Hygrothermal solver's own mesh/iteration/tolerance settings moved to Settings (the gear icon, top right) → 2D solver tab.",
      diagnosticsTitle: "Solver diagnostics (2D mesh)",
      diagnosticsIntro: "Full detail on how the last 2D Hygrothermal result above was actually produced - method, mesh size, convergence, residual history - for the thermal and vapour-pressure field solves separately (they can differ a lot: the vapour field is usually far worse conditioned). Solver convergence (this card) and engineering accuracy (the mesh-convergence table below, when auto mesh is on) are two distinct questions - a fully converged solve on a coarse mesh can still be an inaccurate answer.",
      thermal: "Thermal",
      vapourPressure: "Vapour pressure",
      vapour: "Vapour",
      addLayerForDiagnostics: "Add at least one layer to see solver diagnostics here.",
      noDiagnosticsYet: "No diagnostics yet.",
      methodUsed: "Method used",
      autoSuffix: " (auto)",
      solverAuto: "automatic",
      meshCells: "Mesh cells",
      iterationsCycles: "Iterations / cycles",
      residualInitial: "Residual (initial)",
      residualFinal: "Residual (final)",
      reductionFactor: "Reduction factor",
      solverFlux: "Solver flux q",
      energyBalanceError: "Energy balance error",
      spdSystem: "SPD system",
      conductivityContrast: "Conductivity contrast",
      finalStatus: "Final status",
      yes: "Yes",
      no: "No",
      residualChartTitle: "Residual vs. iteration (log scale)",
      compareSolvers: "Compare CG vs V-cycle on this problem",
      runCalculationFirst: "Run a calculation first.",
      solvingBothMethods: "Solving with both methods…",
      colMethod: "Method",
      colQThermal: "q (thermal)",
      colIterations: "Iterations",
      colResidual: "Residual",
      colStatus: "Status",
      colTime: "Time",
      meshLadder: "Mesh convergence ladder (this run):",
      colRows: "Rows",
      colSolverConverged: "Solver converged",
      colAccepted: "Accepted",
    },
    charts: {
      noDataYet: "No data yet",
      legendToggle: (label: string) => `${label} (click to hide or show)`,
    },
    search: {
      clearSearch: "Clear the search",
      filters: "Filters",
    },
    monthly: {
      title: "Monthly Glaser",
      panelDesc: "Quasi-static monthly analysis (1D), the method of the legacy Thermal application: Glaser at every interface month by month, with condensate accumulating and evaporating from one month to the next, fed by the monthly means of the climate of the site. It runs with every calculation, next to the Simplified and 2D mesh results.",
      inputsTitle: "Calculation inputs",
      summaryClass: (cls: number) => `humidity class ${cls}`,
      summaryConstant: (ti: string, unit: string, rhi: string) => `Ti ${ti} ${unit}, RHi ${rhi} %`,
      climateOfSite: "climate of the site",
      summaryDrying: "drying time",
      outsideClimate: "Outside climate",
      outsideClimateTip: "The climate of the site chosen in the Data tab (a site on the map or an EPW file), the same as the other analyses. Monthly means of the hourly values: Te = mean of T, pv,e = mean of RH·psat(T).",
      loadingClimate: "Loading the climate…",
      change: "change →",
      noClimate: "No climate yet.",
      insideClimate: "Inside climate",
      tipIso13788: "Humidity classes, UNI EN ISO 13788: Δp = 270 · class · clamp((20 − Te) / 20; 0; 1) Pa, pi = pe + 1.10 · Δp, Ti = 20 °C.",
      tipEn15026: "EN 15026 Annex D: hour by hour from the 24 h gliding mean of the outside temperature of the EPW file, then averaged per month.",
      tipConstant: "Constant inside temperature and relative humidity in every month.",
      insideModel: "Inside climate model",
      ashraeOption: "ASHRAE 160 - not available yet: its values are being verified",
      modelIso13788: "Humidity classes (UNI EN ISO 13788)",
      untilAshrae: " - until ASHRAE 160 is available",
      modelConstant: "Constant Ti and RHi",
      modelEn15026: "EN 15026 Annex D",
      humidityClass: "Humidity class",
      classOption: (cls: number) => `Class ${cls}`,
      moistureLoad: "Moisture load",
      loads: {
        "normal+5": "Normal moisture load + 5 % (design case)",
        normal: "Normal moisture load",
        low: "Low moisture load",
        high: "High moisture load",
      },
      dryingTime: "Drying time",
      dryingTip: "Months needed to dry 1 kg/m² of water placed in the middle of the chosen layer, from October, with the monthly climate. Method of the whole analysis: UNI EN ISO 13788, monthly Glaser with accumulation.",
      dryingLayer: "Layer of the drying run",
      chooseLayer: "Choose the layer…",
      monthlyClimate: "Monthly climate",
      monthlyClimateUsed: "Monthly climate and inside climate used",
      monthlyClimateTip: (utcOffset: string) => `Monthly means of the hourly values in local time (UTC${utcOffset}): Te = mean of T; pe = mean of RH·psat(T); RHe = pe / psat(Te).`,
      statusEmpty: "The stratigraphy is empty: add layers in the Stratigraphy panel first.",
      statusInternalWall: "No outdoor side (INSIDE on both sides: an internal wall): the monthly analysis needs one outside and one inside climate.",
      statusBothOutside: "No outdoor side (OUTSIDE on both sides): the monthly analysis needs one outside and one inside climate.",
      statusChooseClimate: "Choose the climate of the site in the Data tab (a site on the map or an EPW file).",
      failed: "The monthly analysis failed.",
      verified: "Verified",
      notVerified: (n: number) => `Not verified (${n} check${n > 1 ? "s" : ""})`,
      checkResidual: "annual balance",
      checkAdmissible: "admissible condensate",
      checkMould: "mould (f_Rsi)",
      allPass: "No condensate left at the end of the cycle, every accumulated quantity within its admissible value, f_{Rsi} above f_{Rsi,min}.",
      allPassBoth: "No condensate left at the end of the cycle, every accumulated quantity within its admissible value, f_{Rsi} above f_{Rsi,min}, in both sections.",
      settingsUsed: "Settings used",
      startOfCycle: "Start of the cycle",
      subLayers: "Sub-layers",
      subLayersValue: (rMax: string) => `every layer with R > ${rMax} m²K/W split into equal sub-layers`,
      humidityDependentSd: "Humidity-dependent Sd",
      condensationTest: "Condensation test",
      surfaces: "Surfaces",
      surfacesValue: (rsi: string, rse: string) => `R_{si} = ${rsi} m²K/W, R_{se} = ${rse} m²K/W (air layers by EN ISO 6946, as in the other analyses)`,
      sections: "Sections",
      sectionsValue: (list: string) => `${list} - separate 1D sections, no combined method`,
      admissibleCondensate: "Admissible condensate",
      mould: "Mould",
      uValue: (section: string) => `U-value (${section})`,
      interfaces: (n: number) => `${n} interfaces`,
      maxAccumulated: "Max. accumulated condensate",
      interfacesCondensing: (n: number) => `${n} interface(s)`,
      noCondensation: "no condensation",
      leftAtEnd: "Left at the end of the cycle",
      mustBeZero: "must be 0",
      mouldStat: "Mould: f_{Rsi} / f_{Rsi,min}",
      criticalMonth: (month: string) => `critical month ${month}`,
      noMonthAtRisk: "no month at risk",
      months: "months",
      dryingTag: (layer: string) => `1 kg/m² in ${layer}, from October`,
      monthByMonth: (section: string) => `Month by month - ${section}`,
      month: "Month",
      maTotal: "M_{a} total",
      noInterfaceCondenses: "No interface condenses in any month.",
      monthsNote: (unit: string) => `G_{c}: condensation (+) or evaporation (−) of the month; M_{a}: accumulated at the end of the month [${unit}]. Interfaces numbered from the outer surface.`,
      admissibleTitle: (section: string) => `Admissible condensate - ${section}`,
      interface: "Interface",
      maxMa: (unit: string) => `Max. M_{a} [${unit}]`,
      admissibleColumn: (unit: string) => `Admissible [${unit}]`,
      materialAndRule: "Material and rule",
      notAssessed: "not assessed",
      within: "within",
      exceeded: "⚠ exceeded",
      admissibleNote: "At an interface between two materials, the lower of their admissible quantities.",
      glaserDiagram: "Glaser diagram",
      abscissa: "Abscissa",
      thickness: "Thickness",
      diagramMonth: "Month of the diagram",
      condensationOverCycle: "Condensation over the cycle",
      condensationNote: (n: number, months: string, maxG: string, unit: string, at: string) => `⚠ Condensate forms in ${n} month(s) (${months}): at most ${maxG} ${unit} at ${at};`,
      evaporatedBy: (month: string) => `it has evaporated by the end of ${month}.`,
      evaporatesWithin: "it evaporates within the cycle.",
      notAllEvaporates: "it does not all evaporate within the cycle.",
      mouldTitle: (section: string) => `Mould - UNI EN ISO 13788 - ${section}`,
      mouldNote: (rh: string, rsi: string, fRsi: string, rMin: string) => `RH_{si} ≤ ${rh} %, R_{si} = ${rsi} m²K/W: f_{Rsi} of the section = ${fRsi}${rMin}. Months with T_{i} ≤ T_{e}: not assessed (—); f_{Rsi,min} ≤ 0: no requirement in that month.`,
      mouldRmin: (value: string, unit: string) => `; minimum thermal resistance of the layers for the critical month: ${value} ${unit}`,
      curvesShown: "Curves shown (click to hide or show)",
      legendP: "vapour pressure p",
      legendPsat: "saturation pressure p_{sat} (● layer face, ○ inside a layer)",
      legendWet: "interface holding condensate",
      glaserAria: (month: string) => `Glaser diagram, ${month}`,
      depthAxis: (unit: string) => `depth from the inside [${unit}]`,
      sdAxisSi: "Sd from the inside [m]",
      sdAxisUk: "vapour resistance from the inside [MN·s/g]",
      sdAxisImperial: "vapour resistance from the inside [rep = 1/perm]",
      inside: "inside",
      outside: "outside",
      pressureAxis: "pressure [Pa]",
      surface: "surface",
      node: (k: number) => `node ${k}`,
      psatTip: (point: string, psat: number) => `${point}: p_sat ${psat} Pa`,
      legendCondensed: "condensed in the month",
      legendEvaporated: "evaporated in the month",
      legendAccumulated: "accumulated at the end of the month",
      noCondensateAnyMonth: "no condensate in any month",
      accumulationAria: "Water condensed, evaporated and accumulated per month",
      condensed: "condensed",
      evaporated: "evaporated",
      accumulated: "accumulated",
      sectionLabels: {
        bay: "Bay (between the studs)",
        stud: "Stud",
        whole: "Whole wall",
      },
      interfaceOuter: "outer surface",
      interfaceInner: (material: string) => `${material} | inner surface`,
      interfaceWithin: (material: string) => `within ${material}`,
      insideIso13788: (cls: number, fs: string, ti: number) =>
        `UNI EN ISO 13788 humidity class ${cls}${cls === 3 ? " (DM 26/06/2015)" : ""}: Δp = 270·${cls}·clamp((20 − Te)/20; 0; 1) Pa, pi = pe + ${fs}·Δp (at most psat(Ti)), Ti = ${ti} °C (Te when warmer)`,
      insideConstant: (ti: number, rhi: number) => `Constant: Ti = ${ti} °C, RHi = ${rhi} %`,
      insideEn15026: (load: string) => `EN 15026 Annex D, ${load}: hourly values from the 24 h gliding mean of the outside temperature, averaged per month`,
      startNotes: {
        OCTOBER_LEGACY: "October, as the legacy method",
        NONE_CONDENSES: "no month condenses from a dry start: cycle from October",
        ALL_CONDENSE: "every month condenses from a dry start: cycle from October",
        FIRST_CONDENSING: (month: string) => `first month that condenses after a month without condensation: ${month}`,
      },
      failureMould: (fRsi: string, fRsiMin: string, month: string) => `f_Rsi = ${fRsi} below f_Rsi,min = ${fRsiMin} (critical month: ${month})`,
      failureResidual: (g: string, at: string) => `condensate left at the end of the cycle: ${g} g/m² at ${at}`,
      failureAdmissible: (g: string, at: string, admissible: string, material: string, rule: string) =>
        `${g} g/m² at ${at}, above the admissible ${admissible} g/m² (${material}: ${rule})`,
      warningPicardCapped: (section: string, passes: number, tolM: number) =>
        `${section}: the humidity-dependent Sd iteration reached ${passes} passes in at least one month without settling within ${tolM} m; the values of the last pass are shown.`,
      warningOutsideRhAbove100: "In at least one month the mean outside vapour pressure is above the saturation pressure of the mean temperature (RHe > 100 %); it is used as it is.",
      presetLabels: {
        legacy: "Legacy Thermal",
        iso13788: "UNI EN ISO 13788 (default)",
      },
      startRules: {
        legacy: "October (legacy)",
        iso13788: "first month that condenses after a month without condensation (UNI EN ISO 13788), per section",
      },
      sdRules: {
        legacy: "humidity-dependent Sd from the RH of the inner face of its sub-layer (legacy rule)",
        iso13788: "humidity-dependent Sd from the mean RH of the two faces of its sub-layer",
      },
      picardRule: (tolM: number, max: number) => `until the sum of |ΔSd| ≤ ${tolM} m, at most ${max} passes`,
      pTolExact: "exact p ≥ psat (legacy)",
      pTolRule: (pTol: number) => `p ≥ psat·(1 − ${pTol})`,
      mouldRule: (rh: number, rsi: number) => `UNI EN ISO 13788: f_Rsi,min per month with RH_si ≤ ${rh} %, R_si = ${rsi} m²K/W`,
      admissibleRule: "legacy table frmInfoVAP per material; at an interface the lower admissible quantity of the materials that touch it; \"30 d\" read as 30·ρ·d",
    },
    dynamic: {
      // Inputs tab
      desc: "Hour by hour over several years with real climate data, in the framework of EN 15026: temperatures, relative humidity, condensation and drying of the current stratigraphy.",
      preview: "Preview",
      why: "Why \"quasi-dynamic\"?",
      hide: "hide",
      settingsTitle: "Calculation settings (Settings → Quasi-dynamic)",
      whyDynamicTerm: "Dynamic",
      whyDynamic: ": time is simulated hour by hour, so the layers store and release heat and moisture - winter wetting and summer drying, construction moisture drying out over the years. This is what the steady-state calculations (Glaser, 2D, monthly) cannot show.",
      whyQuasiTerm: "Quasi",
      whyQuasi: ": the material properties are kept constant. λ, ρc and µ do not change with moisture; moisture storage is linear, w = ξ·φ with ξ the secant of the sorption curve at 80 % RH; water moves only as vapour (no liquid/capillary transport). Latent heat and the heat capacity of the water content are optional (Effects included in the model, below). Humidity-dependent membranes (CLIMA CONTROL) do change their Sd with RH, and condensate is tracked as it forms and dries.",
      whyFullBefore: "The ",
      whyFullTerm: "full dynamic",
      whyFull: " model of EN 15026 (as in WUFI) adds moisture-dependent storage, permeability, liquid conduction and λ(w), and the vapour and liquid enthalpy in the energy balance; it needs complete hygric material data and is validated against the standard's Annex B benchmark. It is the next step. The quasi-dynamic model is reliable for assemblies governed by vapour diffusion (lightweight timber and insulated assemblies with membranes) and is a screening tool, not a certification.",
      tabsLabel: "Quasi-dynamic analysis",
      tabInputs: "Inputs",
      tabResults: "Results",
      runFirst: "Run the quasi-dynamic analysis first",
      outerSurface: "Outer surface",
      absorptance: "Solar absorptance",
      emissivity: "Emissivity",
      insideLoad: "Inside climate (EN 15026 Annex D)",
      loads: {
        "normal+5": "Normal moisture load + 5 % (design case)",
        normal: "Normal moisture load",
        low: "Low moisture load",
        high: "High moisture load",
      },
      ashrae160Note: "ASHRAE 160 not available yet (its values are being verified): the UNI / EN model is used.",
      modelAndRun: "Model and run",
      run: "Run",
      years: "Years",
      woodCriterion: "Wood criterion [M.-%]",
      woodCriterionTitle: "Fixed: moisture content of wood-based materials ≤ 16 M.-%",
      construction: "Construction moisture (initial RH 95 %, else 80 %)",
      stepAuto: (step: string) => `automatic (${step})`,
      solver2d: (method: string, tolerance: string, rows: number) => `${method} · tolerance ${tolerance} · ${rows} rows`,
      direct1d: "direct 1D solver",
      calcSummary: (step: string, solver: string) => `Calculation: step ${step} · ${solver} ·`,
      changeInSettings: "change in Settings ⚙",
      runButton: (mode: string) => `Run quasi-dynamic analysis (${mode})`,
      calculating: "Calculating…",
      hint2d: "2D mesh mode (header): the studs and the insulation are one grid, coupled. About 10-20 s per simulated year at 20-40 rows; finer grids take longer, and the server stops a calculation after 300 s.",
      hint1d: "Simplified mode (header): one 1D column through the layers; studs are not included (switch the header to 2D mesh for them).",
      emptyStratigraphy: "The stratigraphy is empty: add layers in the Stratigraphy panel first.",
      noClimate: "Choose the climate first: Data tab, Climate of the site (a site on the map or an EPW file).",
      noOutdoorSide: "No outdoor side: the quasi-dynamic analysis needs one OUTSIDE and one INSIDE side on the drawing (Stratigraphy tab).",
      analysisFailed: "The analysis failed.",
      cancelled: "The calculation was cancelled; the previous result (if any) is still shown.",
      unreadable: "The server reply could not be read.",
      unreachable: "The server could not be reached.",
      timeout: "The calculation took longer than the server allows (300 s). Simulate fewer years, use a coarser 2D grid or a longer time step.",
      statusError: (status: number) => `The server replied with status ${status}.`,
      stopped: "The calculation stopped before the end (the server may have reached its time limit of 300 s). Simulate fewer years, use a coarser 2D grid or a longer time step.",
      lastResult: (mode: string, years: number, source: string) => `Last result: ${mode}, ${years} year(s), ${source} ·`,
      openResults: "open Results →",
      mode2d: "2D mesh",
      mode1d: "1D",
      modeSimplified: "Simplified",
      modeSimplified1d: "Simplified (1D)",
      // effects included in the model
      effectsLegend: "Effects included in the model",
      effects: {
        dynamic: {
          label: "Dynamic system",
          hint: "Heat and moisture storage (the ∂/∂t terms). Off: every time step is the steady state of its climate (hour-by-hour Glaser); only condensate accumulates.",
        },
        waterHeatCapacity: {
          label: "Heat capacity of the water content",
          hint: "ρc + w·c_w, with c_w = 4190 J/(kg·K) and the water content w of the previous time step.",
        },
        latentHeat: {
          label: "Latent heat",
          hint: "Heat released where vapour is sorbed or condenses and taken where it evaporates: h_v · net vapour inflow of each cell, h_v = 2500 kJ/kg (EN 15026:2007), from the vapour field of the previous time step.",
        },
        variableSd: {
          label: "Variable vapour resistance µ(w), Sd(RH)",
          hint: "Sd(RH) of the membranes with a measured curve (CLIMA CONTROL 80 and 160) and µ(w) of the materials with a moisture-dependent µ curve, updated every time step. Off: their fixed values.",
        },
        rain: {
          label: "Rain",
          hint: "Driving rain on the outer surface, from the rain and the wind of the climate; the water that enters leaves by evaporation (no capillary transport).",
        },
        conductivity: {
          label: "Variable conductivity λ(w)",
          hint: "Needs moisture-dependent λ data for each material.",
        },
        capillarity: {
          label: "Capillarity",
          hint: "Liquid transport: needs the full sorption curve and liquid transport coefficients of each material.",
        },
      },
      availableSoon: "available soon",
      needsDynamic: "Needs the dynamic system (storage).",
      noRain: "The chosen climate has no rain (PVGIS has none): choose ERA5 (Open-Meteo), a TMYx station or an EPW file with rain.",
      rainSettings: "Rain settings",
      waterEntering: "Water entering the wall",
      rainModeTitle: "Set by the standards of the menus (Settings → General)",
      rainAbsorb: "Absorbing façade (EN 15026): 70 % into the outer layer",
      rainDrain: "Cladding with drainage (ASHRAE 160): 1 % behind it",
      rainAbsorbHint: "Render, exposed masonry: 70 % of the driving rain enters the outermost cells of the outer layer as liquid water, up to the material’s capillary saturation (w_cap); the rest runs off.",
      rainDrainHint: "Siding or panels on a drained cavity: 1 % of the driving rain is deposited in the outer part of the first layer behind the cladding (the ASHRAE 160 design leak).",
      exposure: "Exposure (driving-rain coefficient R2)",
      exposures: {
        low: "Low building (up to 10 m)",
        "tall-low": "Tall building, lower part",
        "tall-mid": "Tall building, middle part",
        "tall-top": "Tall building, upper part",
      },
      exposureOption: (label: string, r2: number) => `${label} — R2 = ${r2} s/m`,
      rainFormula: "Rain on the wall = R2 × wind speed × horizontal rain × cos(angle between the wind and the façade), from the windward side only; roofs also get the horizontal rain × cos(tilt).",
      // orientation of the outer surface
      directions: ["N", "NE", "E", "SE", "S", "SW", "W", "NW"],
      orientLabel: (tilt: number, direction: string, azimuth: number) => `Outer surface: tilt ${tilt}°, facing ${direction} ${azimuth}°. Drag or use the arrow keys to turn it.`,
      orientValue: (tilt: number, azimuth: number) => `tilt ${tilt}°, facing ${azimuth}°`,
      outside: "outside",
      inside: "inside",
      turning: "↔ turning (facing)",
      tilting: "↕ tilting",
      tiltTitle: (now: string) => `Tilt: 0° flat roof, 90° vertical wall (now: ${now})`,
      flatRoof: "flat roof",
      wall: "wall",
      roof: "roof",
      azimuthTitle: (now: string) => `Facing: direction of the outer surface, degrees from north, clockwise (now: ${now})`,
      anyFlat: "any, flat",
      orientHint: (wallMinTilt: number) => `Drag the wall: left/right turns it (facing), down raises it towards vertical, up lowers it - one at a time, chosen by the first movement of each drag. Arrow keys: 1° steps, Shift 5°. Tilt ≥ ${wallMinTilt}° is treated as a wall, below as a roof.`,
      // progress window
      running: "Quasi-dynamic analysis running",
      cancelling: "Cancelling…",
      sending: "Sending the stratigraphy to the server",
      comparison: " (comparison)",
      simulatedUpTo: (day: string, years: number) => `simulated up to ${day} of ${years} year(s)`,
      elapsed: (time: string) => `elapsed ${time}`,
      timeLeft: (time: string) => ` · about ${time} left`,
      // Results tab
      resultsDesc: "Result of the last run. Drag across a time chart to select a period, double-click it for the whole run.",
      noResult: "No result yet: run the analysis in Quasi-dynamic analysis.",
      checksNotSatisfied: (failed: number, total: number) => `⚠ ${failed} of ${total} checks not satisfied`,
      allSatisfied: "all checks satisfied",
      noCheckApplicable: "no check applicable",
      verdictLine: (mode: string, years: number, source: string) => `${mode} · ${years} year(s) · ${source} · screening, not certification · click a check for its details`,
      modeMismatch: (resultMode: string, headerMode: string) => `This result was computed in ${resultMode} mode; the header is now on ${headerMode}. Run again to update it.`,
      notApplicable: "n/a",
      satisfied: "satisfied",
      notSatisfied: "not satisfied",
      notSatisfiedWarn: "⚠ not satisfied",
      sectionsLabel: "Sections",
      secRun: "Run and climate data",
      secPeriod: "Results in the period",
      secPeriodTitle: "Results in the selected period",
      secConv: "Convergence",
      secConvTitle: "Convergence in the selected period",
      secCharts: "Charts",
      secChartsTitle: "Charts in the selected period",
      secFields: "2D fields",
      secFieldsTitle: "2D fields at notable hours",
      secHourly: "Hour by hour",
      secHourlyTitle: "Hour by hour (last simulated year)",
      secLayers: "Layers of the model",
      resultBadge: (mode: string) => `${mode} result`,
      source: "Source",
      sourceDbs: (radiation: string, weather: string) => ` (radiation ${radiation}, weather ${weather})`,
      location: "Location",
      period: "Period",
      insideClimate: "Inside climate",
      attribution: "Attribution",
      runLine: (years: number, month: string, step: string, grid: string, cells: number, seconds: string, mass: string, energy: string) =>
        `Run: ${years} years from 1 ${month}, time step ${step}, ${grid}${cells} cells; computed in ${seconds} s; balance residuals: mass ${mass}, energy ${energy}.`,
      gridText: (rows: number, columns: number) => `2D grid ${rows} × ${columns} = `,
      rhInterfaces: "Relative humidity at the interfaces (daily mean)",
      rhAxis: "RH [%]",
      tInterfaces: (unit: string) => `Temperature at the interfaces (daily mean) [${unit}]`,
      outsideAir: "outside air",
      membraneSd: "Membrane Sd (daily mean, band = daily range)",
      condensateTitle: "Condensate (daily mean) [g/m²]",
      total: "total",
      waterTitle: "Total water in the component [kg/m²]",
      totalWater: "total water",
      rainTitle: "Rain on the outer surface and entering the wall (per day) [kg/m²]",
      rainAxis: "kg/m² per day",
      rainOnSurface: "driving rain on the surface",
      rainEntering: "entering the wall",
      woodTitle: "Moisture content of wood-based layers [M.-%]",
      rhMapTitle: "Relative humidity through the component, day by day",
      fieldColdest: "At the coldest hour of the run",
      fieldPeak: "At the peak of condensate",
      inPeriod: "in the period",
      outsidePeriod: "outside the period",
      fieldLine: (date: string, hour: string, te: string, unit: string) => `${date}, ${hour}:00 (hour of the run) · outside ${te} ${unit}`,
      fieldCondensate: (value: string) => ` · condensate ${value} g/m²`,
      noCondensateRun: "No condensate in the whole run.",
      hourlyTitle: "Hour by hour: RH at the 4 most humid interfaces of the last year",
      hourlyLastYearOnly: (from: string) => `The hour-by-hour series cover the last simulated year only (from ${from}).`,
      selectLastYear: "Select the last year",
      membraneVariable: (min: number, max: number) => `membrane, Sd ${min}–${max} m by RH`,
      membraneFixed: (sd: string) => `membrane, Sd ${sd} m`,
      xiText: (xi: string, source: string) => `ξ = ${xi} kg/m³ (${source})`,
      // period filter
      dayLabel: (day: number, month: string, year: number) => `${day} ${month}, year ${year}`,
      yearN: (year: number) => `Year ${year}`,
      days: "days",
      presetPeriods: "Preset periods",
      wholeRun: "Whole run",
      season: (season: string, from: string, to: string, year: number) => `${season} (${from}–${to}), year ${year}`,
      winter: "Winter",
      summer: "Summer",
      from: "From",
      to: "to",
      periodHint: "Drag across the strip or any chart below to select a period; double-click a chart for the whole run.",
      day: "Day",
      month: "Month",
      simulatedYear: "Simulated year",
      overviewLabel: "Selected period within the whole run: drag to change",
      overviewTitle: "outside air temperature, whole run",
      // results in the period
      surfaceTmin: "Interior surface, min. temperature",
      fRsiMin: "Temperature factor f_Rsi, min.",
      fRsiTag: (day: string, limit: number) => `${day} · limit ${limit}`,
      noHour10K: "no hour with Ti − Te ≥ 10 K",
      condMax: "Condensate, max.",
      condTag: (day: string, days: number) => `${day} · ${days} day(s) with condensate`,
      noneInPeriod: "none in the period",
      condLastDay: "Condensate on the last day",
      driedOut: "dried out",
      stillPresent: "still present",
      woodMax: "Wood moisture content, max.",
      rhMax: "RH at an interface, max. (hourly)",
      rainIn: "Rain entering the wall",
      rainTag: (onSurface: string) => `of ${onSurface} kg/m² of driving rain on the surface`,
      waterChange: "Water in the component, change",
      waterTag: (from: string, to: string, trend: string) => `${from} → ${to} kg/m² · ${trend}`,
      netWetting: "net wetting",
      netDrying: "net drying",
      noNetChange: "no net change",
      outsideMean: "Outside air, mean",
      outsideTag: (rh: string, min: string, max: string, unit: string) => `RH ${rh} % · ${min} to ${max} ${unit} (daily means)`,
      checksInPeriod: "Checks in the period",
      screening: "screening, not certification",
      checkCondensate: (limit: number) => `Condensate ≤ ${limit} g/m² throughout the period`,
      checkCondensateDetail: (max: string, day: string, days: number, end: string) => `max ${max} g/m² on ${day}; ${days} day(s) with condensate; ${end} g/m² on the last day`,
      noCondensatePeriod: "No condensate in the period.",
      checkWood: (limit: number) => `Wood-based materials: moisture content ≤ ${limit} M.-%`,
      checkWoodDetail: (name: string, max: string, day: string, daysOver: number) => `${name}: max ${max} M.-% on ${day}${daysOver ? `, ${daysOver} day(s) above` : ""}`,
      noWood: "No wood-based material.",
      checkFrsi: (limit: number) => `Temperature factor f_Rsi ≥ ${limit} (hours with Ti − Te ≥ 10 K)`,
      checkFrsiDetail: (value: string, day: string) => `minimum f_Rsi ${value} on ${day}`,
      noHour10KPeriod: "No hour with Ti − Te ≥ 10 K in the period.",
      accumulationNote: "The year-on-year accumulation check needs whole years: see the end-of-run checks above.",
      membranesWood: "Membranes and wood-based materials",
      colMembrane: "Membrane",
      colSdMean: "Sd mean [m]",
      colSdMin: "Sd min [m]",
      colSdMax: "Sd max [m]",
      byRh: "by RH",
      fixedSd: "fixed",
      noMembrane: "No membrane in the stratigraphy.",
      colWood: "Wood-based material",
      colUMean: "u mean [M.-%]",
      colUMax: "u max [M.-%]",
      colDaysOver: (limit: number) => `Days > ${limit} M.-%`,
      woodNote: "u = daily peak of the wettest cell of the material.",
      layersInPeriod: "Layers in the period",
      colLayer: "Layer",
      colTmin: (unit: string) => `T min [${unit}]`,
      colTmean: (unit: string) => `T mean [${unit}]`,
      colTmax: (unit: string) => `T max [${unit}]`,
      colRhMean: "RH mean [%]",
      colRhMax: "RH max [%]",
      colWaterMean: "Water mean [kg/m²]",
      colCondMax: "Condensate max [g/m²]",
      membraneFace: "membrane: a face between the cells in 1D (see its Sd and the interfaces on its two sides)",
      layersNote: "Layers from the inside outwards. Values of each layer are volume averages per day; minimum and maximum are of those daily means.",
      interfacesInPeriod: "Interfaces in the period",
      colInterface: "Interface",
      colRhMaxHourly: "RH max, hourly [%]",
      colDays80: "Days with mean RH > 80 %",
      interfacesNote: "T minimum and maximum of the daily means; RH maximum over the hours of the period.",
      profileTitle: "Relative humidity through the component in the period",
      // convergence
      convSettings: (method: string, tolerance: string, maxIter: number, step: string) => `${method} · tolerance ${tolerance} · max. ${maxIter} iterations per solve · time step ${step}`,
      hoursConverged: "Hours converged",
      everyHour: "every hour",
      hoursNotConverged: (hours: number) => `${hours} hour(s) not converged`,
      maxIterations: "Linear solve, max. iterations",
      limitN: (limit: number) => `limit ${limit}`,
      directSolver: " · direct solver",
      maxResidual: "Linear solve, max. residual",
      toleranceTag: (tolerance: string) => `tolerance ${tolerance}`,
      energyResidual: "Energy balance, max. residual",
      relativeLimit: (limit: string) => `relative · limit ${limit}`,
      massResidual: "Mass balance, max. residual",
      massTag: (limit: string, max: string) => `relative · limit ${limit} · whole run max. ${max} kg/m² per step`,
      notConvergedTitle: "Hour by hour: not converged hours marked",
      causes: ["heat solve", "vapour solve", "condensation active set", "membrane Sd"],
      causeHours: (cause: string, hours: number) => `${cause} ${hours} h`,
      notConvergedList: (list: string) => `Not converged: ${list}.`,
      allConverged: "Every hour of the period converged: all linear solves reached the tolerance, the condensation active set settled and the membrane Sd iteration converged.",
      residualNote: "The balance residuals are shown separately: a relative mass residual above 1e−6 in an hour with almost no flux through the surfaces can be a tiny absolute amount.",
      iterationsTitle: (perHour: boolean) => `Linear solve iterations (${perHour ? "per hour" : "worst hour of each day"})`,
      residualsTitle: (perHour: boolean) => `Residuals, log10 (${perHour ? "per hour" : "worst hour of each day"})`,
      iterations: "iterations",
      maxIterSeries: "max. iterations (heat or vapour)",
      linearRelative: "linear solve (relative)",
      energyBalance: "energy balance",
      massBalance: "mass balance",
      failsTitle: "Hours not converged (first 30 in the period)",
      colHour: "Hour",
      colNotConverged: "Not converged",
      colIterations: "Iterations",
      colLinearResidual: "Linear residual",
      converged: "converged",
      linearNotConverged: "linear solve not converged",
      activeSetNotConverged: "active set or membrane Sd not converged",
      // charts
      curvesShown: "Curves shown (click to hide or show)",
      rhCondensation: "100 % (condensation)",
      membrane: "membrane",
      profileLabel: "Relative humidity through the component",
      outsideCaps: "OUTSIDE",
      profileMean: "mean of the daily means",
      profileMax: "highest daily mean",
      profileMin: "lowest daily mean",
      insideAtBottom: "inside at the bottom",
      quantity: "Quantity",
      fieldRh: "RH",
      fieldT: "Temperature",
      fieldWc: "Condensate",
      fieldHover: (layer: string, stud: boolean, x: string, y: string, temperature: string, unit: string, rh: string, condensate: string) =>
        `${layer}${stud ? " (stud)" : ""} · x ${x} mm, ${y} mm from the inside · T ${temperature} ${unit} · RH ${rh} % · condensate ${condensate} kg/m³`,
      noCondensateHour: "no condensate at this hour",
      fieldHint: "Hover a cell for its values. Width: half a stud pitch between the symmetry planes.",
    },
    results: {
      badge2D: "2D mesh - last run",
      badgeSimplified: "simplified - last run",
      badgeNone: "no results yet",
      warnings: "Warnings",
      stratigraphy: "Stratigraphy",
      fieldTitle: "Field through the stratigraphy",
      zoomResetTitle: "Reset zoom (scroll over the drawing to zoom in/out)",
      standingNoteTitle: "Numerical methods & material selection",
      standingNoteDetail: "Numerical methods are used where applicable (2D mesh mode). Verifying their validity, and the suitability of every material and product selected in the stratigraphy, for the specific case, is the responsible designer's own responsibility.",
      inputErrorTitle: "Calculation not run - invalid input",
      inputErrorTitleQuota: "Calculation not run - server time used up",
      inputErrorTitleRateLimit: "Calculation not run - request limit reached",
      cancelledTitle: "CALCULATION CANCELLED",
      cancelledDetail: "Calculation cancelled by the user. The figures below are from the last calculation that actually completed.",
      noWarnings: "Add at least one layer to the stratigraphy to see engineering warnings here.",
      uLimitLabels: {
        wall: "External wall (DE Bestand, 0.24)",
        roof: "Roof / top floor (DE Bestand, 0.24)",
        floor: "Floor vs ground/unheated (DE Bestand, 0.30)",
        custom: "Custom limit",
      },
      checks: {
        uValue: "U-value",
        uValue2D: "U-value (2D)",
        limit: (value: string, reference: string) => `limit ${value} (${reference})`,
        interstitial: "Interstitial condensation and mould",
        interstitial2D: "Interstitial condensation and mould (2D)",
        interstitialCondensation: "interstitial condensation",
        possibleMould: "possible mould growth",
        none: "none",
        wet1D: (rate: string, node: string) => `${rate} g/(m²·h) at node ${node} (EN ISO 13788, design month)`,
        wet2D: (cells: number, share: string) => `${cells} cell${cells > 1 ? "s" : ""} at RH > 100 % · ${share} % of the section (design condition)`,
        maxRh: (where: string, rh: string) => `max. RH ${where} ${rh} % (design condition)`,
        betweenLayers: "between the layers",
        inTheField: "in the field",
        noFace: "no face between layers",
        surface1D: "Inside surface RH (mould)",
        surfaceDetail1D: "limit 80 % · click for the psychrometric chart",
        surface2D: "Inside surface (mould)",
        surfaceDetail2D: "max. RH, limit 80 % · min. temperature · click for the psychrometric chart",
        solver: "Solver",
        notConverged: "not converged",
        solverDetail: "heat and vapour fields",
        monthly: "Monthly Glaser",
        notCalculated: "not calculated",
        checksNotSatisfied: (n: number) => `${n} check${n > 1 ? "s" : ""} not satisfied`,
        verified: "verified",
        monthlyOk: "annual balance, admissible condensate, mould",
        monthlyFailure: {
          residual: "condensate left at the end of the cycle",
          admissible: "admissible condensate exceeded",
          mould: "mould risk (f_Rsi)",
        },
        satisfied: "Satisfied",
        notSatisfied: "Not satisfied",
        ofLimit: (percent: number) => `${percent} % of the limit`,
        notSatisfiedCount: (bad: number, total: number) => `⚠ ${bad} of ${total} checks not satisfied`,
        allSatisfied: (total: number) => `all ${total} checks satisfied`,
        mode2D: "2D mesh (steady, finite volumes)",
        mode1D: "Simplified 1D (EN ISO 6946 / EN ISO 13788)",
        clickForDetails: "click a check for its details",
      },
      temperature: "Temperature",
      humidity: "Humidity",
      humidityRh: "Humidity (RH)",
      vapourPressure: "Vapour pressure",
      vapourPressurePa: "Vapour pressure [Pa]",
      moistureContent: "Moisture content",
      secondProfileChart: "Second profile chart",
      field2DDesc: "One symmetric half stud-pitch (or the whole bay width when there is no framing member), true-to-scale row heights. Click a cell for its exact values. Drag the vertical line to move the Vertical profile charts beside it to that position",
      moistureNote: "Red = supersaturated (RH>100%) - the sorption equilibrium w(RH) is no longer a valid description of that cell, regardless of whether a moisture model is available.",
      midBay: "mid-bay",
      midBayColumn: "mid-bay column",
      studCentreline: "stud centreline",
      fromStud: (distance: string) => `${distance} from stud centreline`,
      verticalProfile: (where: string) => `Vertical profile (${where})`,
      temperatureThroughThickness: "Temperature through thickness",
      rhThroughThickness: "RH through thickness",
      psatChartLabel: "Saturation and vapour pressure through the thickness",
      rhChartLabel: "Relative humidity through the thickness",
      cell: {
        hint: "Click a cell in the map above to see its values here.",
        interiorFilm: "Interior surface film",
        exteriorFilm: "Exterior surface film",
        layer: (n: number) => `Layer ${n}`,
        material: "material",
        noMoistureModel: "not available (no moisture model)",
        dryNoModel: "(dry, no model)",
        saturated: "Status: SATURATED / CONDENSATION POTENTIAL",
        rawNote: ">100% - technical value, not the RH shown above",
      },
      field1DDesc: "Section shows the one fixed design condition the calculation solves at (edit Te/RHe/Ti/RHi directly on the graphical section in the Stratigraphy panel), not an annual average, drawn to the same scale as the Stratigraphy panel. Without a resolved 2D thermal bridge solve, the colour field is 1D: it varies through the thickness only, and framing member outlines are drawn purely for reference (the colour itself does not vary laterally there). When a 2D mesh thermal bridge solve is available, the Temperature view shows that real resolved field, and the Humidity view shows the same field's local RH, computed from it together with the 1D Glaser vapour-pressure profile (vapour resistance is not itself resolved laterally, so this is an approximation, not a genuine 2D vapour solve - select the 2D mesh calculation mode for that).",
      sectionTitle: (field: string) => `${field} through stratigraphy - design condition`,
      sectionTitleShort: (field: string) => `${field} through stratigraphy`,
      noResultsYet: "No results yet.",
      diagramNote2D: (humidity: boolean): string =>
        humidity
          ? "2D mesh temperature field + 1D vapour profile (pv not resolved laterally), mirrored about each stud centreline"
          : "2D mesh, mirrored about each stud centreline (2 periods shown)",
      profilesTitle: "Profiles through the thickness (design condition)",
      glaserSubtitleBefore: "Glaser diagram — condensation where ",
      glaserSubtitleAfter: ", at the design condition",
      info: {
        uvalue: "Heat transfer coefficient, checked against the reference limit chosen below (or a custom value).",
        rw: "Simplified mass-law estimate (EN 12354-1-style, Rw ≈ 37.5·log10(m′) − 42 dB) from total surface mass only - not valid for multi-leaf/cavity assemblies (resonance, coincidence not modelled). Not a substitute for a tested or certified Rw.",
        condensationGlaser: "Glaser 1D: vapour pressure exceeding saturation at one internal interface, at the fixed design condition (Stratigraphy panel). A steady-state check, not an annual balance.",
        surfaceCondensation: "Temperature/RH right at the interior surface, at the fixed design condition. Above ~80% RH favours mould growth even without outright condensation; at 100% RH the surface is actually condensing.",
        interstitialCondensation2d: "Highest local RH anywhere in the converged 2D field. Above ~80% favours mould growth; at 100% that cell is supersaturated (condensing).",
      },
      bar: {
        empty: "Add at least one layer to see results here.",
        moreInfo: "More info",
        previousSection: "Previous section",
        nextSection: "Next section",
        sectionOf: (i: number, n: number) => `Section ${i} of ${n}`,
        collapseToggle: "Collapse/expand the results bar",
        thermal: "Thermal",
        thermal2D: "Thermal (2D)",
        hygrometric: "Hygrometric",
        hygrometric2D: "Hygrometric (2D)",
        acoustic: "Acoustic",
        uValue: "U-value",
        uValue2D: "U-value (2D)",
        transmittanceConvergence: "Transmittance convergence",
        vapourConvergence: "Vapour pressure / RH convergence",
        converged: "Converged",
        notConverged: "Not converged",
        solveTime: (time: string) => `Solve time: ${time}`,
        surfaceCondensation: "Surface condensation",
        condensing: "Condensing",
        mouldRisk: "Mould risk",
        ok: "OK",
        noSurfaceCondensation: "No surface condensation",
        condensationGlaser: "Condensation (Glaser)",
        interstitialCondensation: "Interstitial condensation",
        noCondensation: "No condensation",
        suggestion: "Suggestion",
        leverIterations: "increase the max iterations",
        leverMesh: "refine the mesh (more rows)",
        leverTolerance: "loosen the tolerance (εU/εRH)",
        leverOr: ", or ",
        suggestTry: (levers: string) => `Try: ${levers} (Settings → 2D solver).`,
        suggestAllTried: "Already increased iterations/mesh and loosened tolerance from default, still not converging - check the stratigraphy for an extreme conductivity ratio between materials.",
        weight: "Weight",
        rw: "Rw (mass-law estimate)",
      },
      psychro: {
        title: "Psychrometric chart",
        hint: "the inside air down to 80 % RH",
        hintValue: (temperature: string) => `the inside air down to 80 % RH: ${temperature}`,
        unavailable: "Available once the stratigraphy is calculated.",
        insideAir: "Inside air",
        humidityRatio: "Humidity ratio W",
        theta80: "θ at 80 % RH",
        insideSurface: "Inside surface",
        insideSurfaceMin: "Inside surface, min.",
        noMouldRisk: "Surface above θ80%: no mould risk",
        mouldRisk: "Surface below θ80%: mould risk",
        chartLabel: "Psychrometric chart: the inside air cooled at constant humidity ratio down to 80 % RH",
        dryBulb: (unit: string) => `dry-bulb temperature [${unit}]`,
        humidityRatioAxis: "humidity ratio W [g/kg dry air]",
        enthalpy: "enthalpy h [kJ/kg]",
      },
    },
    tutorial: {
      startTitle: "Start the tutorial?",
      startMessage: "A short demonstration of the main functions on the timber-frame wall validated against Ubakus, already calculated: each step is done for you, you go on with Next. Your project is put aside and comes back as it is at the end.",
      start: "Start the tutorial",
      preparing: "Preparing the tutorial…",
      running: "The tutorial is running: press Next, or Skip tutorial to leave it.",
      stepOf: (step: number, total: number) => `Step ${step} of ${total}`,
      showing: "Showing…",
      stepDone: "✓ Done",
      skip: "Skip tutorial",
      next: "Next",
      startButton: "Start",
      finish: "Finish",
      demoName: "Demo wall",
      steps: {
        intro: {
          title: "Tutorial",
          text: "A short demonstration of the main functions on a real example - the timber-frame wall with service cavity validated against Ubakus, already calculated. Each step is done for you and shown on the page: press Next to go on, or Skip tutorial to leave. Your own project is put aside and comes back as it is at the end.",
        },
        name: {
          title: "Project name",
          text: "The project gets a name (optional): it appears on the report.",
        },
        site: {
          title: "Site and climate",
          text: "The site sets the climate of the condensation checks: a click on the map, a place searched by name, or one of the places on the map. Here the example's site is kept.",
        },
        element: {
          title: "Element",
          text: "Wall: the example is an external wall. Roof and ground connection set other surface resistances.",
        },
        analysis: {
          title: "Analysis",
          text: "Static: U-value, Glaser condensation at the design condition and the monthly Glaser on the site's climate. Quasi-dynamic: heat and moisture hour by hour over several years of the site's climate, in its own tabs. The example uses the static analysis.",
        },
        stratigraphy: {
          title: "Stratigraphy",
          text: "The Stratigraphy tab: the layers of the wall, from outside to inside, with their studs.",
        },
        addMaterial: {
          title: "Add a material",
          text: "A material is dragged from the library onto the drawing: a gap shows where it will go, and it is added where it is released.",
        },
        thickness: {
          title: "Change a thickness",
          text: "A click on a layer opens its properties: the thickness is typed and confirmed with Enter.",
        },
        layerValues: {
          title: "Values of one layer",
          text: "In the same panel the λ of this layer is changed: only this layer changes, the material in the library stays as it is.",
        },
        undo: {
          title: "Undo",
          text: "Undo (or Ctrl+Z) takes the changes back one by one, until the example is as it was; ▾ lists the recent changes.",
        },
        results: {
          title: "Analysis & results",
          text: "Analysis & results: the example is already calculated.",
        },
        checks: {
          title: "The checks",
          text: "U-value against the limit, interstitial condensation (Glaser), inside surface humidity (mould) and the monthly Glaser verdict; below, the temperature and vapour pressure through the wall.",
        },
        monthly: {
          title: "Monthly Glaser",
          text: "A click on the Monthly Glaser check: condensation and drying month by month on the climate of the site.",
        },
        report: {
          title: "Report",
          text: "The Report: everything in one document, to print or save as PDF.",
        },
        done: {
          title: "Done",
          text: "That is the main path: project and site, stratigraphy, results, report. Press Finish: your own project comes back as you left it. Tutorial, in the sidebar, shows it again at any time.",
        },
      },
    },
    report: {
      printAndContents: "Print and contents of the report",
      print: "Print…",
      printInfo: (pages: number, bw: boolean) => `${pages} page${pages > 1 ? "s" : ""} · A4 · ${bw ? "black and white" : "colour"}`,
      contents: "Contents",
      groups: {
        inputs: "Inputs",
        results: "Results",
      },
      all: "all",
      none: "none",
      notAvailable: "Not available for the current calculation",
      fieldShown: "Field shown",
      sections: {
        project: "Project",
        site: "Site and climate",
        conditions: "Design conditions",
        drawing: "Stratigraphy drawing",
        layers: "Layer table",
        checks: "Verifications",
        uvalue: "U-value calculation",
        nodes: "Values at the interfaces",
        temp: "Temperature profile",
        profiles2d: "2D vertical profiles",
        compare: "1D / 2D comparison",
        climate: "Reference year of the site",
      },
      relativeHumidity: "Relative humidity",
      untitledProject: "Untitled project",
      notes: "Notes",
      date: "Date",
      calculation2D: "2D mesh (steady state, finite volumes)",
      location: "Location",
      climateData: "Climate data",
      elevation: "Elevation",
      topSide: (side: string) => `Top side (${side}): temperature / RH / surface resistance`,
      bottomSide: (side: string) => `Bottom side (${side}): temperature / RH / surface resistance`,
      materialInsideOutside: "Material (inside → outside)",
      check: "Check",
      value: "Value",
      reference: "Reference",
      outcome: "Outcome",
      satisfied: "SATISFIED",
      notSatisfied: "NOT SATISFIED",
      overallOutcome: "Overall outcome",
      allSatisfied: "ALL CHECKS SATISFIED",
      notAllSatisfied: "NOT ALL CHECKS SATISFIED",
      uFraming: "U with the framing members (EN ISO 6946 combined method)",
      interface: "Interface",
      insideNode: (n: number) => `${n} (inside)`,
      outsideNode: (n: number) => `${n} (outside)`,
      verdict: "Verdict",
      notVerified: (failures: string) => `not verified: ${failures}`,
      method: "Method",
      methodValue: (preset: string, start: string) => `${preset}; start of the cycle: ${start}`,
      monthlySection: (u: string, maxAccumulated: string, left: string, fRsi: string, fRsiMin: string) => `U ${u} W/(m²K) · max. accumulated ${maxAccumulated} · left at the end ${left} · f_Rsi ${fRsi} / ${fRsiMin}`,
      glaserMonthShown: "Glaser diagram (month shown on the page)",
      quantity: "Quantity",
      simplified1D: "Simplified 1D",
      mesh2D: "2D mesh",
      insideSurfaceTemperature: "Inside surface temperature",
      insideSurfaceRh: "Inside surface RH",
      min: "(min.)",
      max: "(max.)",
      empty: "Tick the sections to include in the Contents list.",
      disclaimer1: "All calculations must be checked and approved by the responsible designer before use.",
      disclaimer2: "Material values are taken from the product certificates or technical sheets; the use of numerical methods (2D mesh mode) and the choice of the materials and products of the stratigraphy are the designer's responsibility.",
      pageOf: (page: number, pages: number) => `Page ${page} of ${pages}`,
      reportPages: "Report pages",
      viewer: "Report viewer",
      previousPage: "Previous page",
      nextPage: "Next page",
      page: "Page",
      zoomOut: "Zoom out",
      zoomIn: "Zoom in",
      zoom: "Zoom",
      fitWidthPct: (percent: string) => `Fit width (${percent} %)`,
      fitPagePct: (percent: string) => `Fit page (${percent} %)`,
      fitWidth: "Fit width",
      fitPage: "Fit page",
      pageLayout: "Page layout",
      layouts: {
        continuous: "Continuous pages",
        single: "Single page",
        two: "Two pages side by side",
      },
      blackAndWhite: "Black and white",
      blackAndWhiteOn: "Black and white (on: also printed so)",
      bwShort: "B&W",
    },
  },

  // Texts of the Design System V2 components (src/components/ds2).
  designSystem: {
    openInNewLabel: "Opens in a new tab",
    breadcrumbLabel: "Breadcrumb",
    openMenuLabel: "Open menu",
    closeMenuLabel: "Close menu",
    closeLabel: "Close",
    accountMenuLabel: "Account menu",
    paginationOfLabel: "of",
    previousPageLabel: "Previous page",
    nextPageLabel: "Next page",
    selectPlaceholder: "Select...",
    dropFilesLabel: "Drop it!",
    dropZoneLabel: "File drop zone",
    dropRejectedLabel: "This file type isn't supported",
    searchPlaceholder: "Search",
    clearSearchLabel: "Clear search",
    toggleSubmenuLabel: (text: string) => `Toggle ${text}`,
    contextMenuTriggerLabel: "Open actions menu",
    increaseLabel: (value: number) => `Increase ${value}`,
    decreaseLabel: (value: number) => `Decrease ${value}`,
    datePicker: {
      clearButton: "Clear date",
      nextMonth: "Next month",
      placeholder: "Select date",
      previousMonth: "Previous month",
      selectMonth: "Select month",
      selectYear: "Select year",
      calendarLabel: "Choose a date",
    },
    cancelLabel: "Cancel",
    okLabel: "OK",
    removeLabel: "Remove",
    requiredFieldLabel: "This field is required.",
    fieldTooSmallLabel: "This field's value is too low.",
    fieldTooLargeLabel: "This field's value is too high.",
    fieldInvalidFormatLabel: "This field's value has an invalid format.",
    fieldInvalidValueLabel: "This field's value is invalid.",
    browseOrDropFileLabel: "Drag and drop, or click to browse",
    browseOrDropImageLabel: "Drag and drop, or click to upload an image",
    removeImageLabel: "Remove image",
    resetImageLabel: "Reset image",
    translationsLabel: "Translations",
    increaseOrderLabel: "Increase order",
    decreaseOrderLabel: "Decrease order",
    stagingLabel: "Test environment",
    unexpectedErrorLabel: "Something went wrong. Please try again.",
    saveLabel: "Save",
    loadingLabel: "Loading",
    browseFilesLabel: "Browse files",
    noOptionsLabel: "No options",
    quantityOfLabel: (label: string) => `Quantity of ${label}`,
    chooseOrDropFileLabel: "Choose a file or drag it here to upload it.",
    stepOfLabel: (current: number, total: number) => `Step ${current} of ${total}`,
    stepCompletedLabel: "completed",
    backToStepLabel: (step: number) => `Back to step ${step}`,
  },
};

export default en;
